/* MOFT Snap Studio — <mf-snap-studio>, the interactive layer.
 *
 * Verbatim port of prototype-v2 (docs/snap-studio/prototype-v2/, the single
 * source of truth for interaction). Data comes from the mf-studio-data island;
 * the hydration block at the top of initStudio() is the only Shopify-aware code.
 * Interaction changes belong in the prototype first — see HANDOFF.md.
 * Data contract: docs/snap-studio/data-contract.md. Class/id namespace: mfs-.
 */
(function () {
  'use strict';

  function initStudio(root, DATA) {
    var __subs = [];
    function on(t, ev, fn, opts) { t.addEventListener(ev, fn, opts); __subs.push([t, ev, fn, opts]); }

  /* ================= hydration: island JSON -> the prototype's own data shapes =================
     Everything below this block is a verbatim port of prototype-v2 (see
     docs/snap-studio/prototype-v2/). Only this block knows about Shopify. */
  /* A fixed amount off the set, in dollars — the admin runs a fixed-amount
     automatic discount. A percentage drifts with whichever pair is on screen
     (15% is $12.00 on a $79.98 pair but $8.25 on the cheapest one), so it can
     never match the amount the badge promises. */
  var SAVE_OFF = Number(DATA.saveAmount) || 0;
  var SAVE_BADGE = String(DATA.saveBadge || '').trim();
  var COLORS = DATA.colors || {};                          /* colour code -> { n, h, w, line } */
  var MODELS = DATA.phones || [];                          /* { k, n, s, w, h, alt, bodies } */
  /* Keyed by model AND finish. The 17 and 18 seasons share finish slugs
     (black, mist-blue, silver) on different hexes, so a flat slug map let the
     season declared last silently repaint the earlier one's swatches — 17's
     pale Mist Blue #B7C6D6 would have become 18's #8FACD9. The bare-slug entry
     is kept, first writer wins, for reads that have no model in hand. */
  var BODY_NAME = {}, BODY_HEX = {};
  MODELS.forEach(function (m) {
    (m.bodies || []).forEach(function (b) {
      BODY_NAME[m.k + '/' + b.k] = b.n; BODY_HEX[m.k + '/' + b.k] = b.h;
      if (!(b.k in BODY_NAME)) { BODY_NAME[b.k] = b.n; BODY_HEX[b.k] = b.h; }
    });
    m.bodies = (m.bodies || []).map(function (b) { return b.k; });
  });
  function bodyName(mk, bk) { return BODY_NAME[mk + '/' + bk] || BODY_NAME[bk] || bk; }
  function bodyHex(mk, bk) { return BODY_HEX[mk + '/' + bk] || BODY_HEX[bk] || '#B4B4B4'; }

  /* fold geometry per vector shape (engine constants, not data) */
  var SHAPE_AR = { snap: 0.68, tripod: 0.66, field: 0.70, flow: 0.70 };
  var CASES = [], CASE_VAR = {};                           /* type:dev:col -> variant */
  (DATA.cases || []).forEach(function (c) {
    CASES.push({ k: c.k, n: c.n, s: c.s, price: c.price });
    (c.variants || []).forEach(function (v) {
      if (!CASE_VAR[c.k + ':' + v.dev + ':' + v.col]) CASE_VAR[c.k + ':' + v.dev + ':' + v.col] = v;
    });
  });
  var FAMS = [], STAND_POOL = {}, STAND_VAR = {};          /* fam:ver:col / fam:*:col / fam:ver:* */
  (DATA.stands || []).forEach(function (s) {
    FAMS.push({ k: s.k, shape: s.shape, n: s.n, s: s.s, what: s.what || '', long: s.long || '',
                /* one line per Version, same order as vers — see mf-studio-data */
                vwhat: Array.isArray(s.vwhat) ? s.vwhat : null,
                vers: s.vers && s.vers.length > 1 ? s.vers : null,
                /* version indexes that still have a variant behind them — the
                   island drops the ones the hidden-SKU list emptied */
                vlive: Array.isArray(s.vlive) ? s.vlive.map(Number) : null,
                price: s.price, ar: SHAPE_AR[s.shape] || 0.68 });
    STAND_POOL[s.k] = s.pool || [];
    (s.variants || []).forEach(function (v) {
      var vi = v.ver || 0;
      if (!STAND_VAR[s.k + ':' + vi + ':' + v.col]) STAND_VAR[s.k + ':' + vi + ':' + v.col] = v;
      if (!STAND_VAR[s.k + ':*:' + v.col]) STAND_VAR[s.k + ':*:' + v.col] = v;
      if (!STAND_VAR[s.k + ':' + vi + ':*']) STAND_VAR[s.k + ':' + vi + ':*'] = v;
    });
  });
  /* Declared here, not next to pickableModels(): the tile builder runs during
     init, well before that point in source order, and a `var` read too early
     is undefined — which would have filtered the picker in debug mode too. */
  /* series name on the entry board — the section setting, so a dedicated page
     (iPhone Duo) can say which Studio it is (NO.250) */
  var SERIES = root.getAttribute('data-mfs-series') || 'Snap Studio';
  var SHOW_UNLISTED = root.hasAttribute('data-mfs-design') ||
                      new URLSearchParams(location.search).get('debug') === '1';
  /* SKU prefix per case type, read off the catalogue instead of hardcoded:
     MD020-1-i17p-BE -> MD020-1. Feeds the unlisted-season art path below. */
  var CASE_PFX = {};
  (DATA.cases || []).forEach(function (c) {
    var seg = String(((((c.variants || [])[0]) || {}).sku) || '').split('-');
    if (seg.length > 2) CASE_PFX[c.k] = seg.slice(0, seg.length - 2).join('-');
  });
  var CASE_POOL = [], FRAME_POOL = [];
  (DATA.cases || []).forEach(function (c) {
    if (c.k === 'frame') FRAME_POOL = c.pool || []; else CASE_POOL = c.pool || [];
  });
  var DEF = DATA.def || { m: (MODELS[0] || {}).k, b: ((MODELS[0] || {}).bodies || [])[0] };
  /* ---- the opening device has to be one the customer can actually buy ----
     A new season arrives art-first: iPhone 18's layers were in the library weeks
     before the shop had a single i18 case variant. The island may point `def` at
     it anyway (and does, from 2026-09-10) — but on the storefront that would open
     the Studio on a phone whose cases have no variant: no price, and the checkout
     gate turns it away at the last step. So clamp to the first sellable model,
     and clamp the body with it (17 Pro Max has no Deep Burgundy).
     Under ?debug=1 / the theme editor SHOW_UNLISTED is true and pickableModels()
     returns everything, so `def` stands — that is how the new season is previewed.
     The day the variants go live nothing here has to change. */
  (function clampDefault() {
    var ok = pickableModels(), i, hit = false;
    for (i = 0; i < ok.length; i++) if (ok[i].k === DEF.m) { hit = true; break; }
    if (!hit && ok.length) {
      /* nearest by body height, not first in the list: the array is ordered
         newest-first with Pro before Pro Max, so "first sellable" would drop an
         18 Pro Max default onto a 17 Pro. The stand-in for a phone we cannot
         sell yet should be the same size phone from the season we can. */
      var want = (find(MODELS, DEF.m) || {}).h || 0, best = ok[0], bestD = Infinity, d;
      for (i = 0; i < ok.length; i++) {
        d = Math.abs((ok[i].h || 0) - want);
        if (d < bestD) { bestD = d; best = ok[i]; }
      }
      DEF.m = best.k;
    }
    var bodies = (find(MODELS, DEF.m) || {}).bodies || [];
    if (bodies.length && bodies.indexOf(DEF.b) < 0) DEF.b = bodies[0];
  })();

  /* ── layer resolution: filename = variant SKU (data-contract §1) ── */
  var LAYERS = {}, LAYER_URLS = {};
  Object.keys(DATA.layers || {}).forEach(function (n) {
    LAYERS[n] = DATA.layers[n].ar || 1;
    LAYER_URLS[n] = DATA.layers[n].u;
  });
  /* ---- the inspector page stops here ----
     templates/page.mf-inspect.json mounts this same host with
     `data-mfs-inspect-only` and NONE of the Studio markup — the bench only ever
     needed the parsed catalogue, not the stage. Everything insCtx() hands over
     is resolved by this line; carrying on would walk into `$('phoneFrame')` and
     a hundred other nulls. Bailing here also means the bench page pays for no
     attract loop, no warm-up and no layout work. (2026-09-10, pre-launch: the
     Studio page itself no longer carries any inspector entry point.) */
  if (root.hasAttribute('data-mfs-inspect-only')) {
    var insUrl = root.getAttribute('data-mfs-inspector');
    var insBenchClose = null;
    if (insUrl) {
      import(insUrl).then(function (mod) { insBenchClose = mod.open(insCtx()); })
                    .catch(function (e) { console.warn('[snap-studio] inspector failed to load', e); });
    } else {
      console.warn('[snap-studio] inspect-only host without data-mfs-inspector');
    }
    return function () { if (insBenchClose) { insBenchClose(); insBenchClose = null; } };
  }

  /* COREG: flips on when the co-registered 1080x1080 batch lands (asset-spec v2);
     the per-type boxes then stop mattering and all three layers go full-bleed.
     Scale rule (asset-spec §3): on the 1080 canvas the base device (17 Pro,
     design width 126) is drawn 400px wide — so a stage that wants the device
     devW px wide shows the square at devW * 1080/400. One constant, all layers,
     all models (the canvas itself is drawn to physical scale). */
  var COREG = !!DATA.coreg;
  if (COREG) root.classList.add('mfs-coreg');
  /* User-calibrated (2026-08-31): a 1080x1080 layer renders 572px square at desk
     scale (Z = 1.69) to match the design boards. One constant, every layer,
     every model — the canvas itself already carries the physical proportions. */
  var COREG_SIDE = 572 / 1.69;
  function sqPx() { return Math.round(COREG_SIDE * Z()); }
  /* ---- the stage reference ----
     Under COREG the stage is one fixed square for every phone, and the rail line,
     the rail pitch and the ring radius were calibrated on the Pro Max default
     (137 x 286 mm, 2026-08-31 / 09-02). They used to read those numbers off DEF.m,
     which was the same thing while every phone was a portrait one. iPhone Duo
     (2026-10-22) is the first that is not: its dictionary w/h is the UNFOLDED
     proportion, and fed through DEF.m it dropped the rail 70px below the phone
     and halved the ring. So the calibration is pinned here; off COREG the vector
     build still sizes everything off the picked model, as before. */
  var STAGE_REF = { w: 137, h: 286 };
  function stageRef() { return COREG ? STAGE_REF : find(MODELS, curModel()); }
  /* the stage device lies on its side (iPhone Duo, unfolded) */
  function landscapeModel() { var m = find(MODELS, DEF.m); return !!(m && m.w > m.h); }
  /* half the unfolded body's width on the canvas: 929 of 1080 (Figma ⑩ Duo) */
  var LAND_HALF = 929 / 1080 / 2;
  /* Every stage-size paint asks for the SAME copy, so exactly one bitmap per
     layer is ever in play. At DPR 1 that is the 720 (1.98MB) instead of the
     1080 (4.45MB), and the desk ring's seats — which are the same 572 square —
     resolve to the same name and share it rather than decoding a second copy.
     On a phone (DPR 3 against a 438 box) it resolves to the original, which is
     what that screen can actually show. */
  function stagePx() { return fitSize(sqPx()); }
  function stageLay(n) { return atSize(n, stagePx()); }
  /* rail sprites: the handoff draws stands at 0.679x device width; the delivered
     snap sprite fills ~0.885 of its canvas, so shrink the square to match */
  /* RAIL_FIT 1: the rail shows stands at the SAME shared-canvas scale as the
     one worn on the phone (572 square) — the old 0.77 shrink made the row read
     23% smaller than the committed stand, so t3 felt like a size jump (user).
     The pitch below compensates so the row keeps its breathing room. */
  var RAIL_FIT = 1;
  var RAIL_PITCH = 1.21;   /* slotStep scale under COREG: content grew 176->229px */
  var LBOX = COREG
    ? { body: { x: 0, w: 100 }, stand: { y: 0, h: 100 } }
    : { body: { x: 3.556, w: 93.33 }, stand: { y: 32.31, h: 61.14 } };
  /* ================= size variants =================
     A decoded 1080 square costs 4.45MB of memory whatever size it is painted
     at, so a board made of full-size layers is measured in hundreds of
     megabytes and the tab gets reaped. Bytes are not the problem: measured on
     the test theme through the real CDN, a browser Accept header gets webp/avif
     back — 16.7KB for the 1080, 11.2KB for the 720, 3.9KB for the 270. (curl
     without an Accept header gets PNG at 175/131/25KB, which is where the
     "the CDN transcodes our webp" reading came from. It does not; it content-
     negotiates, and no browser ever sees those numbers.)

     Three rungs, and the size is addressed by NAME (`<layer>#720`) rather than
     by a flag, so warm(), the WARM cache, swapLayer's out-of-order guard and
     the debug readout all keep working untouched, and each size caches as what
     it is: its own bitmap.

     URL shape: a theme asset answers to the `_270x` filename the CDN already
     serves for `asset_img_url` (verified against the real CDN through theme
     dev — 270×270, alpha intact); Shopify Files answer to `&width=`, which is
     the shape these URLs take once the batch moves off the theme (PROGRESS todo
     3). Anything else keeps the full-size URL: a heavy thumbnail is a
     performance bug, a missing one is a broken page. */
  var THUMB_PX = 270, MID_PX = 720, SIZE_TAG = /#(\d+)$/;
  function baseName(name) { return name ? name.replace(SIZE_TAG, '') : name; }
  function sizeTag(name) { var m = name && name.match(SIZE_TAG); return m ? +m[1] : 0; }
  function atSize(name, px) { return name ? (px ? baseName(name) + '#' + px : baseName(name)) : name; }
  function cdnSrc(u, px) {
    if (!u || !px) return u;
    if (/\/files\//.test(u)) return u + (u.indexOf('?') > -1 ? '&' : '?') + 'width=' + px;
    var m = u.match(/^([^?]+)(\.[a-zA-Z0-9]+)(\?.*)?$/);
    return m ? m[1] + '_' + px + 'x' + m[2] + (m[3] || '') : u;
  }
  function thumbSrc(u) { return cdnSrc(u, THUMB_PX); }
  function layerURL(name) {
    if (!name) return null;
    var b = LAYER_URLS[baseName(name)];
    return b ? cdnSrc(b, sizeTag(name)) : null;
  }
  /* How many SOURCE pixels a box of `cssSide` actually needs on this screen.
     The old rule compared CSS px against 270 and ignored devicePixelRatio, so a
     desk at DPR 1 painted a 572px box out of the full 1080 — four times the
     bitmap it can show. Three rungs only: the CDN copies we ask for are 270,
     720 and the original, and a fourth would just fragment the cache.
     ⚠ Do NOT let this "upgrade" the deliberately soft boxes: the 192px angle
     card and the phone build's dial seats take 270 on purpose (memory), so
     callers floor at 270 rather than asking fitSize for a small box. */
  function fitSize(cssSide) {
    var need = cssSide * (window.devicePixelRatio || 1);
    if (need <= THUMB_PX) return THUMB_PX;
    if (need <= MID_PX) return MID_PX;
    return 0;                     /* 0 = the original 1080 square */
  }
  /* decoded cost of a bitmap, in MB — RGBA, four bytes a pixel, and completely
     independent of how many bytes came down the wire (a 1080 square is ~17KB of
     webp and 4.45MB of memory). This is the number the warm budget spends. */
  function bmpMB(px) { px = px || 1080; return px * px * 4 / 1048576; }
  function srcMB(u) {
    var m = u && u.match(/_(\d+)x\.|[?&]width=(\d+)/);
    if (m) return bmpMB(+(m[1] || m[2]));
    return bmpMB(/mf-(angle|gallery)-/.test(u || '') ? 720 : 1080);
  }
  function layerAR(name) { return LAYERS[baseName(name)] || 0; }
  function shapeOf(fk) { var f = find(FAMS, fk); return (f && f.shape) || fk; }
  function devCandidates(modelKey) { var m = find(MODELS, modelKey); return [m.k].concat(m.alt || []); }
  function bodyLayer(modelKey, bodyKey) {
    var col = String(bodyKey).toLowerCase(), codes = devCandidates(modelKey), mine = codes[0], k, m;
    for (m = 0; m < codes.length; m++) {
      if (LAYERS[k = 'mf-body-' + codes[m] + '-' + col]) return k;
    }
    var names = Object.keys(LAYERS).filter(function (n) { return n.indexOf('mf-body-') === 0; });
    return firstMatch(names, mine + '-')      /* my model, whatever colour  */
        || firstMatch(names, '-' + col)       /* my colour, whatever model  */
        || names[0] || null;
  }
  function firstMatch(names, frag) {
    for (var i = 0; i < names.length; i++) if (names[i].indexOf(frag) > -1) return names[i];
    return null;
  }
  /* ================= a season with art but no catalogue yet =================
     Design delivers a new iPhone's layers a release ahead of ops creating the
     variants (iPhone 18, 2026-09-05: 36 case layers in Figma, 0 variants in the
     shop). The layer file name is {SKU prefix}-{device}-{colour} and all three
     parts are known here, so the art is reachable without a variant.

     Deliberately narrow: it applies only where the model has NO variant of this
     case type at all. On a model ops HAS listed, a colour with no variant is a
     delist, not a gap, and must stay off the board. It is also not deviation
     #13 — nothing of another model is substituted, this addresses the model's
     own file. Buying still needs a real variant; see the Checkout handler. */
  function typeUnlisted(ct, mk) {
    var devs = devCandidates(mk), pool = ct === 'frame' ? FRAME_POOL : CASE_POOL, i, j;
    for (i = 0; i < pool.length; i++)
      for (j = 0; j < devs.length; j++)
        if (CASE_VAR[ct + ':' + devs[j] + ':' + pool[i]]) return false;
    return true;
  }
  function artLayer(ct, mk, colorKey) {
    var pfx = CASE_PFX[ct]; if (!pfx) return null;
    var devs = devCandidates(mk), i, n;
    for (i = 0; i < devs.length; i++)
      if (LAYERS[n = 'mf-layer-' + pfx + '-' + devs[i] + '-' + colorKey]) return n;
    return null;
  }
  /* The board for such a model is exactly what art was delivered for. The
     previous season's pool would be both short and wrong: 18 ships 13 Snap
     colours against 17's 11, and Frame's five 18 duals share nothing with the
     two on 17. Memoised — it walks every layer name. */
  var ART_POOL = {};
  function artPool(ct, mk) {
    var key = ct + '/' + mk;
    if (ART_POOL[key]) return ART_POOL[key];
    var pfx = CASE_PFX[ct], out = [];
    if (pfx) {
      var names = Object.keys(LAYERS);
      devCandidates(mk).forEach(function (d) {
        var head = 'mf-layer-' + pfx + '-' + d + '-';
        names.forEach(function (n) {
          if (n.indexOf(head) !== 0) return;
          var c = n.slice(head.length);
          /* dictionary colours only: an unknown code has no name and no swatch
             hex, and would put a blank chip on the board */
          if (COLORS[c] && out.indexOf(c) < 0) out.push(c);
        });
      });
    }
    return (ART_POOL[key] = out);
  }
  function caseLayer(caseType, modelKey, colorKey) {
    /* no cross-model substitution (deviation #13): a case photo of another model
       must never stand in — a miss falls to the procedural build instead */
    var devs = devCandidates(modelKey), i, v, n;
    for (i = 0; i < devs.length; i++) {
      v = CASE_VAR[caseType + ':' + devs[i] + ':' + colorKey];
      if (v && LAYERS[n = 'mf-layer-' + v.sku]) return n;
    }
    return typeUnlisted(caseType, modelKey) ? artLayer(caseType, modelKey, colorKey) : null;
  }
  /* same model-candidate walk as caseLayer, but returning the variant itself —
     the receipt needs its photo and its SKU, not the layer file name */
  function caseVariant(caseType, modelKey, colorKey) {
    var devs = devCandidates(modelKey), i, v;
    for (i = 0; i < devs.length; i++) {
      if ((v = CASE_VAR[caseType + ':' + devs[i] + ':' + colorKey])) return v;
    }
    return null;
  }
  function standVariant(famKey, colorKey, vi) {
    var v = STAND_VAR[famKey + ':' + vi + ':' + colorKey];
    if (v) return v;
    /* the ':*:' wildcard is an any-version fallback the adapter builds per
       colour — legal only for families WITHOUT versions. For versioned
       families it borrowed another version's variant: the wrong product on
       the phone and the wrong SKU in the cart (user-caught, 2026-09-01). */
    var f = find(FAMS, famKey);
    return (f && f.vers) ? null : (STAND_VAR[famKey + ':*:' + colorKey] || null);
  }
  /* SKU → stand layer name, with fallbacks for three systematic mismatches
     between the delivered file names and the shop's SKUs. Brand moved the three
     stand products onto the AERA product pages (2026-09-09), and the delivery
     names never caught up:

       -ME- / -MO-   The AERA pages ship 41 variants, every one with ME in the
                     third segment; Material is a separate option and BOTH its
                     values use ME. So MO is not a material — it is leftover
                     layer naming on the design side, which split one product's
                     colourways across two prefixes (the ME and MO colour sets
                     do not overlap). Verified visually: MS027A ME and MO render
                     the same product in different colours.
       -1 revision   Some SKUs carry a revision tail the layer name omits
                     (MS027A-1-ME-LTPK-1 vs …-LTPK). The data adapter already
                     tolerates this tail when reading colour codes; the file
                     lookup has to as well.
       WAF / WF      Trackable Tripod Wallet mixes MS027WAF-1 and MS027WF-1 in
                     one Version; only WF art was delivered.

     Deliberately ordered: the exact name always wins, so the day design renames
     its layers these fallbacks stop firing on their own — no code to unwind.
     A total miss still returns null and falls through to the procedural build,
     which is the correct degradation (never substitute another product's art). */
  function standLayerName(sku) {
    if (!sku) return null;
    var cands = [sku], m;
    if (sku.indexOf('-ME-') > -1) cands.push(sku.replace('-ME-', '-MO-'));
    if ((m = /^(.+)-\d+$/.exec(sku))) {
      cands.push(m[1]);
      if (m[1].indexOf('-ME-') > -1) cands.push(m[1].replace('-ME-', '-MO-'));
    }
    if (sku.indexOf('MS027WAF-1') > -1) {
      var wf = sku.replace('MS027WAF-1', 'MS027WF-1');
      cands.push(wf);
      if (wf.indexOf('-ME-') > -1) cands.push(wf.replace('-ME-', '-MO-'));
    }
    for (var i = 0; i < cands.length; i++) {
      var n = 'mf-layer-' + cands[i];
      if (LAYERS[n]) return n;
    }
    return null;
  }
  function standLayer(famKey, colorKey) {
    return standLayerAt(famKey, colorKey, (state.ver || {})[famKey] || 0);
  }
  /* the same lookup for a version the user is NOT on — the warm-up needs to
     name the art behind the other chips before they are tapped */
  function standLayerAt(famKey, colorKey, vi) {
    var v = standVariant(famKey, colorKey, vi);
    if (!v) return null;
    return standLayerName(v.sku);
  }
  function angleShots(famKey, colorKey, vi) {
    var v = standVariant(famKey, colorKey, vi), out = [], j, n;
    if (!v) return out;
    /* Same SKU-name fallbacks as the layer lookup (ME/MO, revision tail,
       WAF/WF) — the angle files were delivered under the same names. The v
       segment is the Version INDEX on the live product page, so it is taken
       from vi and not from the file name. */
    var skus = [v.sku];
    if (v.sku.indexOf('-ME-') > -1) skus.push(v.sku.replace('-ME-', '-MO-'));
    var rm = /^(.+)-\d+$/.exec(v.sku);
    if (rm) skus.push(rm[1]);
    if (v.sku.indexOf('MS027WAF-1') > -1) skus.push(v.sku.replace('MS027WAF-1', 'MS027WF-1'));
    for (j = 1; j <= 2; j++) {
      n = null;
      for (var si = 0; si < skus.length && !n; si++) {
        var cand = 'mf-angle-' + skus[si] + '-v' + vi + '-' + j;
        if (LAYER_URLS[cand]) n = cand;
      }
      if (!n) continue;
      /* the card is 192px on desk and a ~78px PiP on the phone build; neither
         needs the 720px delivery, and the phone pays for it in memory */
      if (LAYER_URLS[n]) out.push(isMob() ? thumbSrc(LAYER_URLS[n]) : LAYER_URLS[n]);
    }
    return out;
  }
  /* One shared angle shot per product, whatever the colour:
     mf-angle-{SKU prefix}-default (MB004D / MS029, iPhone Duo page, client
     2026-10-23 NO.250). Where it exists it leads every strip of shots (see
     withDefault), so the card has a picture the moment the step opens, instead
     of waiting on a colour that may have none. */
  function defaultAngle(famKey, vi) {
    var v = STAND_VAR[famKey + ':' + (vi || 0) + ':*'];
    if (!v) return null;
    var u = LAYER_URLS['mf-angle-' + String(v.sku).split('-')[0] + '-default'];
    return u ? (isMob() ? thumbSrc(u) : u) : null;
  }
  /* The colour the shots are taken in: on the stand-pick board the stand on the
     phone wears the default white (renderStand), so the card and the sheet show
     that one too — state.standColor there is whatever the attract row left
     behind. The colour boards go back to the picked colour. */
  function shotColor(fk) {
    return modelPicking() ? defaultIn(standColors(fk)) : state.standColor;
  }
  /* The shared shot leads and the colour's own pair follows (NO.264: "需要同时
     展示通用图和 2 张角度图"), in the step-2 card, the step-3 info box and the
     phone sheet alike. A product with no shared shot keeps just its pair. */
  function withDefault(f, vi, pair) {
    var d = defaultAngle(f.k, vi);
    return d ? [d].concat(pair.filter(function (u) { return u !== d; })) : pair;
  }
  function sheetShots(f, vi) {
    var col = shotColor(f.k);
    var shots = galleryShots(f.k, col, vi);
    return withDefault(f, vi, shots.length ? shots : angleShots(f.k, col, vi));
  }
  /* ================= intro shots (the info box's pictures) =================
     Two per colourway, delivered on the Figma "angle" page as `cell ·
     mf-gallery-{SKU}-a{1|2}` and synced by tools/studio-gallery-sync.mjs. They
     carry the designers' #F4F3F0 plate, unlike the transparent layer art — the
     box is white, a cut-out product would float on it.

     Addressed off the variant SKU with the revision segment dropped
     (MS007MP-1-ME-LTPK -> MS007MP-ME-LTPK), because the file name IS the Figma
     cell name: a re-export overwrites in place and nothing here has to change.
     A colourway with no shot delivered returns [] and the box keeps the posture
     tiles it has always had — this is a picture layer over that, not instead. */
  function galleryKey(sku) {
    var segs = String(sku || '').split('-');
    if (segs[1] === '1') segs.splice(1, 1);
    return 'mf-gallery-' + segs.join('-');
  }
  /* The shop renamed the stand prefixes to the design side's (MS007MP -> MS007MA,
     MS027 -> MS027A, MS027W -> MS027WA) while the gallery batch is still on disk
     under the old ones — the layer and angle batches were re-synced under both
     names, this one never was. Without the alias every Snap-on / Tripod colourway
     resolves to nothing and the box falls back to the CSS posture figures, which
     is what it did on 2026-09-10 (user: "我看还有用矢量的"): 49 variants that DO
     have photography were showing drawings.

     Same shape as standLayerName's chain and the same exit: the exact name wins,
     so a re-export under the new prefix stops these firing by itself. ME/MO is
     here for the same reason it is there — the delivery used whichever the
     product page carried at the time. */
  var GALLERY_PFX_ALIAS = { MS007MA: 'MS007MP', MS027A: 'MS027', MS027WA: 'MS027W', MS027WAF: 'MS027WF' };
  function galleryNames(sku) {
    var base = galleryKey(sku), roots = [base], out = [], i;
    var pfx = base.slice('mf-gallery-'.length).split('-')[0];
    if (GALLERY_PFX_ALIAS[pfx]) roots.push('mf-gallery-' + GALLERY_PFX_ALIAS[pfx] + base.slice('mf-gallery-'.length + pfx.length));
    /* the shop's own prefix first, both ME and MO, before falling back to the
       old one: interleaved, `MS027-MO-…` beat `MS027A-MO-…` and the alias would
       never retire even with the new batch sitting in the library */
    for (i = 0; i < roots.length; i++) {
      out.push(roots[i]);
      if (roots[i].indexOf('-ME-') > -1) out.push(roots[i].replace('-ME-', '-MO-'));
    }
    return out;
  }
  function galleryShots(famKey, colorKey, vi) {
    var v = standVariant(famKey, colorKey, vi), out = [], j, k, names, n;
    if (!v) return out;
    names = galleryNames(v.sku);
    for (j = 1; j <= 2; j++) {
      n = null;
      for (k = 0; k < names.length && !n; k++) if (LAYER_URLS[names[k] + '-a' + j]) n = names[k] + '-a' + j;
      /* the box is 174px wide and the sheet's strip not much more — the 270px
         CDN copy is the one to ask for, same reasoning as the angle card */
      if (n) out.push(thumbSrc(LAYER_URLS[n]));
    }
    return out;
  }
  /* Per-model colour pools (deviation #13, user-decided 2026-08-31): once a phone
     is picked, only colourways sold FOR THAT MODEL are offered — no other model's
     products appear. The prototype pooled colours across models because assets
     were scarce; live variant data makes the honest pool possible. */
  function poolForType(ct) {
    var pool = ct === 'frame' ? FRAME_POOL : CASE_POOL;
    var devs = devCandidates(curModel());
    var out = pool.filter(function (c) {
      for (var i = 0; i < devs.length; i++) if (CASE_VAR[ct + ':' + devs[i] + ':' + c]) return true;
      return false;
    });
    if (out.length) return out;
    var art = artPool(ct, curModel());          /* unlisted season — see artPool */
    return art.length ? art : pool;
  }

  /* a case TYPE exists for the current model only if some colour has a variant
     (deviation #13: Frame sells Pro/Pro Max only — hide the type elsewhere) */
  function typeHasStock(ct) {
    /* typeUnlisted false === some variant exists for this model */
    if (!typeUnlisted(ct, curModel())) return true;
    return artPool(ct, curModel()).length > 0;
  }

  /* live prices off the selected variants; falls back to the product base price */
  function casePrice() {
    var devs = devCandidates(curModel()), i, v;
    for (i = 0; i < devs.length; i++) {
      v = CASE_VAR[state.caseType + ':' + devs[i] + ':' + (state.color || '')];
      if (v) return v.price;
    }
    return find(CASES, state.caseType).price;
  }
  function standPrice(f) {
    var vi = (state.ver || {})[f.k] || 0;
    var v = STAND_VAR[f.k + ':' + vi + ':' + (state.standColor || '')] || STAND_VAR[f.k + ':' + vi + ':*'];
    return v ? v.price : f.price;
  }

  /* theme-editor-only: surface island parse warnings where the operator can see them */
  if (DATA.warn && DATA.warn.length && root.hasAttribute('data-mfs-design')) {
    var __wp = document.createElement('div');
    __wp.className = 'mfs-warn-panel';
    var __wb = document.createElement('b');
    __wb.textContent = 'Snap Studio data warnings (' + DATA.warn.length + ')';
    __wp.appendChild(__wb);
    DATA.warn.forEach(function (w) {
      var d = document.createElement('div');
      d.textContent = w;
      __wp.appendChild(d);
    });
    root.appendChild(__wp);
  }


  /* ================= data =================
     Vector build. The colour truth still comes from the real line (palette and
     per-product colour ranges lifted from the shot catalog), but nothing is
     gated on what happened to be photographed: one case palette for every
     phone, so no colour is ever unreachable on some body. */
  var MOVAS = Object.keys(COLORS).map(function (k) {
    return { k: k, n: COLORS[k].n, h: COLORS[k].h, h2: COLORS[k].h2 || '', w: COLORS[k].w };
  });
  /* ---- 双色色块 ----
     走查 0905 C：「Snap Flow 是拼色，色块只展示单色 → 需要双色/拼色色块能力，
     其他 SKU 同理」。双色码整体是一个色码（不拆——CFSPBU 是 CF+SPBU，2+4，
     拆码不可靠，见出图规范 §4），所以第二个颜色是字典里 authored 的 h2 字段，
     不在运行时切字符串。
     返回值直接当 background 用：没有 h2 就是原来的纯色，有就是 135° 硬分割。
     硬分割（0 50% / 50% 100%，没有过渡）读起来是「两个颜色」，渐变会读成
     「一个渐变色」——产品是拼色，不是渐变。 */
  function swatchBg(c) {
    if (!c) return 'transparent';
    var a = c.h || 'transparent';
    if (!c.h2) return a;
    return 'linear-gradient(135deg, ' + a + ' 0 50%, ' + c.h2 + ' 50% 100%)';
  }

  function rgbOf(h) { return [parseInt(h.substr(1, 2), 16), parseInt(h.substr(3, 2), 16), parseInt(h.substr(5, 2), 16)]; }
  function lum(h) { var c = rgbOf(h); return .299 * c[0] + .587 * c[1] + .114 * c[2]; }
  function gap(a, b) { var x = rgbOf(a), y = rgbOf(b); return Math.pow(x[0]-y[0],2) + Math.pow(x[1]-y[1],2) + Math.pow(x[2]-y[2],2); }
  /* how much colour a hex actually carries — greys score ~0 */
  function chroma(h) { var c = rgbOf(h); return Math.max(c[0], c[1], c[2]) - Math.min(c[0], c[1], c[2]); }

  /* ---- availability: the single source of truth for what the UI may show ---- */
  function bodiesFor(mk) { return find(MODELS, mk).bodies; }
  function caseColors() { return poolForType(state.caseType); }
  function standColors(fk) { return STAND_POOL[fk] || []; }
  function bodyRec(mk, dev) { return { k: dev, n: bodyName(mk, dev), h: bodyHex(mk, dev) }; }
  function phonesFor(mk) { return bodiesFor(mk).map(function (b) { return bodyRec(mk, b); }); }
  function nearestIn(colorKey, pool) {
    if (!pool.length) return null;
    if (pool.indexOf(colorKey) >= 0) return colorKey;
    var me = COLORS[colorKey]; if (!me) return pool[0];
    return pool.reduce(function (a, b) { return gap(me.h, COLORS[b].h) < gap(me.h, COLORS[a].h) ? b : a; });
  }

  /* pairings are computed, not authored — the colour set moves with the photos.
     tonal = nearest neighbour · clash = furthest · classic = darkest */
  function pickLook(colorKey, pool, mode) {
    var me = COLORS[colorKey];
    var others = pool.filter(function (k) { return k !== colorKey; });
    if (!me || !others.length) return pool[0];
    if (mode === 'clash') return others.reduce(function (a, b) { return gap(me.h, COLORS[b].h) > gap(me.h, COLORS[a].h) ? b : a; });
    if (mode === 'classic') return others.reduce(function (a, b) { return lum(COLORS[b].h) < lum(COLORS[a].h) ? b : a; });
    var live = chroma(me.h) > 26 ? others.filter(function (k) { return chroma(COLORS[k].h) > 26; }) : [];
    var field = live.length ? live : others;
    return field.reduce(function (a, b) { return gap(me.h, COLORS[b].h) < gap(me.h, COLORS[a].h) ? b : a; });
  }

  /* (colorLine and its COLOR_LINE index are gone: the colour-step's small line
     "{colour}. MOVAS™ leather." is the leftover the 2026-09-04 review asked us to
     drop, and it had no second caller. The colour dictionary still carries `line`
     in the data island — read it off COLORS if it is ever wanted again.) */

  /* every snap-on seats identically: measured off the reference composite —
     277x607 device, stand 188px wide with its top edge 33.6% down the back. */
  var STAND_WR = 0.679;
  var DECK_MAX = 24;   /* how deep the rail runs before it repeats */

  /* the hero combo drives the attract loop until the user picks */
  /* The attract loop holds one device and changes what it wears — so the eye
     reads "same phone, a thousand outfits" rather than "different phones". */

  /* ================= state ================= */
  var state = {
    model: null, body: null,
    caseType: 'movas', color: null, colorManual: false,
    fam: FAMS[0].k, ver: {}, standColor: null, standManual: false, standPicked: false,
    cart: 0
  };
  function curModel() { return state.model || DEF.m; }
  function curBody() { return state.body || DEF.b; }
  var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  function $(id) { return root.querySelector('#mfs-' + id); }
  function find(a, k) { for (var i = 0; i < a.length; i++) if (a[i].k === k) return a[i]; return a[0]; }
  function verName(f) { return f.vers ? f.vers[state.ver[f.k] || 0] : f.n; }
  /* The one-line intro for the version now on the phone — client-approved copy,
     keyed per SKU in the data island's vwhat table (see mf-studio-data.liquid).
     Three places show it (board head's third line, the desk info box, the phone
     product sheet), so it is read here once: the fallback order cannot drift
     between them, and a product-level `what` only stands in for a version that
     has no line of its own. Callers add their own last resort. */
  function verWhat(f) {
    if (!f) return '';
    return (f.vwhat && f.vwhat[state.ver[f.k] || 0]) || f.what || '';
  }
  /* Which versions this product still sells. The island's hidden-SKU list can
     empty a whole Version (a collaboration, a run that never shipped), and a chip
     for a version with no variant behind it is a dead end: it selects, the colour
     pool comes back empty and the deck falls through to another product's palette.
     Indexes stay the product's own — vers[] keeps every slot so state.ver, the
     variant records and the version copy all line up; only the chips are filtered.
     An island without vlive (older payload) means "all of them". */
  function verLive(f) {
    var out = [], i, n;
    if (!f || !f.vers) return out;
    if (f.vlive && f.vlive.length) {
      for (i = 0; i < f.vlive.length; i++) {
        n = f.vlive[i];
        if (n >= 0 && n < f.vers.length && out.indexOf(n) < 0) out.push(n);
      }
      out.sort(function (a, b) { return a - b; });
    }
    if (!out.length) for (i = 0; i < f.vers.length; i++) out.push(i);
    return out;
  }
  /* the version this product opens on: its first live one, not a blind 0 */
  function verOf(f) {
    var cur = state.ver[f.k] || 0, live = verLive(f);
    return live.indexOf(cur) > -1 ? cur : (live[0] || 0);
  }
  /* Prices are formatted in the page's own currency (kk 2026-10-23: a Thai
     session showed "+$684.00" for a ฿684 surcharge). The island's prices are
     already in the presentment currency, only the symbol was hard-coded. The
     format string comes from mf-money (data-mfs-money), the same one Color Code
     and the buy engine read, and the placeholder table mirrors theirs so the
     no-decimal markets (JPY) keep their thousands separator. */
  var MONEY_FMT = root.getAttribute('data-mfs-money') || '${{amount}}';
  var MONEY_SEP = {
    amount: [2, ',', '.'],
    amount_no_decimals: [0, ',', '.'],
    amount_with_comma_separator: [2, '.', ','],
    amount_no_decimals_with_comma_separator: [0, '.', ','],
    amount_with_space_separator: [2, ' ', ','],
    amount_no_decimals_with_space_separator: [0, ' ', ','],
    amount_with_apostrophe_separator: [2, "'", '.'],
    amount_with_period_and_space_separator: [2, ' ', '.']
  };
  var MONEY_TAG = MONEY_FMT.match(/\{\{\s*([a-z_]+)\s*\}\}/);
  var MONEY_RULE = (MONEY_TAG && MONEY_SEP[MONEY_TAG[1]]) || MONEY_SEP.amount;
  function money(v) {
    var parts = (Number(v) || 0).toFixed(MONEY_RULE[0]).split('.');
    var body = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, MONEY_RULE[1]) + (parts[1] ? MONEY_RULE[2] + parts[1] : '');
    return MONEY_TAG ? MONEY_FMT.replace(MONEY_TAG[0], body) : '$' + body;
  }
  /* ---- board title ----
     One rule decides what the top-left block says, and it comes from how the step
     presents its options rather than from the step's number:

       · options all on screen at once (02 two cases, 03 the colour orbit,
         05 the swatch strip) -> the 48px line ASKS for the decision, and the 20px
         line under it carries what has been decided so far.
       · options scrolling past one at a time (04 the stand row) -> the 48px line
         NAMES the product now at centre and the 20px line is its version, because
         "what am I looking at" is the question the user actually has there.

     The sub is live in both cases: it accumulates the build as the user walks the
     flow, so the choices already made stay visible without a summary panel. */
  /* ---- board copy: a creation flow, not a checkout flow (Kay, 2026-08-31) ----
     Every title is an invitation to ACT (verb first), every sub is encouragement
     — no spec strings, no product names. The metaphor carries the arc: the case
     is the CANVAS you pick then paint; the stand builds the SCENE you live in;
     the last step signs the work. Product facts live in the breadcrumb, the
     version tabs and the small caption under the stand row — never up here. */
  /* The boards name a case by its material, not its catalogue title: the same
     "Snap Case MOVAS™" reads as "MOVAS™" in the crumb and "MOVAS™ Case" in a
     sub line. One place to strip it so the two never drift apart. */
  function caseShort(k) {
    var c = find(CASES, k || state.caseType);
    return c ? c.n.replace(/^Snap\s+/i, '').replace(/\s*Case\s*/i, ' ').trim() : '';
  }
  function boardCopy() {
    var ph = root.getAttribute('data-ph');
    /* Board copy is the design's (Snap Play, 554:1011). Two things changed with
       it: the titles name the step's own subject instead of a generic prompt,
       and the sub is no longer a slogan — it states what you are working ON
       (the phone you picked, the case you are colouring), so the line is live
       rather than decorative. */
    /* Copy revision (2026-09-04 "Studio" review: replace every board title,
       referring to the DESKTOP section). The case-pick pair comes from the new
       frame the review itself supplies (1056:3241); the rest from the matching
       board in Desktop final (802:5613). */
    /* Figma 571:1173 (Snap Play, "02 选壳 · Choose this case"), user 2026-09-05.
       The \n in the board's 48px line is deliberate — it is kept as two array
       entries so desk joins them with <br> exactly where the design breaks, and
       the phone column joins them with a space and wraps to its own width
       (setBoardHead's rule). Sentence case throughout, per the same message:
       「所有标题只有首字母大写，同一句的不同单词不用」. */
    if (ph === 't1' && typePicking())
      return { t: ['Two shells.', 'Two moods.'], s: 'MOVAS\u2122 or Frame \u2014 tap either to try it on.' };
    if (ph === 't1')
      return { t: ['Color it in.'], s: 'Tap a shade to wear it.' };
    if (ph === 't2') {
      /* the exception the rule above already names: the stand row scrolls its
         options past one at a time, so "what am I looking at" is the live
         question and the 48px line answers it — the product now at centre, with
         its version on the 20px line. The invitation to act has been made by the
         time the row is moving (user, 2026-09-01).
         The name wraps on the block's 427px width, so it is one string, not a
         hand-split pair. */
      var f = find(FAMS, state.fam);
      /* Desk puts the VERSION here (802:5121, in ink — it is one of the four
         choices below). The phone design (1147:3506) puts the product's own line
         here in grey instead, because the version already reads on the chip row
         under the stage: two lines saying "With a Stand" would be one too many.

         Desk additionally carries the one-line intro on a THIRD line, in grey
         under the version (user, 2026-09-10). That copy is the client-approved
         per-SKU text — see the vwhat table in mf-studio-data.liquid. The phone
         build gets no third line: its sub IS that copy already. */
      if (f) {
        var vi = (state.ver || {})[f.k] || 0;
        var vname = f.vers ? (f.vers[vi] || '') : '';
        /* Version-level line first: with Standard and Find My-Enabled side by
           side, only the per-version line says what the difference is. Falls
           back to the product-level blurb when a version has no copy. */
        var blurb = verWhat(f) || f.s || '';
        if (isMob()) return { t: [f.n], s: blurb || vname };
        return { t: [f.n], s: vname || blurb, b: vname ? blurb : '' };
      }
      return { t: ['Build Your', 'Scene'], s: 'Desk, bed, anywhere you create.' };
    }
    if (ph === 't3') {
      /* Desktop final's board 05 hard-codes "Twelve colorways"; the pool is
         derived per version, so this reports the real count — a literal 12 goes
         wrong the moment a version sells a different number, and the whole point
         of the line is to say how many there are. The product name is not in
         these two lines; it rides the right-hand info block, as the design has it. */
      /* Figma 571:1332 ("05 支架信息 · Info box 展开"), user 2026-09-05: this
         board's 48px line is the COLOUR you are on, and the 20px line under it
         is that colour's own tagline — not a generic prompt. PROGRESS had this
         parked as "看不出来，没做"; the user has now called it.
         Only a handful of codes carry a tagline in the dictionary, so the count
         line stays as the fallback: it is the one true thing we can always say
         about this board, and an empty sub reads as a bug. */
      var pool3 = standColors(state.fam).filter(function (k) {
        return !!standVariant(state.fam, k, (state.ver || {})[state.fam] || 0);
      });
      var n3 = pool3.length || standColors(state.fam).length;
      var sc3 = COLORS[state.standColor || ''];
      return { t: [sc3 ? sc3.n : 'Color it yours.'],
               s: (sc3 && sc3.line) || (n3 + ' colorways. Tap to color it.') };
    }
    return { t: [SERIES], s: 'One phone. A thousand outfits.' };
  }
  /* The block changes with the row now, and a straight textContent swap read as a
     glitch — between two frames the line was simply a different line, with no
     account of where the old one went (user, 2026-09-01). So the outgoing line
     lifts out of the block and the incoming one comes up from under it: one
     short push, travelling the way the type reads. The sub follows a beat later
     so the pair reads as one line changing rather than two. */
  function pushLine(el, txt, asHTML, delay) {
    if (!el) return;
    var cur = asHTML ? el.innerHTML : el.textContent;
    if (cur === txt) return;
    var set = function () { if (asHTML) el.innerHTML = txt; else el.textContent = txt; };
    if (reduced || !('animate' in Element.prototype)) { set(); return; }
    /* a fill:forwards animation outlives its run — leaving them to pile up would
       pin the line at whatever the last one ended on */
    if (el._push) el._push.forEach(function (a) { try { a.cancel(); } catch (e) {} });
    clearTimeout(el._pushT);
    el._push = [el.animate(
      [{ transform: 'translateY(0)', opacity: 1 }, { transform: 'translateY(-0.34em)', opacity: 0 }],
      { duration: 165, delay: delay || 0, easing: 'cubic-bezier(.5,0,.75,.3)', fill: 'forwards' })];
    el._pushT = setTimeout(function () {
      set();
      el._push.push(el.animate(
        [{ transform: 'translateY(0.44em)', opacity: 0 }, { transform: 'translateY(0)', opacity: 1 }],
        { duration: 300, easing: 'cubic-bezier(.16,.86,.22,1)', fill: 'both' }));
    }, 165 + (delay || 0));
  }
  function setBoardHead() {
    var c = boardCopy(), h = $('boardTitle'), b = $('boardSub'), g = $('boardBlurb');
    if (!h) return;
    /* The board's titles are hand-split for a 427px block at 48px. In a 361px
       column at 17px those breaks land in the wrong places, so the phone build
       joins the parts and lets the line wrap to the column it actually has (the
       head clamps at two lines). */
    pushLine(h, c.t.join(isMob() ? ' ' : '<br>'), true, 0);
    pushLine(b, c.s, false, 55);
    /* every other board leaves `b` undefined, so the line empties and :empty
       collapses it — no stray gap under the sub */
    pushLine(g, c.b || '', false, 90);
  }

  /* ---- one definition of a step ----
     The stage is described by THREE independent attributes — data-ph (phase),
     data-cs (case sub-step) and data-sn (stand sub-step) — and nearly every CSS
     rule keys off a combination of them. Each entry point used to set only the
     ones it happened to care about, and data-sn was never cleared at all, so
     walking backwards through the breadcrumb left the others behind:

       t3 → [Case]  set ph=t1, cs=pick … but left sn="color"
          → [Stand] set sn="pick"      … but left cs="pick"
          → and the second case, whose only hiding rule is
            `body:not([data-cs="pick"]) .alt-case`, stayed on stage through
            checkout.

     Every navigation now names a step and this decides what that step means. */
  var STEP_STATE = {
    /* entry is a step too: going back to the first screen has to CLEAR data-cs,
       or the `:not([data-cs="pick"])` hide rule no longer holds and the spare case
       stays on stage — exactly the bug the comment above this table records. */
    entry:     { ph: 'entry', cs: null,   sn: null },
    casePick:  { ph: 't1',   cs: 'pick',  sn: null },
    casePaint: { ph: 't1',   cs: 'paint', sn: null },
    standPick: { ph: 't2',   cs: 'paint', sn: 'pick' },
    standPaint:{ ph: 't3',   cs: 'paint', sn: 'color' },
    done:      { ph: 'done', cs: 'paint', sn: 'color' }
  };
  function setStep(key) {
    var st = STEP_STATE[key];
    if (!st) return;
    /* Nothing hides the title any more (swapCase used to), but a step change
       clearing the class is a cheap, idempotent guard: a stale .mfs-head-hidden
       left by an older cached bundle would otherwise blank the head for good. */
    var head = $('boardHead');
    if (head) head.classList.remove('mfs-head-hidden');
    if (st.cs) root.setAttribute('data-cs', st.cs);
    else root.removeAttribute('data-cs');
    if (st.sn) root.setAttribute('data-sn', st.sn);
    else root.removeAttribute('data-sn');
    setPhase(st.ph);        /* last — it re-syncs the chrome off the other two */
    setBoardHead();
  }

  function setPhase(p) {
    if (typeof swingSettle === 'function') swingSettle();   /* never fly across steps */
    if (typeof verMenu === 'function') verMenu(false);      /* no open menu across steps */
    root.setAttribute('data-ph', p);
    mobFit();          /* phone build: a phase change is a resize (see mobFit) */
    setTimeout(updHint, 80);
    setBoardHead();
    if (typeof hasLook !== 'undefined') flowSync();
    $('fbPaint').classList.toggle('mfs-now', p === 't1');
    $('fbSnap').classList.toggle('mfs-now', p === 't2');
    $('fbColor').classList.toggle('mfs-now', p === 't3');
    /* the phase just became visible, so widths are measurable now — resize both strips' end spacers */
    requestAnimationFrame(function () {
      if (typeof fanEl !== 'undefined' && fanEl) sizeSpacers(fanEl);
      if (typeof standFanEl !== 'undefined' && standFanEl) sizeSpacers(standFanEl);
    });
  }
  function micro(html) { $('micro').innerHTML = html; flowSync(); }
  function flowSync() {
    var picking = root.getAttribute('data-cs') === 'pick';
    var caseDone = hasLook && !picking;
    /* full product names (client, 2026-10-23 NO.250): the first word alone read
       "MOFT" for MOFT O and "Snap" for every Snap stand, and the case lost its
       "Case". The pill is a third of the bar on desk; anything longer than that
       ellipsises in CSS rather than being cut here. */
    var cc = caseDone ? find(CASES, state.caseType) : null;
    $('fvCase').textContent = cc ? cc.n : '';
    $('fvStand').textContent = state.standPicked ? find(FAMS, state.fam).n : '';
    var pd = state.standPicked && state.color && state.standColor;
    $('fvPaint').textContent = pd ? find(MOVAS, state.color).n + ' / ' + find(MOVAS, state.standColor).n : '';
    /* phone build: "Misty Cove x Cement · Misty Cove" does not fit a 393px bar,
       so a finished step leaves a colour receipt instead of its value — the case
       colour, a filled mark for the product, then the stand colour. */
    mobDot('fdCase', caseDone && state.color ? swatchBg(find(MOVAS, state.color)) : '');
    mobDot('fdStand', state.standPicked ? 'var(--ink)' : '');
    mobDot('fdPaint', pd ? swatchBg(find(MOVAS, state.standColor)) : '');
    /* forward-gating: Stand unlocks once a case is committed, Paint once a stand
       is — a crumb for a step never reached must not be clickable (Kay).
       "Case committed" is data-cs==='paint' (set by chooseCase and the dice),
       NOT hasLook — that flag means the entry bang has played, which debug and
       deep-link paths skip. */
    $('fbSnap').disabled = root.getAttribute('data-cs') !== 'paint';
    $('fbColor').disabled = !state.standPicked;
    /* the title's sub line tracks the same state as the breadcrumb values, so it
       rides the same settle hook rather than needing its own call sites */
    setBoardHead();
  }
  function mobDot(id, hex) {
    var el = $(id); if (!el) return;
    el.style.background = hex || '';
    el.classList.toggle('mfs-on', !!hex);
  }
  /* what each case type wears on the pick board, before a colour is chosen */
  /* ---- the default look (2026-09-04 review: "the case and stand model-picking
     boards must all use the default white product art") ----
     That art is listed in the Figma "默认素材" (default art) section (1168:3065)
     — the section name is kept verbatim so it can be searched for — and every file
     name ends in MCGY: mf-layer-MD020-1-{model}-MCGY / MD023-1-{model}-MCGYCMGY /
     MS007MP-1-MO-MCGY / MS027-1-MO-MCGY / MB005S-1-MCGY / MC002-1-ME-MCGY.
     So "default" is the colour code MCGY in the data; the two-tone case's
     MCGYCMGY is the same batch of white, hence the prefix match. Only when the
     pool has neither (a version that does not sell it) does this fall back to the
     first colour — otherwise the board would come up empty. */
  var DEFAULT_LOOK = 'MCGY';
  function defaultIn(pool) {
    if (!pool || !pool.length) return null;
    var i;
    for (i = 0; i < pool.length; i++) if (pool[i] === DEFAULT_LOOK) return pool[i];
    for (i = 0; i < pool.length; i++) if (pool[i].indexOf(DEFAULT_LOOK) === 0) return pool[i];
    return pool[0];
  }
  function pickDefault(ct) { return defaultIn(poolForType(ct)); }
  function typePicking() { return root.getAttribute('data-cs') === 'pick'; }
  /* The two model-picking boards: the case pick (t1 · pick) and the stand pick
     (t2 · pick). Those boards compare SHAPES, so everything wears the default
     white there; the colour boards go straight back to what the user picked. */
  function modelPicking() {
    return typePicking()
        || (root.getAttribute('data-ph') === 't2' && root.getAttribute('data-sn') === 'pick');
  }
  /* the frame case tints against the device body — during type selection that would
     tint it orange, so the device reads as plain white too */

  /* ================= naming ================= */
  function comboWords() {
    var c = find(MOVAS, state.color), s = find(MOVAS, state.standColor);
    var w1 = c.w;
    if (w1 === s.w) return 'Full ' + w1;
    return w1 + ' ' + s.w;
  }
  function comboSubText() {
    var c = find(MOVAS, state.color), s = find(MOVAS, state.standColor), f = find(FAMS, state.fam);
    return (c.n + ' case') + ' × ' + s.n + ' ' + f.n;
  }
  function fullPrice() { return casePrice() + standPrice(find(FAMS, state.fam)); }
  /* Capped at the set total so a discount larger than the pair cannot show a
     negative price. */
  function saveAmt() { var f = fullPrice(); return Math.min(SAVE_OFF, f > 0 ? f : 0); }
  function setPrice() { return Math.round((fullPrice() - saveAmt()) * 100) / 100; }
  function saveLabel() { return SAVE_BADGE || ('Save ' + money(SAVE_OFF)); }
  /* The receipt shows a per-line net price, but a fixed amount belongs to the
     set, not to either item. Split it in proportion to each line's share of the
     total so the two lines still add up to the total shown below them. */
  function lineNet(p) {
    var f = fullPrice();
    if (f <= 0) return p;
    return Math.round((p - saveAmt() * (p / f)) * 100) / 100;
  }

  /* ================= stage render ================= */
  /* one scale for the whole rig — the phone and the loose stands beside it have
     to grow together, so this can't be a transform on .phone alone */
  /* 1.69 puts the device at 213px wide, measured off the design board (the centre
     case on 1:142 scans 213×487). Everything on the stage is keyed off Z — stand,
     rail pitch, ring radius — so they scale with it and keep their relationships.
     The aspect still differs (0.479 here vs 0.437 on the board): the board render
     is a photo of a phone *in a case*, which is taller than the device. Only the
     bitmaps can close that, so width is what we match. */
  function Z() {
    if (!isMob()) return 1.69;
    if (!mobZ) mobZ = (mobDevH() / MOB_FILL) / COREG_SIDE * (COREG && landscapeModel() ? MOB_LAND_K : 1);
    return mobZ;
  }
  /* An unfolded iPhone Duo fills ~96% of the column at the portrait scale and
     reads as too big (client, 2026-10-23 NO.261: "shrink to 90%"). Only the
     device scale drops — stand, rail pitch and ring follow Z — the stage band
     keeps its height, so every control stays where it was. */
  var MOB_LAND_K = 0.9;

  /* ================= the phone build: one column, five bands =================
     Desk pins every control to a board coordinate (deviation #30) around a device
     whose width is a constant. A phone column cannot use those coordinates, so
     this build stacks five bands with fixed heights instead — top bar · board
     head · stage · deck · commit — and the stage carries the device at ONE size
     for the whole flow, a pure function of the column's width (--mfs-stageH owns
     the number; Z() reads it back off the laid-out stage so there is only ever
     one copy of it). Every control therefore sits in the same place in every
     phase, on every handset, with nothing to re-measure per model.

     The studio is a section in the page, not a layer over it: it is taller than a
     short viewport and the page scrolls the difference, like any other section. */
  var MOB_DECK = 118;              /* the deck band: dial seats · stand thumbs · swatches */
  /* mirrors --mfs-stageH (see the stylesheet, THE PHONE BUILD): the device is ONE
     size for the whole flow, a pure function of the frame's WIDTH, and the
     stylesheet owns it. These two only serve the very first frame, before there is
     a laid-out stage to measure. */
  var MOB_STAGE_K = 0.909, MOB_STAGE_MAX = 440;
  /* The DEVICE's height, which is not the stage's: the stage is taller by
     --mfs-air top and bottom so the device has room to breathe, and the device is
     centred in it. Z() is derived from this one; anything positioned against the
     deck band (the dial's seats, the stand row) measures the STAGE instead. */
  function mobDevH() {
    var st = $('stage'), fr = $('phoneFrame');
    var w = (st && st.clientWidth) || (fr && fr.clientWidth) || window.innerWidth || 393;
    return Math.max(200, Math.min(w * MOB_STAGE_K, MOB_STAGE_MAX));
  }
  /* Measured off the delivered layers rather than guessed (canvas alpha scan,
     2026-09-02): a case fills 0.815 of the 1080 canvas height (MD020 .8148 /
     MD023 .7741) and a stand 0.50 × 0.341, both centred on the canvas. The
     DRESSED device is the case's extent, so that is what the stage height is
     divided by to get the canvas — the transparent remainder overflows the
     stage top and bottom, which is why the phone build does not clip it. */
  var MOB_FILL = 0.815;
  function isMob() { return root.classList.contains('mfs-mob'); }

  /* ================= the loading budget =================
     One number decides everything about how far ahead this page loads: how much
     DECODED bitmap it is allowed to hold. Bytes are not the constraint (the CDN
     answers a browser Accept header with webp/avif — a 1080 square measured
     16.7KB on the test theme), decoded bitmaps are: 4.45MB each, whatever size
     they are painted at, and a phone that holds too many gets its tab reaped.

       mb    bitmap ceiling for the warm cache (evictWarm trims to it)
       railR floor for how many seats either side of centre carry an <img>
             (railWinR raises it to whatever the viewport actually shows)
       lead  how many extra seats to fill AHEAD of a spin, in its direction
       nearN how far either side of the current chip a colour strip decodes
             before it falls back to bytes-only

     Recomputed on the desk/phone switch, so a resized window gets the other
     build's budget rather than keeping the one it booted with. */
  var BUD = null;
  function budget() {
    if (BUD) return BUD;
    var mem = navigator.deviceMemory || (isMob() ? 4 : 8);
    var save = !!(navigator.connection && navigator.connection.saveData);
    if (save) BUD = { mb: 40, railR: 1, lead: 1, nearN: 1 };
    else if (isMob()) BUD = { mb: mem <= 3 ? 55 : 90, railR: 2, lead: 2, nearN: 3 };
    else BUD = { mb: mem <= 4 ? 160 : 300, railR: 4, lead: 3, nearN: 8 };
    return BUD;
  }
  function resetBudget() { BUD = null; }
  var mobZ = 0;
  /* the STAGE box — the device plus its air. Only the things that hang off the
     stage's edges into the deck band need this. */
  function mobStageH() {
    var st = $('stage'), h = st ? st.clientHeight : 0;
    if (h < 140) h = mobDevH();    /* pre-layout, or the studio is still hidden */
    return Math.max(200, h);
  }
  function frameW() { var st = $('stage'); return (st && st.clientWidth) || 393; }

  /* ---- the page behind a bottom sheet must not scroll ----
     The studio itself never takes the screen — it is a section in the page, header
     and footer included (user, 2026-09-02: "不用点 start 推到没有 header 和 footer
     了", "正常交互就好"). So this is only about the sheets: the iPhone picker, the
     product sheet, the poster. They are fixed over the viewport, and a page
     sliding behind one reads as a bug. The scrolling element is <html>, never
     body (env fact), and data-mf-overlay is the house lever that pushes the
     rewards widget's int-max z-index back under our own overlay. */
  var mobLocked = false, mobScrollY = 0, mobSetOverlay = false;
  function mobLock(on) {
    on = !!on;
    if (on === mobLocked) return;
    mobLocked = on;
    var h = document.documentElement;
    if (on) {
      mobScrollY = window.scrollY || h.scrollTop || 0;
      if (!h.hasAttribute('data-mf-overlay')) { h.setAttribute('data-mf-overlay', ''); mobSetOverlay = true; }
      h.style.overflow = 'hidden';
    } else {
      if (mobSetOverlay) { h.removeAttribute('data-mf-overlay'); mobSetOverlay = false; }
      h.style.overflow = '';
      window.scrollTo(0, mobScrollY);
    }
  }
  /* one call site for every sheet open and close: the lock follows whether a
     sheet is actually on screen, rather than each path remembering to pair itself */
  function mobSheets() {
    mobLock(isMob() && !!root.querySelector('.mfs-modal.mfs-open'));
  }
  /* The device is ONE size for the whole flow now (see --mfs-stageH: a function
     of the frame's width, which a phase change cannot alter), so this is purely a
     resize hook. It stays on setPhase because the first phase change is also the
     first moment the stage is measurable. */
  function mobFit() {
    if (!isMob()) return;
    mobZ = 0;
    renderPhone(); renderStand();
  }

  function renderPhone() {
    /* the stage device keeps one silhouette; the model choice drives copy and SKUs only */
    var m = find(MODELS, DEF.m), ph = bodyRec(curModel(), curBody());
    var el = $('phoneEl'), z = Z();
    /* the pick board shows the real products (Figma 571:1173), not white tints —
       the neutral colourway stands in until the user paints */
    var c = typePicking() ? find(MOVAS, pickDefault(state.caseType)) : find(MOVAS, state.color);

    /* ---- layer composite ----
       DEF.m, not curModel(): the stage device is fixed (Kay, 2026-08-31) — the
       model choice drives copy, cart and SKU, never the hero's shape. The body
       COLOUR still follows the picker. */
    var caseL = stageLay(caseLayer(state.caseType, curModel(), c.k)),
        bodyL = stageLay(bodyLayer(curModel(), curBody()));   /* the hero wears the picked model+colour (Kay, final) */
    {
      /* every board is the layered build now (deviation #27) — a missing case
         layer paints nothing at all inside the same 572 frame */
      var w = Math.round(m.w * z), h = caseL ? Math.round(w / layerAR(caseL)) : Math.round(m.h * z);
      if (COREG) { w = sqPx(); h = w; }
      el.style.width = w + 'px'; el.style.height = h + 'px';
      el.classList.add('mfs-layered');
      var slot = el.querySelector('.mfs-lay-slot');
      if (!slot) { slot = document.createElement('div'); slot.className = 'mfs-lay-slot'; el.insertBefore(slot, el.firstChild); }
      /* The hero goes caseless wherever something else is already showing the
         case: desk's whole t1 (pick = #mfs-wornCase, paint = the ring's centre
         item), and the phone build's PICK step (same worn card). The phone
         build's paint step is the exception — its dial is a strip of thumbnails
         beside the device, so there the hero is the only thing wearing a case.
         Missing the phone build's pick step painted the case twice, exactly on
         top of itself: invisible until something moved, and then a case slid off
         a phone that was still wearing one (user, 2026-09-03: "手机端就还是多了
         一层手机壳…在蓝色滑过去的过程，手机上就明显还有一个壳在"). Latent since
         the COREG rework; the follow-the-finger drag is what made it holdable. */
      var t1 = root.getAttribute('data-ph') === 't1';
      var wheelOn = t1 && (root.classList.contains('mfs-desk')
                           || root.getAttribute('data-cs') === 'pick');
      var group = [['mfs-lay-body', bodyL, 'left:' + LBOX.body.x + '%;width:' + LBOX.body.w + '%']];
      if (wheelOn) dropLayer(slot, 'mfs-lay-case');
      else group.push(['mfs-lay-case', caseL, 'left:0;top:0;width:100%']);
      swapLayerGroup(slot, group);
    }
  }

  /* ================= visual source indirection =================
     Two renderers live behind one call. The procedural build (DOM spans tinted
     by --sc) is what shipped; the layer build composites the delivered PNGs.

     Delivery contract — asset delivery v1, drawing spec (asset-spec.md):
       · body x13 + case x34 + stand x60 layers = the stage composites any combination live
       · every layer shares one canvas, the MagSafe centre is the common origin of all three
       · PNG-24 transparent, 0-degree frontal view, no baked shadows (the front end draws them)

     Because the origin is shared, a composite is three images pinned to one box
     and there is no per-SKU offset table. Anything the manifest can't resolve
     falls through to procedural, so the batch can land one SKU at a time and the
     page never breaks in between. */


  function imgTag(cls, name, style) {
    return '<img class="mfs-lay ' + cls + '" src="' + layerURL(name) + '" alt=""'
         + (style ? ' style="' + style + '"' : '') + '>';
  }

  /* ================= no-blank-frame swaps =================
     Every layer change used to rebuild its <img> via innerHTML: the old bitmap
     died before the new one had pixels, and that gap read as a white flash.
     swapLayer() decodes the incoming bitmap OFF the DOM first, then fades it in
     over the outgoing one; the old image keeps its pixels until the new one is
     fully painted, so there is no frame without content. */
  /* stacking weights inside a layer slot: lower mounts under higher */
  var LAY_ORDER = { 'mfs-lay-body': 1, 'mfs-lay-fill': 1, 'mfs-lay-case': 2 };
  /* ================= two-tier warm-up =================
     The old warm() did one thing: fetch AND decode, forever, with no ceiling.
     That conflated two costs that are three orders of magnitude apart — ~17KB
     of webp against 4.45MB of bitmap — and it is why the desk ladder's blanket
     sweep of 169 layers was quietly asking for ~750MB of decoded squares.

       warmSrc(u)    decode now, hold the bitmap, charge it to the budget
       fetchSrc(u)   pull the bytes into the HTTP cache and NEVER decode

     fetchSrc uses an <img> rather than fetch() on purpose. The CDN varies on
     Accept: a browser image request gets webp/avif (16.7KB), while fetch()'s
     default `Accept: * / *` gets PNG (175KB) — a DIFFERENT cache entry, so a
     fetch()-based prefetch would be worse than useless, paying ten times the
     bytes and still leaving the real <img> to miss. An <img> request is
     byte-identical to the one the page will make. It is never decode()d and the
     reference is dropped on load, so nothing is charged to the budget.

     Both are keyed by resolved URL, not by layer name, so the angle and gallery
     shots — which only ever exist as URLs — go through the same machinery as
     the layers. */
  var WARM = {}, warmMB = 0, warmClock = 0, FETCHED = {};
  function warmSrc(u, prio) {
    if (!u) return Promise.resolve(null);
    var rec = WARM[u];
    if (rec) { rec.at = ++warmClock; return rec.p; }
    var im = new Image();
    im.decoding = 'async';
    if (prio && 'fetchPriority' in im) im.fetchPriority = prio;
    im.src = u;
    var done = im.decode ? im.decode().catch(function () {}) : Promise.resolve();
    rec = WARM[u] = { im: im, ready: false, p: null, at: ++warmClock, mb: srcMB(u) };
    warmMB += rec.mb;
    /* Trim on the way IN as well as on the way out. Hanging eviction off decode
       completion alone left the cache parked above its ceiling whenever the
       page went quiet right after crossing it — measured on the phone build,
       111MB held against a 90MB budget with nothing left to decode to trigger
       the sweep. The record just added is not ready yet, so it can never evict
       itself. */
    evictWarm();
    /* decode() never settles in a background tab — time out so the promise keeps
       moving; `ready` stays honest (set only when pixels are provably there) */
    rec.p = Promise.race([done, new Promise(function (r) { setTimeout(r, 1200); })])
      .then(function () {
        if (im.complete && im.naturalWidth) rec.ready = true;
        /* a load that definitively failed (complete, no pixels) must give its
           allowance back — otherwise every missing file permanently shrinks the
           budget and nothing else can be held. The record stays a few seconds
           so the URL is not retried on a loop, then goes: a dropped request on
           a phone network is not a missing file, and keeping the record for
           good left that layer unpaintable for the rest of the visit (NO.264). */
        else if (im.complete) failed();
        else im.addEventListener('error', failed, { once: true });
        evictWarm();
        return im;
      });
    function failed() {
      warmMB -= rec.mb; rec.mb = 0;
      setTimeout(function () { if (WARM[u] === rec) delete WARM[u]; }, 4000);
    }
    return rec.p;
  }
  /* a layer whose pixels are in, whether or not decode() got to say so before
     its timeout — the attract loop asks this before it moves on (NO.264) */
  function warmLoaded(name) {
    var r = WARM[layerURL(name)];
    return !!(r && r.im.complete && r.im.naturalWidth);
  }
  /* Data Saver and 2G opt out of the prefetch tier completely. Nothing breaks
     without it — every image is still fetched the moment it is actually
     needed; the page just stops spending someone's bandwidth on a step they
     may never take. */
  var NO_PREFETCH = !!(navigator.connection && (navigator.connection.saveData
                    || /^(slow-)?2g$/.test(navigator.connection.effectiveType || '')));
  /* Six at a time. A board's lookahead can name eighty files and firing them
     all into one task puts eighty rows in the network panel competing with the
     stage; low priority keeps them behind it, and a shallow queue keeps the
     picture honest for anyone reading a waterfall. */
  var FQ = [], fqOn = 0, FQ_MAX = 6;
  function fetchSrc(u) {
    if (!u || NO_PREFETCH || FETCHED[u] || WARM[u]) return;
    FETCHED[u] = 1;
    FQ.push(u);
    fqPump();
  }
  function fqPump() {
    while (fqOn < FQ_MAX && FQ.length) {
      fqOn++;
      (function (img, src) {
        if ('fetchPriority' in img) img.fetchPriority = 'low';
        /* hold the reference until the bytes are in, then let it go: an <img>
           nobody references and nobody paints leaves the decoder alone, so this
           tier costs cache bytes and no bitmap */
        img.onload = img.onerror = function () {
          img.onload = img.onerror = null;
          fqOn--; fqPump();
        };
        img.src = src;
      })(new Image(), FQ.shift());
    }
  }
  /* LRU trim. Blanking the src of an OFF-DOM image releases its decode; the
     copies swapLayer mounted are separate elements with their own src, so a
     trim never touches anything on screen. Two guards keep it from evicting
     work in flight: the newest RESERVE records are always spared, and so is
     anything younger than the swapLayer cold path's own timeout. */
  /* The ceiling is SOFT, and it has to be: a record still decoding cannot be
     evicted, so a burst of warm() calls overshoots until they settle. Measured
     on the phone build (440x956 @DPR3, 90MB budget) across every board and
     every version chip: peak 103.2MB, 15% over, and only 12 of 701 sampled
     frames were above the line before it fell back to 85.7. */
  var WARM_RESERVE = 12;
  function evictWarm() {
    var cap = budget().mb;
    if (warmMB <= cap) return;
    var keys = Object.keys(WARM);
    if (keys.length <= WARM_RESERVE) return;
    keys.sort(function (a, b) { return WARM[a].at - WARM[b].at; });
    var floor = warmClock - WARM_RESERVE;
    for (var i = 0; i < keys.length && warmMB > cap * 0.8; i++) {
      var r = WARM[keys[i]];
      if (!r || r.at > floor) break;          /* sorted: everything after is younger too */
      if (!r.ready) continue;                 /* still decoding — leave it alone */
      r.im.src = '';
      warmMB -= r.mb;
      delete WARM[keys[i]];
    }
  }
  function warm(name, prio) {
    var u = layerURL(name);
    return u ? warmSrc(u, prio) : Promise.resolve(null);
  }
  function warmFetch(name) { fetchSrc(layerURL(name)); }
  function hotSrc(u) { var r = u && WARM[u]; if (!r || !r.ready) return null; r.at = ++warmClock; return r.im; }
  function hotImg(name) { return hotSrc(layerURL(name)); }
  function dropLayer(host, cls) {
    if (host.__mfsW) host.__mfsW[cls] = null;
    Array.prototype.forEach.call(host.querySelectorAll('img.' + cls), function (n) { n.remove(); });
  }
  function swapLayer(host, cls, name, style) {
    if (!name) { dropLayer(host, cls); return; }
    var have = host.querySelectorAll('img.' + cls);
    var cur = have.length ? have[have.length - 1] : null;
    if (cur && cur.getAttribute('data-n') === name) {
      if (style != null) cur.style.cssText = style;   /* size may still change */
      return;
    }
    /* out-of-order guard: rapid re-picks race their decodes — only the swap
       whose name still matches the latest ask is allowed to land */
    var w = host.__mfsW || (host.__mfsW = {});
    w[cls] = name;
    function land(im, instant) {
      if (w[cls] !== name || !host.isConnected) return;
      var olds = host.querySelectorAll('img.' + cls);
      var nu = document.createElement('img');
      nu.className = 'mfs-lay ' + cls;
      nu.setAttribute('data-n', name);
      nu.alt = '';
      nu.decoding = 'async';
      if (style != null) nu.style.cssText = style;
      nu.src = im.src;              /* decoded already — paints on its first frame */
      /* DOM order IS stacking order in the slot — a blind append put a fresh
         body ABOVE the case (the device popped over its own case, then dropped
         back on the next repaint). Land right on top of the outgoing image of
         the same layer; first mounts slot in below any higher layer. */
      var anchor = olds.length ? olds[olds.length - 1].nextSibling : null;
      if (!olds.length) {
        var myO = LAY_ORDER[cls] || 9, kids = host.children;
        for (var i = 0; i < kids.length; i++) {
          var kcls = (kids[i].className || '').match(/mfs-lay-[a-z]+/);
          if (kcls && (LAY_ORDER[kcls[0]] || 9) > myO) { anchor = kids[i]; break; }
        }
      }
      function retire() { Array.prototype.forEach.call(olds, function (o) { o.remove(); }); }
      if (instant || reduced) { host.insertBefore(nu, anchor); retire(); return; }
      nu.classList.add('mfs-xin');
      host.insertBefore(nu, anchor);
      void nu.offsetWidth;          /* commit the transparent frame */
      nu.classList.remove('mfs-xin');
      /* not transitionend — transitions freeze in background tabs */
      setTimeout(retire, 240);
    }
    /* already decoded → swap in the SAME frame, no fade: choreography that pairs
       a content swap with a position tween (swapCase, flights) stays in sync,
       exactly like the old synchronous innerHTML path — minus the blank frame */
    var hot = hotImg(name);
    if (hot) { land(hot, true); return; }
    /* cold: the old bitmap holds the stage; the new one fades in when its pixels
       are REALLY there (a timeout-resolved warm() may still be undecoded) */
    warm(name).then(function (im) {
      if (!im || w[cls] !== name || !host.isConnected) return;
      if (im.complete && im.naturalWidth) { land(im, false); return; }
      im.addEventListener('load', function () { land(im, false); }, { once: true });
    });
  }
  /* deviation #27 (user, 2026-09-01), tightened 2026-09-02: NO fallback of any
     kind for a missing layer — not a vector build, not a colour block. A layer
     the manifest cannot resolve is passed to swapLayer as null, which drops the
     <img> and leaves the slot empty: a gap in the delivery reads as a gap
     instead of quietly shipping a shape that is not the product. */
  /* grouped swap: layers that change TOGETHER (a model switch moves body and
     case at once) must land in the same frame — two independent cold swaps
     arrive on their own clocks and the composite wobbles between them */
  function swapLayerGroup(host, specs) {
    var cold = [];
    specs.forEach(function (s) { if (s[1] && !hotImg(s[1])) cold.push(warm(s[1])); });
    function fire() { specs.forEach(function (s) { swapLayer(host, s[0], s[1], s[2]); }); }
    if (!cold.length) { fire(); return; }
    /* stamp the wants NOW so stray older decodes can't land in the meantime */
    var w = host.__mfsW || (host.__mfsW = {});
    specs.forEach(function (s) { w[s[0]] = s[1]; });
    Promise.all(cold).then(function () {
      if (!host.isConnected) return;
      var fresh = specs.every(function (s) { return w[s[0]] === s[1]; });
      if (fresh) fire();
    });
  }
  /* ================= the warm-up ladder =================
     Reachability decides the tier, not importance:

       ONE interaction away  -> warm()      decode, so the swap lands in-frame
       TWO or more away      -> warmFetch() bytes only, decode when it is asked for

     A miss resolver returns null and both calls no-op, so blanket sweeps are
     safe. What is NOT safe is a blanket DECODE sweep — that was the old second
     rung (169 layers, ~750MB of bitmaps on desk), and it is gone: lookahead()
     below walks the same ground one step at a time. */
  /* Warm the size the board will actually PAINT, not the original. A colour
     board draws many seats at once and the phone build draws them at 270 — so
     warming the 1080s there decoded eighteen squares the ring never asks for
     and left the ones it does ask for cold. The worn case is a different,
     single image at stage size; it gets bytes here and its decode from
     whatever swaps it in. */
  function caseSeatPx() { return isMob() ? THUMB_PX : stagePx(); }
  function warmCases(mk, decode) {
    var px = caseSeatPx();
    Object.keys(COLORS).forEach(function (k) {
      var a = caseLayer('movas', mk, k), b = caseLayer('frame', mk, k);
      if (decode) { warm(atSize(a, px)); warm(atSize(b, px)); }
      else { warmFetch(atSize(a, px)); warmFetch(atSize(b, px)); }
    });
    /* On desk the seat IS the stage square, so the line above already covered
       the worn case. The phone build draws 270 seats beside a full-size worn
       case — a different cache entry — so that one needs its own pass, and it
       cannot be a blanket decode: fifteen 1080 squares is 67MB on the device
       that can least afford it. Neighbours of the current colour get bitmaps,
       the rest bytes; a tap further along the strip decodes on the spot, which
       swapLayer covers by holding the picture already on the phone. */
    if (px === stagePx() || mk !== curModel()) return;
    var pool = caseColors(), near = budget().nearN, cur = pool.indexOf(state.color);
    if (cur < 0) cur = 0;
    pool.forEach(function (k, i) {
      var n = stageLay(caseLayer(state.caseType, mk, k));
      if (decode && Math.abs(i - cur) <= near) warm(n); else warmFetch(n);
    });
  }
  function warmModel(mk) {
    bodiesFor(mk).forEach(function (b) { warm(stageLay(bodyLayer(mk, b))); });
    warmCases(mk, true);
  }
  /* The stand strip is CSS swatches — the only picture a colour tap changes is
     the one on the phone's back, at stage size. Decoding all fifteen of those
     is 67MB, which a phone cannot hold; the strip is scrolled centred on the
     current chip, so index distance is a fair proxy for "likely next tap".
     Near neighbours decode, the rest are bytes. */
  function warmStands(fk) {
    var pool = standColors(fk), near = budget().nearN;
    var cur = pool.indexOf(state.standColor);
    if (cur < 0) cur = 0;
    pool.forEach(function (k, i) {
      var n = stageLay(standLayer(fk, k));
      if (Math.abs(i - cur) <= near) warm(n); else warmFetch(n);
    });
  }
  function idleWarm(fn) {
    if ('requestIdleCallback' in window) requestIdleCallback(fn, { timeout: 2500 });
    else setTimeout(fn, 700);
  }

  /* ================= one step of lookahead =================
     Replaces the old blanket second rung. Same ground covered, but scoped to
     what the CURRENT board can actually lead to and paid for in bytes rather
     than bitmaps, so it costs the same on a phone as on a desk and there is
     nothing to switch off.

     Called at idle after boot and again on entering each board, so it always
     answers "what is one more tap from here" rather than "what exists". */
  function shotURLs(fk, ck, vi) { return angleShots(fk, ck, vi).concat(galleryShots(fk, ck, vi)); }
  function famDefaults(skip, withShots) {
    FAMS.forEach(function (f) {
      if (f.k === skip) return;
      var pool = standColors(f.k), c = defaultIn(pool);
      warmFetch(stageLay(standLayer(f.k, c)));   /* the size the row and the phone both use */
      if (withShots) shotURLs(f.k, c, verOf(f)).forEach(fetchSrc);
    });
  }
  function lookahead() {
    var ph = root.getAttribute('data-ph'), mk = curModel();
    if (ph === 'entry') {
      /* Start is one tap away and it opens the case board — the whole ring in
         bytes now, decoded by enterCase when the board actually opens */
      warmCases(mk, false);
      famDefaults(null, false);
      return;
    }
    if (ph === 't1') {
      warmCases(mk, false);
      pickableModels().forEach(function (m) {
        if (m.k !== mk) bodiesFor(m.k).forEach(function (b) { warmFetch(stageLay(bodyLayer(m.k, b))); });
      });
      famDefaults(null, true);
      return;
    }
    if (ph === 't2') {
      /* The angle card is the one thing at this step that was never in the warm
         system at all: 460 angle and gallery files, all of them cold, so every
         stand hop held the previous SKU's picture for up to the 1200ms decode
         timeout. This is the SKU under the phone, so it gets decoded. */
      var f = find(FAMS, state.fam);
      if (f) shotURLs(f.k, state.standColor, verOf(f)).forEach(function (u) { warmSrc(u); });
      standColors(state.fam).forEach(function (k) { warmFetch(stageLay(standLayer(state.fam, k))); });
      /* The version chips sit right under the device — every one of them is a
         single tap, and taking one used to rebuild the row against art nothing
         had asked for yet. Each other version gets two bitmaps: what it would
         put in the ROW (the deck's colour for this product) and what it would
         put on the PHONE (the colour the user is on). Its remaining colours and
         its shots are bytes. */
      if (f && f.vers) {
        var here = verOf(f), rowC = null;
        for (var di = 0; di < DECK; di++) if (deck[di] && deck[di].fam === f.k) { rowC = deck[di].color; break; }
        verLive(f).forEach(function (vx) {
          if (vx === here) return;
          warm(stageLay(standLayerAt(f.k, state.standColor, vx)));
          if (rowC && rowC !== state.standColor) warm(stageLay(standLayerAt(f.k, rowC, vx)));
          standColors(f.k).forEach(function (c) { warmFetch(stageLay(standLayerAt(f.k, c, vx))); });
          shotURLs(f.k, state.standColor, vx).forEach(fetchSrc);
        });
      }
      famDefaults(state.fam, true);
      return;
    }
    /* t3: the next tap is Done, and Done draws the poster */
    if (ph === 't3') warmPosterLayers();
  }

  /* `defer` returns the box WITHOUT its <img>, carrying the layer name on
     data-lay so railFill can mount the picture later. The box is already at its
     final px size — under COREG every layer is the same square and the size is
     known before a single byte arrives — so a deferred slot and a filled one
     occupy exactly the same space and nothing reflows when the picture lands.
     That is the whole reason the rail can be virtualised at all. */
  function visualFor(kind, key, ver, colorKey, pose, w, side, defer) {
    var f = find(FAMS, key);
    var lay = standLayer(key, colorKey);
    var sq = COREG ? (side || Math.round(sqPx() * RAIL_FIT)) : 0;
    /* Small boxes keep their deliberately soft 270 copy (the 192px angle card,
       the phone build's picker row); everything larger asks for the copy this
       screen can actually show — at DPR 1 a 572px box takes 720, not 1080, and
       that is 1.98MB of bitmap instead of 4.45. */
    if (lay && sq) lay = atSize(lay, sq <= THUMB_PX ? THUMB_PX : fitSize(sq));
    var bw = COREG ? sq : w;
    var bh = COREG ? sq : Math.round(w / (lay ? layerAR(lay) : (f ? f.ar : 1)));
    /* deviation #27 tightened: the delivered layer, or an empty box */
    var inner = (lay && !defer) ? imgTag('mfs-lay-fill', lay) : '';
    return '<span class="mfs-stand-bmp' + (COREG ? ' mfs-coreg' : '') + '"'
         + (lay ? ' data-lay="' + lay + '"' : '')
         + ' style="width:' + bw + 'px;height:' + bh + 'px">' + inner + '</span>';
  }

  function renderStand() {
    var f = find(FAMS, state.fam);
    /* the one on the phone's back wears the default white on the stand-pick
       board too, so it matches the row (2026-09-04) */
    var sk = modelPicking() ? defaultIn(standColors(f.k)) : state.standColor;
    var s = find(MOVAS, sk) || find(MOVAS, state.standColor);
    var rig = $('rig'), stand = $('standEl');
    /* scale to the device, not a fixed px, so proportion holds across models */
    var sw = Math.round(find(MODELS, curModel()).w * Z() * STAND_WR);
    var sh = Math.round(sw / f.ar);

    /* ---- layer composite ---- */
    var lay = stageLay(standLayer(f.k, s.k));
    if (lay) {
      /* the delivered stands share a canvas height and differ in width, so pin
         the height against the case box and let the width follow the layer —
         that is what keeps the four products' real proportions apart */
      var ch = $('phoneEl').clientHeight || sh;
      var lh = Math.round(ch * LBOX.stand.h / 100), lw = Math.round(lh * layerAR(lay));
      /* clear the procedural spans once when coming from the vector build,
         then swap in place — never rebuild the <img> */
      if (!stand.querySelector('img.mfs-lay-fill')) stand.innerHTML = '';
      stand.className = 'mfs-stand mfs-layered';
      swapLayer(stand, 'mfs-lay-fill', lay, null);
      stand.style.width = lw + 'px'; stand.style.height = lh + 'px';
      rig.classList.add('mfs-on');
      rig.style.marginTop = '0px';
      rig.style.top = LBOX.stand.y + '%';
      return;
    }
    rig.style.top = '';
    stand.className = 'mfs-stand mfs-layered';
    stand.innerHTML = '';
    var sqf = COREG ? sqPx() : sw;
    stand.style.width = sqf + 'px'; stand.style.height = (COREG ? sqf : sh) + 'px';
    rig.classList.add('mfs-on');
    rig.style.marginTop = '0px';   /* anchored by its top edge, not its centre */
  }

  /* ================= the stand rail =================
     One stand on a white field says "we sell a stand". A row of them running off
     both edges says "we sell a line" — and it gives the roll something to move:
     the row scrolls, and whatever lands under the phone snaps on. */
  var railEl = $('rail'), trackEl = $('railTrack');
  var deck = [], DECK = 0, railIdx = 0, slotW = 0, slotStep = 0, spinning = false, spinT = 0;
  /* 'looks' — the whole line shuffled, one roll = one new outfit. Endless: the
                deck is laid three times over and silently rewound.
     'fams'  — the four product lines in the colour you're on, so sliding the row
                sideways is how you change product. Finite on purpose: four
                products, four positions, no phantom duplicates on a wide screen. */
  var railMode = 'looks';
  /* 'looks' is the attract queue: it only ever scrolls past, never gets stepped
     through, so the seam never shows and laying the deck down three times keeps
     stands on both sides of a wide stage.
     'fams' is the four-product picker the user actually walks. Looping there put
     the first product straight after the last, which reads as more products than
     exist — the same false expectation the colour ring gave (user, 2026-09-01).
     Finite, with ends the chevrons grey out on. */
  function cyclic() { return railMode !== 'fams'; }

  /* round-robin across the families, not colour-by-colour: eight Snap Stands in
     a row reads "eight colours", one of each reads "four products × colours" */
  function makeDeck() {
    railMode = 'looks';
    var queues = FAMS.map(function (f) {
      /* Only colourways that can actually be DRAWN go into the attract row.
         standColors() is the on-sale pool, and a pool entry is not a promise of
         delivered art: 6 of the 51 on-sale colourways have no layer today
         (SSRD / ETBG never shipped; tripod's TPGY / NVBU are sold on the Wallet
         version only, while attract runs at version 0). Those used to be dealt
         into the row anyway, and visualFor's deviation-#27 fallback drew them as
         an empty box — 12% of the seats. Nobody notices an empty seat out at the
         edge, but every ~8th tick it lands at CENTRE, which is the seat the phone
         is wearing, and the phone comes up bare (user, 2026-09-04, four screens
         in a row: "这个看下真的在吗？没显示出来").
         The #27 fallback stays for the case it was written for — the user
         steering INTO a colour that has no art. Dealing an undrawable seat into
         an idle showreel is a different thing, and this is that filter. */
      var cs = standColors(f.k).filter(function (c) { return !!standLayer(f.k, c); });
      if (!cs.length) cs = standColors(f.k).slice();   /* a family with no art at all still gets its row */
      cs = cs.slice();
      for (var i = cs.length - 1; i > 0; i--) {
        var j = Math.floor(Math.random() * (i + 1)), t = cs[i]; cs[i] = cs[j]; cs[j] = t;
      }
      return cs.map(function (c) { return { fam: f.k, color: c }; });
    });
    var out = [], any = true;
    while (any && out.length < DECK_MAX) {
      any = false;
      for (var i = 0; i < queues.length && out.length < DECK_MAX; i++) {
        if (!queues[i].length) continue;
        out.push(queues[i].shift()); any = true;
      }
    }
    deck = out; DECK = out.length;
  }

  /* each line in the colour you're wearing — the row becomes a product picker
     without changing what it is */
  function famDeck() {
    railMode = 'fams';
    var picking = modelPicking();
    deck = FAMS.map(function (f) {
      /* Same filter as makeDeck, same reason: a colourway with no delivered layer
         draws as an empty box, and this row is four products lined up for
         comparison — an empty seat here reads as "this product does not exist".
         The suggestion rules run against the drawable subset, so they still
         answer "closest to the case" / "closest to what you picked", just among
         colours that can be shown. */
      var all = standColors(f.k);
      var pool = all.filter(function (c) { return !!standLayer(f.k, c); });
      if (!pool.length) pool = all;
      /* the whole row wears the default white on the pick board (2026-09-04): a
         row of differently coloured stands compares colours, not shapes. The
         colour board goes back to "follow the case" / "what the user picked". */
      return { fam: f.k,
               color: picking ? defaultIn(pool)
                    : state.standManual ? nearestIn(state.standColor, pool)
                                        : pickLook(state.color, pool, 'tonal') };
    });
    DECK = deck.length;
  }
  function famRailSync() {
    famDeck();                   /* sets railMode = 'fams', so cyclic() is false below */
    railIdx = 0;
    for (var i = 0; i < DECK; i++) if (deck[i].fam === state.fam) railIdx = i;
    if (cyclic()) railIdx += DECK;   /* park in the middle copy of the tripled track */
    layoutRail();
  }
  /* colour changed at the foot: repaint the slots in place rather than rebuild —
     they keep their size, so nothing has to move */

  /* the deck is laid down three times over, so the reel always has stands on
     both sides and can be silently rewound to the middle copy after a spin */
  function layoutRail() {
    if (!DECK) return;
    /* COREG: rail geometry pins to the fixed stage silhouette (DEF.m). The
       shared 1080 canvas keeps the MagSafe anchor put across models, so a rail
       that re-derives its line from the picked model just wobbles on a switch */
    var z = Z(), pw = Math.round(stageRef().w * z);
    slotW = Math.round(pw * STAND_WR);
    /* pitch is measured edge-to-edge, not as a multiple of the stand: the gap
       either side of the phone is what makes it read as a row rather than a pile */
    slotStep = Math.round(pw / 2 + slotW / 2 + pw * (z > 1.3 ? 0.22 : 0.15));
    if (COREG) slotStep = Math.round(slotStep * RAIL_PITCH);
    /* an unfolded phone is wider than the portrait silhouette pw stands for —
       push the neighbours out by the difference in half-widths */
    if (COREG && landscapeModel()) slotStep += Math.round(sqPx() * LAND_HALF - pw / 2);
    /* One rail, one geometry, both builds: full-size stands riding across the
       device, the slot parked at centre being the one on its back. The phone
       build had a thumb strip in the deck band for a while — it read as a
       picker, but it cost the band and it made the stand step the one place the
       device was not wearing what you were choosing. Swiping the device itself
       is the gesture the opening screen already teaches (user, 2026-09-02: "让支
       架类型像加载时直接在手机上面横滑切换吧"), and it hands the whole band back
       to the version chips, the angle shot and Details. */
    var slotH = slotW;              /* COREG: one square slot for every family */
    /* Figma 1:169 drops the titled card: the row is bare stands in both modes now.
       'fams' still means a finite four-product deck — only the chrome is gone, so
       the geometry that centres the phone between stands is shared. */
    var gap = slotStep - slotW, html = '', reps = cyclic() ? 3 : 1;
    /* Click target, walkthrough 0908 item 10. The slot box is slotW square, but
       the stand the user sees is the layer image: a larger square, centred on the
       slot, absolutely positioned and pointer-events:none — so it neither grows
       the slot nor takes clicks itself. Only ~8% of the visible stand was
       clickable, in a band across its middle. These two lengths let the CSS grow
       the hit area to the whole stand: hx spans the gap to the next slot (one
       slotStep in total, so neighbours abut instead of overlapping and every x
       belongs to the nearest stand), hy covers the image's overhang above and
       below. Both are derived, not fixed, so they track Z(). */
    var imgSide = COREG ? Math.round(sqPx() * RAIL_FIT) : 0;
    var hitY = COREG ? Math.max(0, Math.round((imgSide - slotH) / 2)) : 0;
    for (var rep = 0; rep < reps; rep++) {
      for (var i = 0; i < DECK; i++) {
        var f = find(FAMS, deck[i].fam);
        /* deferred: the box only — railFill hangs the picture on the seats in reach */
        var body = visualFor('stand', deck[i].fam, state.ver[f.k] || 0, deck[i].color, 0, slotW, 0, true);
        /* COREG: one slot height for every family — per-family aspect heights
           left each slot's centreline (and its centred 572 square) sitting at a
           different y, so the row never lined up with the hero exactly */
        html += '<span class="mfs-slot" data-i="' + i + '" data-fam="' + shapeOf(f.k) + '" style="width:' + slotW + 'px;margin-right:' + gap + 'px'
             + (COREG ? ';height:' + slotH + 'px;--mfs-hx:' + gap + 'px;--mfs-hy:' + hitY + 'px' : '') + '">';
        html += body;
        html += '</span>';
      }
    }
    /* Rebuilding the track throws every picture away, and the commonest reason
       to call layoutRail is a change that keeps the seats exactly where they
       are: switching version keeps the same four products at the same size and
       only swaps the art behind them. The wipe made that read as a blank flash
       (user, 2026-09-10: "这里在切型号，会闪空加载"). So when the geometry is
       unchanged, retag the seats in place and let each one cross-fade — the
       outgoing picture holds the seat until the incoming one has pixels,
       exactly the contract swapLayer gives the stage. */
    if (trackEl.children.length === reps * DECK && trackEl.getAttribute('data-w') === String(slotW)) {
      retagRail();
      railFill();            /* a seat that had no art and now has some still needs mounting */
    } else {
      trackEl.innerHTML = html;
      trackEl.setAttribute('data-w', slotW);
      railFill();
    }
    placeRail(false);
    /* the chevrons are desk furniture — the phone build drags and taps the row,
       and there is no room beside a 393px column to park them in anyway */
    /* (the landscape phone build does show them, but parked by the stylesheet
       in the deck band — clear any desk coordinates left from a resize) */
    if (isMob()) {
      ['railPrev', 'railNext'].forEach(function (id) { var s = $(id).style; s.top = s.left = s.right = ''; });
      markEnds(); return;
    }
    /* park the chevrons on the stand band, not the phone's centre line, and just
       outside the row rather than on the far page edges when there's room */
    var navY = railTy() + Math.round(slotW * 0.6);
    var navX = Math.round(Math.min((railEl.clientWidth || 393) / 2 - 20, slotStep * 2 + slotW / 2 + 16));
    var railW = railEl.clientWidth || 1440;
    /* the prev chevron is NOT mirrored: it parks in the gap between the angle
       card and the t2 mask's dissolve band (mask solid up to 35.5% - 110px),
       clear of the product photos (user, 2026-09-01) */
    var prevX = Math.round(Math.max(railW * 0.046 + 192 + 12, railW * 0.355 - 156));
    $('railPrev').style.top = 'calc(50% + ' + navY + 'px)';
    $('railPrev').style.left = prevX + 'px';
    $('railPrev').style.right = '';
    $('railNext').style.top = 'calc(50% + ' + navY + 'px)';
    $('railNext').style.right = 'calc(50% - ' + navX + 'px)';
    $('railNext').style.left = '';
    markEnds();
  }
  /* ================= the rail is virtualised =================
     The track lays down every seat — three copies of the deck, 72 boxes — but
     only the seats within reach carry an <img>. Before this, all 24 distinct
     stands were requested and decoded at boot on every device, while the row
     shows 5 seats on a 1440 desk and 2 on a 393 phone: ~107MB of bitmaps for
     ~20MB of visible picture, and 48 seconds of attract before the last of them
     was ever seen. That was the single biggest reason a phone got its tab
     reaped, and it was paid before the user had done anything at all.

     Nothing about the geometry changes — every box is already at its final px
     size (COREG makes every layer the same square), so mounting and unmounting
     the picture inside it can never move anything. The empty box IS the
     placeholder the design asks for.

     Filling runs AHEAD of travel: spinRail sets railLead in the direction it is
     about to move, so the seats coming in from that edge decode during the
     900ms flight rather than on arrival. A drag does the same from its
     transient position, since railIdx does not move until the finger lets go. */
  var railLead = 0, railPeek = null;
  /* The budget names a floor, the viewport sets the real radius: a 2560 monitor
     shows 9 seats and would have shown two empty ones either side of a fixed
     radius of 4. Half the row's width in slots IS the answer — the seat at that
     distance already overhangs the edge — and `lead` supplies the margin in the
     direction anything is actually moving. */
  function railWinR() {
    var vis = slotStep ? Math.ceil((railEl.clientWidth || 393) / 2 / slotStep) : 0;
    return Math.max(budget().railR, vis);
  }
  function railFill(centre) {
    if (!DECK || !trackEl.children.length) return;
    var c = centre == null ? railIdx : centre, r = railWinR();
    var lo = c - r + Math.min(0, railLead);
    var hi = c + r + Math.max(0, railLead);
    var kids = trackEl.children, i, box, lay;
    for (i = 0; i < kids.length; i++) {
      box = kids[i].firstElementChild;
      if (!box) continue;
      lay = box.getAttribute('data-lay');
      if (!lay) continue;                       /* deviation #27: a seat with no art stays an empty box */
      if (i >= lo && i <= hi) {
        if (box.getAttribute('data-on') === '1') continue;
        box.setAttribute('data-on', '1');
        mountSlotArt(box, lay);
      } else if (box.getAttribute('data-on') === '1') {
        box.setAttribute('data-on', '0');
        box.innerHTML = '';                     /* the box keeps its size; only the picture leaves */
      }
    }
  }
  /* A hot bitmap mounts in the same frame, so a lap rewind (railLand moves
     railIdx by a whole deck every landing) unmounts and remounts the visible
     seats inside one task and never shows an empty frame. A cold one waits for
     its decode and checks it is still wanted — the seat may have left the
     window, or the deck may have been rebuilt underneath it. */
  /* which layer a seat should be showing right now, at the row's size */
  function railLay(d) {
    var lay = standLayer(d.fam, d.color), sq = Math.round(sqPx() * RAIL_FIT);
    return lay ? atSize(lay, sq <= THUMB_PX ? THUMB_PX : fitSize(sq)) : null;
  }
  /* ⚠ The keep path inherits whatever presentation state the last step left on
     the slots, and `innerHTML =` used to launder exactly that. chooseStand's exit
     sweep puts `.mfs-out` on every seat — a `both`-filled animation, so it holds
     them at opacity 0 forever — and liftCentre leaves one seat inline-hidden.
     Coming back into the stand step then showed four blank seats (user,
     2026-09-10: 「所有返回都看不到素材图」). So reset the same things the rebuild
     used to: railEnter re-applies the walk-in right after this. */
  function retagRail() {
    var kids = trackEl.children, i, box, lay, was;
    for (i = 0; i < kids.length; i++) {
      var d = deck[i % DECK], f = find(FAMS, d.fam);
      kids[i].classList.remove('mfs-out', 'mfs-in', 'mfs-inR');
      kids[i].style.animationDelay = '';
      kids[i].style.visibility = '';
      kids[i].setAttribute('data-fam', shapeOf(f.k));
      box = kids[i].firstElementChild;
      if (!box) continue;
      lay = railLay(d);
      was = box.getAttribute('data-lay') || '';
      if (was === (lay || '')) continue;
      if (lay) box.setAttribute('data-lay', lay); else box.removeAttribute('data-lay');
      if (box.getAttribute('data-on') !== '1') { box.innerHTML = ''; continue; }
      if (!lay) { box.innerHTML = ''; box.setAttribute('data-on', '0'); continue; }
      swapSlotArt(box, lay);
    }
  }
  /* the seat's own no-blank-frame swap: the old bitmap keeps the seat until the
     new one is decoded, then the new one fades up over it */
  function swapSlotArt(box, lay) {
    var old = box.firstChild;
    function land(instant) {
      if (!box.isConnected || box.getAttribute('data-lay') !== lay) return;
      var nu = document.createElement('img');
      nu.className = 'mfs-lay mfs-lay-fill';
      nu.alt = '';
      nu.decoding = 'async';
      nu.src = layerURL(lay);
      if (instant || reduced || !old) { box.innerHTML = ''; box.appendChild(nu); return; }
      nu.classList.add('mfs-xin');
      box.appendChild(nu);
      void nu.offsetWidth;
      nu.classList.remove('mfs-xin');
      setTimeout(function () { if (old && old.parentNode === box) old.remove(); }, 240);
    }
    if (hotImg(lay)) { land(!old); return; }
    warm(lay).then(function (im) { if (im) land(false); });
  }
  function mountSlotArt(box, lay) {
    var hot = hotImg(lay);
    if (hot) { box.innerHTML = imgTag('mfs-lay-fill', lay); return; }
    warm(lay).then(function (im) {
      if (!im || !box.isConnected || box.firstChild) return;
      if (box.getAttribute('data-on') !== '1' || box.getAttribute('data-lay') !== lay) return;
      box.innerHTML = imgTag('mfs-lay-fill', lay);
      /* only a seat the lead did not reach in time gets here; fade it the same
         way swapLayer does rather than letting it appear on one frame */
      var el = box.firstChild;
      if (reduced) return;
      el.classList.add('mfs-xin');
      void el.offsetWidth;
      el.classList.remove('mfs-xin');
    });
  }
  /* a finite row has to admit where it ends */
  function markEnds() {
    $('railPrev').classList.toggle('mfs-off', !cyclic() && railIdx <= 0);
    $('railNext').classList.toggle('mfs-off', !cyclic() && railIdx >= DECK - 1);
  }
  function railTx() { return Math.round((railEl.clientWidth || 393) / 2 - (railIdx * slotStep + slotW / 2)); }
  /* the loose stands sit on the same line as the one on the back: the rig's top
     edge is 33.6% down the device, i.e. 16.4% above its centre */
  function railTy() {
    /* the fams row used to be cards, which hung lower; now it is bare stands, so it
       shares the attract row's line — level with the stand on the phone's back */
    /* COREG: same pin as layoutRail — the worn stand's line is set by the shared
       canvas anchor, not by the picked model's design height. With the uniform
       COREG slot height the centred squares land exactly on the hero's square —
       measured zero offset, no fudge constant needed */
    return Math.round(-0.164 * stageRef().h * Z());
  }
  /* the row assembles itself, radiating out from the phone */
  function railEnter() {
    if (reduced) return;
    var slots = trackEl.children;
    for (var i = 0; i < slots.length; i++) {
      var d = Math.abs(i - railIdx);
      slots[i].classList.remove('mfs-in', 'mfs-inR');
      if (d > 7) continue;
      /* the cards walk in from the right, one after the other */
      var cls = railMode === 'fams' ? 'mfs-inR' : 'mfs-in';
      slots[i].style.animationDelay = (railMode === 'fams' ? 0.04 + i * 0.09 : 0.05 + d * 0.07) + 's';
      void slots[i].offsetWidth;
      slots[i].classList.add(cls);
    }
  }
  function placeRail(anim, dur) {
    trackEl.style.transition = (anim ? 'transform ' + (dur || 820) + 'ms cubic-bezier(.16,.86,.16,1), ' : '') + 'opacity .45s ease';
    trackEl.style.transform = 'translate(' + railTx() + 'px,' + railTy() + 'px)';
    seatShift(anim, dur);
  }
  /* ---- landscape seats: the worn one off-centre, the loose ones centred ----
     iPhone Duo's stand art is drawn where it mounts — on the left half's MagSafe
     ring, its content centred at x 306 of the 1080 canvas instead of 540. The
     seat parked at centre IS the stand on the phone's back, so it keeps that
     offset; every other seat moves right by the difference so the loose stands
     sit symmetrically about the phone instead of the right one landing on its
     screen (user, 2026-10-22). The parked seat eases into place with the
     row's own curve and duration, which reads as the stand snapping on. Uses
     the `translate` property so the seat's entrance keyframes (transform) and
     its hit area (::after, carried along) are untouched. */
  var LAND_STAND_X = (540 - 306) / 1080;
  function seatShift(anim, dur) {
    if (!COREG || !landscapeModel()) return;
    var kids = trackEl.children, dx = Math.round(sqPx() * LAND_STAND_X) + 'px 0';
    for (var i = 0; i < kids.length; i++) {
      kids[i].style.transition = anim ? 'translate ' + (dur || 820) + 'ms cubic-bezier(.16,.86,.16,1)' : 'none';
      kids[i].style.translate = i === railIdx ? '0 0' : dx;
    }
  }
  function readDeck() {
    if (!DECK) return;
    var d = deck[((railIdx % DECK) + DECK) % DECK];
    state.fam = d.fam;
    /* Shape syncs, colour does not: the pick board's row wears the default white
       (2026-09-04) and that is display art. Writing it into state would overwrite
       whatever stand colour the user had picked, so the colour board would open on
       white and chooseStand's "keep the manual pick" branch would have nothing
       left to keep. */
    if (!modelPicking()) state.standColor = d.color;
  }
  function spinRail(steps, dur, then) {
    if (!DECK || !steps) { if (then) then(); return; }
    if (!cyclic()) {
      steps = Math.max(0, Math.min(DECK - 1, railIdx + steps)) - railIdx;
      if (!steps) { placeRail(true, 200); if (then) then(); return; }
    }
    spinning = true;
    spinThen = then || null;
    railIdx += steps;
    railLead = steps > 0 ? budget().lead : -budget().lead;
    railFill();                  /* the incoming edge decodes during the flight */
    placeRail(true, dur);
    markEnds();
    clearTimeout(spinT);
    spinT = setTimeout(function () { railLand(false); }, dur + 20);
  }
  /* the landing, split out so a finger can trigger it early. freeze = a drag is
     taking the row over: leave the pixels alone and skip the rewind, which is
     invisible only where the row is parked on an exact slot (it shifts railIdx by
     a whole lap, and mid-flight that is a whole lap of pixels too). The next
     landing rewinds. */
  var spinThen = null;
  function railLand(freeze) {
    clearTimeout(spinT);
    if (!spinning) return;
    if (cyclic() && !freeze) railIdx = DECK + (((railIdx % DECK) + DECK) % DECK);
    railLead = 0; railPeek = null;
    railFill();                  /* same task as the rewind, so no empty frame */
    if (!freeze) placeRail(false);
    readDeck();
    renderStand();
    spinning = false;
    railSettled();
    var th = spinThen; spinThen = null;
    if (th) th();
  }
  /* what a landing means depends on what the row is standing for */
  function railSettled() {
    /* the row landed on another product, so any open version menu is stale */
    if (typeof verMenu === 'function') verMenu(false);
    if (railMode === 'fams') { buildStandFan(); famDeck();   /* layoutRail carries the row's colours; this only rebuilds the deck data */ renderStandStep(); micro(''); return; }
    micro('');   /* attract mode: the product does the talking */
  }

  /* ---- stand step furniture: name, model name, model demo, model buttons ----
     All four read from the same place (state.fam + state.ver[fam]), so one call
     keeps the top-left, the left card and the button row in step. */
  var angTimer = 0;   /* angle-shot carousel clock (deviation #23) */
  function renderStandStep() {
    var f = find(FAMS, state.fam);
    /* clamp onto a live version first: a product whose version 0 is on the hidden
       list would otherwise open on a version with no colours behind it */
    var vi = verOf(f);
    if (f.vers) state.ver[f.k] = vi;
    /* the board head names whatever the row is parked on, so every landing and
       every version tap has to refresh it (see boardCopy's t2 branch). It used to
       also fill a #mfs-standHead block and bail out if that block was missing —
       the block has been display:none in every phase since the title moved here,
       and the bail-out would have taken the angle card and the version chips with
       it. Both are gone. */
    setBoardHead();


    /* The angle card: every version ships TWO angle shots (2 per product colour —
       146 planned in delivery v1), read separately by filename. The naming is
       mf-angle-{SKU}-v{version}-{1|2} (data contract) — a miss falls
       back to the procedural at-rest render, so the card works before the batch
       lands and upgrades itself file by file. Click the card to flip angles. */
    var shot = $('standShot');
    /* the index is the source of truth for what exists — no probing, no 404s */
    /* ---- which pair the PiP plays (走查 0905 A3) ----
       "选支架页面左上角缩略图，每一个型号两行对应图片，需轮播，请替换".
       The carousel itself has always been here (deviation #23, see mountAngles);
       what it lacked was a SECOND frame. mf-angle is the older product-library
       crop set and many colourways only ever got one of it, while the pair the
       design actually delivers per colourway is the gallery a1/a2 —
       MC002-1-ME-MCGYSPBU: 1 angle vs 2 gallery, MS007MP-1-MO-SROG: 0 vs 2.
       So play whichever set is fuller. Angle stays the source where it is the
       only one, and where both have two nothing changes — the churn is limited
       to the cards that could not carousel before. */
    var shotCol = shotColor(f.k);
    var angHave = angleShots(f.k, shotCol, vi);
    var galHave = galleryShots(f.k, shotCol, vi);
    var have = withDefault(f, vi, galHave.length > angHave.length ? galHave : angHave);
    clearInterval(angTimer);
    if (have.length) {
      /* decode off-DOM first: a stand hop used to blank the card for the time
         the cold frames took to arrive — the old card holds until the new
         frames have pixels, same philosophy as swapLayer */
      var angWant = shot.__mfsAng = have.join('|');
      /* through the shared cache, not a private one: these URLs are prefetched
         by lookahead() a step earlier, so a hop that used to hold the previous
         SKU's picture for the full 1200ms decode timeout now lands hot.
         warmSrc keeps the same never-rejects, times-out-in-background contract
         this loop was written around. */
      Promise.all(have.map(function (u) { return warmSrc(u); })).then(function () {
        if (shot.__mfsAng !== angWant || !shot.isConnected) return;
        mountAngles();
      });
    } else {
      shot.__mfsAng = '';
      shot.innerHTML = '<span class="mfs-ang-fallback">' + visualFor('stand', f.k, vi, state.standColor, 0, 192, 192) + '</span>';
      /* deviation #28 leaves a missing layer empty, and on desk that is a 192px
         card the eye reads as "shot pending". At 78px in the corner of the stage
         it just reads as a bug, so the PiP steps out instead. */
      shot.onclick = isMob() ? openProdSheet : null;
      shot.onmouseenter = null; shot.onmouseleave = null;
      shot.onpointerdown = null; shot.onpointermove = null; shot.onpointerup = null; shot.onpointercancel = null;
    }

    function mountAngles() {
      /* a SKU with several shots plays as a sideways carousel (deviation #23):
         frames sit side by side on a sliding strip — auto-advance every 3s,
         paused on hover, a click steps manually and re-arms the clock */
      shot.innerHTML = '<span class="mfs-angle-flip">' + have.map(function (x) {
        return '<img class="mfs-ang" src="' + x + '" alt="">';
      }).join('') + '</span>';
      var flip = shot.firstChild, angAt = 0;
      var angGo = function (i) {
        angAt = ((i % have.length) + have.length) % have.length;
        flip.style.transform = 'translateX(' + (-angAt * 100) + '%)';
      };
      var angArm = function () {
        clearInterval(angTimer);
        if (have.length < 2 || reduced) return;
        angTimer = setInterval(function () {
          if (!shot.isConnected) { clearInterval(angTimer); return; }
          if (root.getAttribute('data-ph') !== 't2') return;
          angGo(angAt + 1);
        }, 3000);
      };
      var drag = null, swallow = false;
      /* phone build: the card is a PiP in the corner of the stage, too small to
         step through by hand — it keeps carousel-ing itself, and a tap opens the
         product sheet, where the same shots are full width and swipeable. */
      if (isMob()) {
        shot.onclick = openProdSheet;
        shot.onmouseenter = shot.onmouseleave = null;
        shot.onpointerdown = shot.onpointermove = shot.onpointerup = shot.onpointercancel = null;
        angArm();
        return;
      }
      shot.onclick = have.length > 1 ? function () {
        if (swallow) { swallow = false; return; }
        angGo(angAt + 1); angArm();
      } : null;
      shot.onmouseenter = have.length > 1 ? function () { clearInterval(angTimer); } : null;
      shot.onmouseleave = have.length > 1 ? angArm : null;
      /* drag to swipe: the strip follows the pointer, release steps by direction */
      shot.onpointerdown = have.length > 1 ? function (e) {
        drag = { x: e.clientX, at: angAt, moved: false };
        clearInterval(angTimer);
        flip.style.transition = 'none';
        if (shot.setPointerCapture) shot.setPointerCapture(e.pointerId);
      } : null;
      shot.onpointermove = function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x;
        if (Math.abs(dx) > 4) drag.moved = true;
        var w = shot.clientWidth || 192;
        flip.style.transform = 'translateX(' + (-drag.at * 100 + dx / w * 100) + '%)';
      };
      shot.onpointerup = shot.onpointercancel = function (e) {
        if (!drag) return;
        var dx = e.clientX - drag.x, moved = drag.moved, at = drag.at;
        drag = null;
        flip.style.transition = '';
        if (moved) {
          swallow = true;
          angGo(at + (dx <= -30 ? 1 : dx >= 30 ? -1 : 0));
        }
        angArm();
      };
      angArm();
    }

    var demo = $('standDemo');
    if (demo) demo.classList.toggle('mfs-empty', isMob() && !have.length);

    /* position bars: one per model, the live one inked (Figma 1:201) */
    var live = verLive(f), bars = '';
    if (!f.vers) live = [0];
    for (var b = 0; b < live.length; b++) bars += '<i' + (live[b] === vi ? ' class="mfs-on"' : '') + ' data-vi="' + live[b] + '"></i>';
    $('standBars').innerHTML = bars;

    /* model buttons — hidden-out versions get no chip (verLive) */
    $('standVers').innerHTML = !f.vers ? '' : verLive(f).map(function (i) {
      return '<button type="button" role="tab" aria-selected="' + (i === vi) + '"'
           + (i === vi ? ' class="mfs-on"' : '') + ' data-vi="' + i + '">' + f.vers[i] + '</button>';
    }).join('');
    /* nothing to choose = an empty band on the phone build, so the stylesheet
       fills it with this product's angle shot instead. One thing on the centre
       line either way; CSS cannot ask a later sibling how many chips it has. */
    root.classList.toggle('mfs-onever', !(f.vers && verLive(f).length > 1));
    /* a landscape device (iPhone Duo) fills the stage's width, so the phone
       build's angle card cannot sit in the corner beside it — CSS moves it under
       the device (NO.250) */
    root.classList.toggle('mfs-land', COREG && landscapeModel());
  }
  function pickVer(vi) {
    var f = find(FAMS, state.fam);
    if (!f.vers || verLive(f).indexOf(vi) < 0) return;
    state.ver[f.k] = vi;
    buildStandFan();      /* the colour pool is per version — rebuild + clamp */
    renderStand();        /* the stand on the phone takes the model too */
    renderStandStep();
    famDeck();   /* layoutRail carries the row's colours; this only rebuilds the deck data */
    /* …and this is what puts the rebuilt deck on screen. famDeck() only touches
       the data, so without it the row kept the PREVIOUS version's art: switch
       Field to "Wallet without a Stand" and the thumbnail stayed the with-a-stand
       product (user, 2026-09-05 走查 D10「选支架页残留数据」). Same pairing as
       famRailSync(). It was invisible while those versions had no layer at all —
       now that the art is delivered, the stale slot is the whole symptom. */
    layoutRail();
    syncInfoShots();      /* each version is its own SKU, so its own pair of shots */
    idleWarm(lookahead);  /* the chip they did NOT take is still one tap away */
  }
  /* The phone build shows one row of the list at a time. Four versions of a
     wallet are long product strings ("Trackable Wallet without a Stand"), and a
     strip that scrolls sideways hides both ends of itself: you cannot see what
     the options are, only that there are more (user, 2026-09-03: "考虑下选规格是
     不是可以直接用点击展示像 select 一样来做？现在列出来在手机上不友好").
     So: closed it is the version you are on, with a caret; a tap opens the whole
     list as a panel over the device; a tap on a row picks it and closes. Same
     buttons, same click path, one class. */
  function verMenu(open) {
    var r = $('standVers');
    if (!r) return;
    r.classList.toggle('mfs-open', !!open);
    var b = r.querySelector('button.mfs-on');
    if (b) b.setAttribute('aria-expanded', open ? 'true' : 'false');
  }
  on(document, 'pointerdown', function (e) {
    var r = $('standVers');
    if (!r || !r.classList.contains('mfs-open')) return;
    if (!r.contains(e.target)) verMenu(false);
  }, true);
  /* both the buttons and the bars under the demo card select a model */
  $('standVers').addEventListener('click', function (e) {
    var b = e.target.closest && e.target.closest('button[data-vi]');
    /* The row is a row again on the phone (design 1147:3506): every version is a
       chip you can see and tap, scrolling sideways. It spent one revision folded
       into a select — the band it shared with the angle card and the ⓘ pill had
       no room for four product strings — and the new design frees the band by
       moving the card onto the stage, so the fold is no longer buying anything.
       (The open-panel branch stays reachable for nothing now; verMenu is kept so
       a future fold needs no new plumbing.) */
    if (b) verMenu(false);
    if (b) pickVer(+b.getAttribute('data-vi'));
  });
  $('standBars').addEventListener('click', function (e) {
    var i = e.target.closest && e.target.closest('i[data-vi]');
    if (i) pickVer(+i.getAttribute('data-vi'));
  });
  function famLine() {
    var f = find(FAMS, state.fam);
    /* The version's line, not the product's. This block already names the
       version right above the sentence, so a product-level tagline contradicted
       it — Note Flip read "Grip, stand and fidget — one magnetic ring", which is
       the Note Stand. Same fix as the info box that hangs off it; the two are
       never readable at once (the box opens over this block), so they can carry
       the same string without repeating themselves. */
    return '<b>' + f.n + '</b>'
         + (f.vers ? '<span class="mfs-fam-ver">' + verName(f) + '</span>' : '')
         + verWhat(f) + infoTag(f);
  }
  /* hover target: the long description plus every posture the stand folds into */
  /* Information box — Figma 571:1365 (Desktop / 06b 支架信息 · Info box). A 214px
     panel: product name over the version in grey, a ✕ on that line, one strip of
     156px squares, then the blurb. The "Ways to use it" heading the old board had
     is gone — the phone sheet dropped it when it was rebuilt to 1147:3747 and the
     desktop box was left behind (user, 2026-09-10: "移动端的同一个打开弹窗的图是
     对的，是不是 PC 漏了改"). */
  /* The tile strip: real product photography when this colourway has it, the CSS
     posture figures only when it has none. Same strip, same 156px squares, same
     horizontal scroll.

     Two batches count as photography, in the phone sheet's order: the gallery
     pair delivered per colourway, and the angle shots as the fallback. Desk used
     to take gallery only and drop straight to drawings — the same product then
     showed photos on the phone and figures on the desktop, which openProdSheet's
     own comment already called out as wrong.

     The photos take neither the numbered badge nor the posture caption: a2 is a
     material close-up, and "FLAT" printed under it would be a wrong label. What
     each posture is called still reads in the blurb below the strip.

     Nothing delivered = an empty strip, and the box is title + blurb (user,
     2026-09-10: "那矢量这些全去掉吧，缺图就空着就好"). The CSS posture figures
     that used to stand in are gone from both builds: they read as a rendering
     failure next to photography, and they were the only thing the `modes` field
     drew — so that field is gone too, from the record here and from the data
     island (2026-09-10 清场). */
  function ipModesHTML(f) {
    var vi = (state.ver || {})[f.k] || 0;
    var shots = sheetShots(f, vi);
    if (!shots.length) return '';
    var name = f.n + (f.vers && verName(f) !== f.n ? ' ' + verName(f) : '');
    /* a product title with a quote in it would end the attribute early */
    var alt = name.replace(/"/g, '&quot;');
    return shots.map(function (u) {
      return '<span class="mfs-ip-mode">'
           + '<span class="mfs-ip-fig">'
             + '<img src="' + u + '" alt="' + alt + '" draggable="false">'
           + '</span></span>';
    }).join('');
  }
  /* a colourway (or version) away from the one the box was built for: the strip
     is per-variant, so changing colour changes the pictures in it */
  function syncInfoShots() {
    var f = find(FAMS, state.fam);
    if (!f) return;
    var el = root.querySelector('.mfs-ip-modes');
    if (!el) return;
    el.innerHTML = ipModesHTML(f);
    el.scrollLeft = 0;
  }
  function infoTag(f) {
    /* the box is worth opening when there is copy to read in it — it used to
       hang off the `modes` field, which no longer exists */
    var ipL = verWhat(f) || f.long || '';
    if (!ipL) return '';
    return '<span class="mfs-infowrap">'
      + '<button class="mfs-infobtn" type="button" aria-label="About ' + f.n + '">+</button>'
      + '<span class="mfs-infopop" role="tooltip">'
        + '<span class="mfs-ip-hd">'
          /* Two lines, not one (571:1365): the product in ink over the version in
             grey. Joined with an em dash they wrapped to three lines in a 174px
             column and read as one long string. Still only naming the version
             when it says something the family name doesn't — "Tripod Stand" under
             "Tripod Stand" is not a subtitle. */
          + '<span class="mfs-ip-ttl">'
            + '<span class="mfs-ip-n">' + f.n + '</span>'
            + (f.vers && verName(f) !== f.n ? '<span class="mfs-ip-v">' + verName(f) + '</span>' : '')
          + '</span>'
          + '<button class="mfs-ip-x" type="button" aria-label="Close">'
            + '<svg width="13" height="13" viewBox="0 0 13 13" aria-hidden="true"><path d="M1.5 1.5 L11.5 11.5 M11.5 1.5 L1.5 11.5" stroke="currentColor" stroke-width="1.1" fill="none"/></svg>'
          + '</button>'
        + '</span>'
        + '<span class="mfs-ip-modes">' + ipModesHTML(f) + '</span>'
        /* The version's own line, not the product's long description. The box
           names a VERSION in its header, so describing the product underneath
           contradicted it — Note Flip read "A magnetic ring you can hook a
           finger through…", which is the Note Stand (user, 2026-09-10). The
           long description stays as the fallback for a version with no line. */
        + '<span class="mfs-ip-l">' + ipL + '</span>'
      + '</span></span>';
  }
  /* a neighbour is a target: click it and it slides in */
  trackEl.addEventListener('click', function (e) {
    var ph = root.getAttribute('data-ph');
    if (ph !== 't2' || spinning || dragMoved) return;
    var ch = e.target.closest ? e.target.closest('[data-choose]') : null;
    if (ch) { chooseStand(railIdx); return; }
    var slot = e.target.closest ? e.target.closest('.mfs-slot') : null;
    if (!slot) return;
    var i = Array.prototype.indexOf.call(trackEl.children, slot);
    if (i < 0 || i === railIdx) return;
    /* shortest way round: a stand sitting to the left slides in from the left.
       Forcing every pick forward landed on the right stand but read as "next"
       whichever side was clicked (user, 2026-09-01). A finite row has no way
       round at all — the gap between the two slots IS the distance. */
    var steps = i - railIdx;
    if (root.getAttribute('data-ph') === 't2' && cyclic()) {
      steps = ((steps % DECK) + DECK) % DECK;
      if (steps > DECK / 2) steps -= DECK;
    }
    spinRail(steps, 420);
  });
  function railStep(dir) {
    var ph = root.getAttribute('data-ph');
    if (ph !== 't2' || spinning) return;
    /* each chevron travels the way it points. It used to walk DECK-1 slots
       forward to reach the previous stand, which landed right but played as a
       "next" slide (user, 2026-09-01). One slot back is a real left slide; the
       incoming stand emerges out of the left mask's dissolve band, the outgoing
       one has the open right half of the stage to travel into. At the ends
       spinRail clamps and markEnds has already greyed the chevron out. */
    spinRail(dir < 0 ? -1 : 1, 420);
  }
  $('railPrev').addEventListener('click', function () { railStep(-1); });
  $('railNext').addEventListener('click', function () { railStep(1); });

  /* ================= one horizontal-drag recogniser, three surfaces =================
     The dial, the case pair and the stand row each gated the axis the same way:
     at the first 6-8px of travel, take the gesture if |dx| > |dy|, otherwise drop
     it for good. Two things are wrong with that, and both were measured by
     dispatching paths into the live build (2026-09-02):

       - A thumb does not sweep in a straight line. It leaves on a downward arc,
         so the opening samples of a perfectly ordinary side swipe are
         dy-dominant: |dx| <= |dy| is how a horizontal swipe BEGINS, not evidence
         of a scroll. A path of (-2,4) (-4,7) (-7,9) and then 130px straight left
         was ignored outright, while the same 130px as a straight line tracked.
         The dial was answering roughly half the swipes aimed at it.
       - One verdict at 8px is a verdict on noise, and nothing recovered from it
         because the recogniser had already set itself to null.

     So: no 45-degree line and no early verdict. Horizontal takes the gesture when
     it leads the vertical by LOCK px, vertical hands it to the page when it leads
     by GIVE px, and until one of those happens the gesture is still open. The
     asymmetry is deliberate - these are horizontal controls, and the page can
     always be scrolled from the head, the deck or the button rows. Whoever wins
     keeps it; no re-deciding mid-drag.

     Two more things every native carousel has and these did not: a release reads
     velocity as well as distance, so a short hard flick counts (a 25px throw used
     to be exactly nothing); and the surface tracks the finger for as long as the
     drag is live, because feedback is what makes a gesture feel like it is
     holding an object rather than being graded on submission.

     touch-action: pan-y on the surfaces leaves the browser one axis to claim, and
     the scrollTop check catches the case where it claimed it anyway: iOS commits
     to a pan early and ignores preventDefault afterwards, and a control that
     keeps tracking through a scroll it cannot stop is the worst of both. */
  var SWIPE_LOCK = 7;     /* px of horizontal lead that takes the gesture */
  var SWIPE_GIVE = 13;    /* px of vertical lead that hands it to the page */
  var FLICK_V = 0.32;     /* px/ms at release that counts as a throw */
  var FLICK_GAP = 90;     /* ms: a finger that rested before lifting is not throwing */

  /* the scroll container is <html>, so this is where a page scroll shows up */
  function pageTop() { return document.documentElement.scrollTop || 0; }

  function hSwipe(targets, o) {
    var g = null;
    function P(e) { return e.touches ? (e.touches[0] || e.changedTouches[0]) : e; }
    function T(e) { return e.timeStamp || Date.now(); }
    function down(e) {
      g = null;
      if (!o.can()) return;
      /* native scrollers and sheets own their own gestures */
      if (e.target && e.target.closest
          && e.target.closest('.mfs-scroller, .mfs-sp-vers, .mfs-ip-modes, .mfs-modal')) return;
      var p = P(e);
      g = { x: p.clientX, y: p.clientY, live: false, top: pageTop(), px: p.clientX, pt: T(e), v: 0 };
      if (o.down) o.down();
    }
    function move(e) {
      if (!g) return;
      var p = P(e), dx = p.clientX - g.x, dy = p.clientY - g.y, t = T(e);
      if (!g.live) {
        if (Math.abs(pageTop() - g.top) > 2) { g = null; return; }        /* the page took it */
        if (Math.abs(dy) - Math.abs(dx) >= SWIPE_GIVE) { g = null; return; }
        if (Math.abs(dx) - Math.abs(dy) < SWIPE_LOCK) return;             /* still open */
        g.live = true;
        if (o.start) o.start();
      }
      if (t > g.pt) { g.v = (p.clientX - g.px) / (t - g.pt); g.px = p.clientX; g.pt = t; }
      if (o.move) o.move(dx);
      if (e.cancelable) e.preventDefault();
    }
    function up(e) {
      if (!g) return;
      var d = g; g = null;
      if (!d.live) return;
      if (o.end) o.end(P(e).clientX - d.x, T(e) - d.pt > FLICK_GAP ? 0 : d.v);
    }
    targets.forEach(function (t) {
      t.addEventListener('mousedown', down);
      t.addEventListener('touchstart', down, { passive: true });
      /* touchmove is dispatched to the touchstart target for the life of the
         touch, so the finger may leave the surface and the drag survives - which
         is also why this needs no pointer capture */
      t.addEventListener('touchmove', move, { passive: false });
      /* a native image drag freezes the stream: mouseup never arrives, only
         dragend, and the row stays parked wherever the cursor let go */
      t.addEventListener('dragstart', function (ev) { ev.preventDefault(); });
    });
    on(window, 'mousemove', move);
    on(window, 'mouseup', up);
    on(window, 'touchend', up);
    on(window, 'touchcancel', up);
  }

  /* Every drag surface: the stage, plus the phone build's transparent pad over
     the deck band. The dial's seats hang BELOW the stage box, and between them a
     touch landed on .mfs-zone, which listens to nothing - measured across a 440px
     band, 26% of it reached a listener, and the dead part included the centre
     seat, which is exactly where a thumb goes. */
  var swipeSurf = [$('stage')];
  if ($('swipePad')) swipeSurf.push($('swipePad'));

  /* drag: the row is a physical thing, so it should answer to a thumb too */
  var dragMoved = false, dragBase = 0, dragBaseIdx = 0;
  /* where the row actually is, in pixels, transition included - a drag that
     takes over mid-spin has to start from what the eye sees, not from the slot
     the spin was heading for */
  function trackX() {
    var t = getComputedStyle(trackEl).transform, m;
    if ((m = /matrix\(([^)]+)\)/.exec(t))) return parseFloat(m[1].split(',')[4]);
    if ((m = /matrix3d\(([^)]+)\)/.exec(t))) return parseFloat(m[1].split(',')[12]);
    return railTx();
  }
  /* the inverse of railTx: pixels back to slots, fractional while in flight */
  function railAt(x) { return ((railEl.clientWidth || 393) / 2 - slotW / 2 - x) / slotStep; }
  hSwipe(swipeSurf, {
    can: function () { return root.getAttribute('data-ph') === 't2' && !!DECK; },
    down: function () { dragMoved = false; },
    start: function () {
      dragMoved = true;
      /* mid-flight is not a wall. The drag used to refuse to start while a spin
         played, so in a quick series of flicks every second one died inside that
         380ms window. Land the spin's bookkeeping without moving the row and the
         finger picks the line up exactly where it is. */
      if (spinning) railLand(true);
      dragBase = trackX();
      dragBaseIdx = Math.round(railAt(dragBase));
      railEl.classList.add('mfs-dragging');
    },
    move: function (dx) {
      trackEl.style.transition = 'opacity .45s ease';
      trackEl.style.transform = 'translate(' + (dragBase + dx) + 'px,' + railTy() + 'px)';
      /* railIdx does not move until the finger lets go, so the window has to
         follow the transient position or a long drag runs off its own edge */
      var at = Math.round(railAt(dragBase + dx));
      if (at !== railPeek) { railPeek = at; railFill(at); }
    },
    end: function (dx, v) {
      railEl.classList.remove('mfs-dragging');
      var to = Math.round(railAt(dragBase + dx));
      /* a throw carries one slot past where it was let go */
      if (to === dragBaseIdx && (Math.abs(v) > FLICK_V || Math.abs(dx) > 18)) to += dx < 0 ? 1 : -1;
      var steps = to - railIdx;
      if (!steps) { placeRail(true, 240); return; }
      spinRail(steps, 360);
    }
  });

  /* ================= entry: two-step iPhone picker =================
     the tile tap used to fire the whole flow, so body colour (sitting under the
     grid) was never reached. Now tap 1 selects, step 2 unlocks, and an explicit
     CTA commits. */
  var modelsEl = $('models'), bodyEl = $('bodyColors');
  var pending = { model: null, body: state.body };
  var hasLook = false;

  /* name only — the phone glyph and the diagonal both said things the user is not
     choosing between, and the row is a list of models, not a spec comparison */
  function tileHTML(nm) { return '<div class="mfs-mn">' + nm + '</div>'; }

  modelsEl.innerHTML = '';   /* re-connect of the same node must not double the tiles */
  pickableModels().forEach(function (m, i) {
    var b = document.createElement('button');
    b.className = 'mfs-m-tile';
    b.setAttribute('data-k', m.k);
    b.style.animationDelay = (0.13 + i * 0.035) + 's';
    b.innerHTML = tileHTML(m.n);
    b.addEventListener('click', function () { pickModel(m.k, null); });
    modelsEl.appendChild(b);
  });

  function renderBodyDots() {
    bodyEl.innerHTML = '';
    phonesFor(pending.model || DEF.m).forEach(function (p, i) {
      var b = document.createElement('button');
      b.className = 'mfs-pdot' + (pending.body === p.k ? ' mfs-on' : '');
      b.style.animationDelay = (0.3 + i * 0.04) + 's';
      b.innerHTML = '<i style="background:' + p.h + '"></i><span>' + p.n + '</span>';
      b.addEventListener('click', function () { pickBody(p.k); });
      bodyEl.appendChild(b);
    });
  }
  function markTile(k) {
    modelsEl.querySelectorAll('.mfs-m-tile').forEach(function (t) { t.classList.remove('mfs-on'); });
    /* .mfs-m-tile, not .m-tile: an unported selector from the prototype left the
       picked model tile silently unlit */
    var hit = modelsEl.querySelector('.mfs-m-tile[data-k="' + k + '"]');
    if (hit) hit.classList.add('mfs-on');
    $('pickModelName').textContent = find(MODELS, k).n;
  }
  function armCTA() {
    var go = $('sheetGo');
    go.disabled = false;
    go.textContent = (hasLook ? 'Switch to this iPhone' : 'Start') + '  →';
  }
  function pickModel(k, forceBody) {
    pending.model = k;
    warmModel(k);   /* the sheet is still open — decode this model's set now */
    /* model swap can invalidate the body colour — snap to one this model has */
    var bods = bodiesFor(k);
    if (bods.indexOf(pending.body) < 0) pending.body = bods[0];
    markTile(k);
    $('stepBody').classList.remove('mfs-locked');
    $('stepBody').classList.add('mfs-live');
    renderBodyDots();
    pickBody(pending.body);
    /* the bar has no scroller: picking a model just closes the dropdown and lights
       up the colour segment in place. */
    $('stepModel').classList.remove('mfs-open');
    $('stepModel').classList.add('mfs-chosen');
    $('modelDrop').setAttribute('aria-expanded', 'false');
  }
  function pickBody(k) {
    pending.body = k;
    var list = phonesFor(pending.model || DEF.m), kids = bodyEl.children;
    for (var i = 0; i < kids.length; i++) kids[i].classList.toggle('mfs-on', list[i] && list[i].k === k);
    $('pickBodyName').textContent = bodyName(pending.model || DEF.m, k);
    armCTA();
    /* live preview behind the scrim — the choice is visible before committing */
    state.body = k;
    if (pending.model) state.model = pending.model;
    /* the case palette is per model+body — re-clamp so the swatch always has a shot */
    var pool = caseColors(curModel(), curBody());
    if (pool.indexOf(state.color) < 0) state.color = nearestIn(state.color, pool) || pool[0];
    renderPhone();
  }
  renderBodyDots();

  function openModelSheet() {
    /* phone build: the picker is the first thing inside the layer, not a bar
       floating over the page — so the device travels in before the sheet opens */
    pending.model = state.model; pending.body = state.body;
    if (state.model) {
      markTile(state.model);
      $('stepBody').classList.remove('mfs-locked'); $('stepBody').classList.add('mfs-live');
      renderBodyDots();
      $('pickBodyName').textContent = bodyName(state.model || DEF.m, state.body);
      armCTA();
    } else {
      modelsEl.querySelectorAll('.mfs-m-tile').forEach(function (t) { t.classList.remove('mfs-on'); });
      $('pickModelName').textContent = ''; $('pickBodyName').textContent = '';
      $('stepBody').classList.add('mfs-locked'); $('stepBody').classList.remove('mfs-live');
      renderBodyDots();
      $('sheetGo').disabled = true;
      $('sheetGo').textContent = 'Pick your iPhone first';
    }
    /* the bar opens with the dropdown shut; nothing is picked yet on a cold start */
    $('stepModel').classList.remove('mfs-open');
    $('stepModel').classList.toggle('mfs-chosen', !!state.model);
    $('modelDrop').setAttribute('aria-expanded', 'false');
    if (!state.model) $('pickModelName').textContent = 'Which iPhone';
    /* a page that sells for one phone only (iPhone Duo, 2026-10-22) has nothing
       to ask: pick it for them so the bar opens on the body colour with Start lit
       (user: 只有一个机型时自动选中). The editor lists every model, so it never
       fires there. */
    var onlyOne = pickableModels();
    if (!state.model && onlyOne.length === 1) pickModel(onlyOne[0].k, null);
    $('modelModal').classList.add('mfs-open');
    mobSheets();
    growBar();
  }

  /* Open the bar at the button's width and let it widen to its own. The natural
     width has to be measured first — the bar sizes to its content, so there is no
     number to animate towards until it has been laid out once. */
  /* ---- seat the bar on the button's line (2026-09-04) ----
     It used to sit 40px off the floor of the viewport while the button that opens
     it is much higher up the page, with a screenful of nothing in between — the
     review's words: "it should open at the button; right now it is too far away
     to catch the eye". growBar already opens it from 196px (PRESS START's width),
     and only with this vertical alignment does the whole move read as that button
     pulling open to both sides.
     Measured once, handed to CSS as one number (--mfs-bar-top) so the layout
     stays in the stylesheet. The phone build is untouched: there the picker is a
     bottom card by design (1147:3407). */
  function seatBar() {
    if (isMob()) return;
    var btn = root.getAttribute('data-ph') === 'entry'
      ? $('startBtn')
      : root.querySelector('.mfs-zone .mfs-row-btns .mfs-btn.mfs-solid');
    var bar = root.querySelector('.mfs-pickbar');
    if (!btn || !bar) return;
    var b = btn.getBoundingClientRect();
    if (!b.height) return;
    /* Centre the bar on the button's centre line. On a first open the bar may not
       have a laid-out height yet; fall back to its own content height of 60 —
       that is Figma 1:58's 700x60 row. */
    var barH = bar.getBoundingClientRect().height || 60;
    root.style.setProperty('--mfs-bar-top', Math.round(b.top + b.height / 2 - barH / 2) + 'px');
  }
  function growBar() {
    root.classList.add('mfs-picking');
    var bar = root.querySelector('.mfs-pickbar');
    seatBar();
    /* the phone build's picker is a bottom sheet, which arrives by sliding up —
       growing it out of a 196px button would be two entrances at once */
    if (!bar || reduced || isMob()) return;
    bar.classList.add('mfs-grow');
    bar.style.transition = 'none';
    bar.style.width = 'auto';
    var full = bar.getBoundingClientRect().width;
    bar.style.width = '196px';                 /* the button it grows out of */
    void bar.offsetWidth;
    bar.style.transition = 'width .46s cubic-bezier(.16,.86,.22,1)';
    bar.style.width = full + 'px';
    setTimeout(function () {
      bar.style.transition = ''; bar.style.width = ''; bar.classList.remove('mfs-grow');
      seatBar();          /* the height is only final once the width settles */
    }, 520);
  }
  $('sheetGo').addEventListener('click', function () {
    if (this.disabled) return;
    commitPick();
  });

  /* ---- bottom-bar plumbing (model dropdown + dismiss) ---- */
  $('modelDrop').addEventListener('click', function (e) {
    e.stopPropagation();
    var seg = $('stepModel'), willOpen = !seg.classList.contains('mfs-open');
    seg.classList.toggle('mfs-open', willOpen);
    this.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
  });
  $('models').addEventListener('click', function (e) { e.stopPropagation(); });
  $('barClose').addEventListener('click', dismissPicker);
  /* click anywhere off the bar closes the dropdown, not the bar itself —
     dismissing the whole bar is the ✕'s job */
  on(document, 'click', function (e) {
    if (!$('stepModel').classList.contains('mfs-open')) return;
    if (e.target.closest && e.target.closest('#mfs-stepModel')) return;
    $('stepModel').classList.remove('mfs-open');
    $('modelDrop').setAttribute('aria-expanded', 'false');
  });
  on(document, 'keydown', function (e) {
    if (e.key !== 'Escape') return;
    if ($('stepModel').classList.contains('mfs-open')) {
      $('stepModel').classList.remove('mfs-open');
      $('modelDrop').setAttribute('aria-expanded', 'false');
    } else if ($('modelModal').classList.contains('mfs-open')) {
      $('modelModal').classList.remove('mfs-open');
      mobSheets();
    }
  });
  function commitPick() {
    root.classList.remove('mfs-picking');   /* the bar is done with the line */
    state.model = pending.model; state.body = pending.body;
    if (!typeHasStock(state.caseType)) state.caseType = CASES[0].k;
    stopEntryLoop();
    $('modelModal').classList.remove('mfs-open');
    mobSheets();                /* the sheet is gone: let the page scroll again */
    if (hasLook) {
      /* Mid-flow model change lands on the case-colour board: the model's first
         downstream consequence is the case (its colour pool and cut vary per
         model), so the user is taken to where any clamped colour is VISIBLE and
         fixable. The stand choice is model-independent and survives — crumb 3
         stays unlocked if it was. */
      setStep('casePaint');
      renderPhone(); layoutRail(); renderStand();
      renderAltCase();
      buildFan(); buildCaseRing(); ringEnter();
      micro('');
      return;
    }
    /* let the sheet finish sliding out before the bang owns the frame.
       The bang clears onto the studio itself, already dressed: the roll is the
       starting point of the work, not a finished product to approve. */
    /* the entry furniture leaves the INSTANT Start is pressed — waiting out
       the sheet slide left the CTAs standing for 300ms and then fading, which
       read as a stray flash (user). The stand row files out slot by slot. */
    root.classList.add('mfs-blastoff');
    var outs = trackEl.children;
    for (var oi = 0; oi < outs.length; oi++) {
      var od = Math.abs(oi - railIdx);
      if (od > 7) continue;
      outs[oi].style.animationDelay = (od * 0.05) + 's';
      outs[oi].classList.remove('mfs-in', 'mfs-inR');
      outs[oi].classList.add('mfs-out');
    }
    setTimeout(function () {
      bigBang(function () {
        root.classList.remove('mfs-blastoff');
        /* the rebuilt track must NOT flash through its phase fade: layoutRail's
           innerHTML wipe discards the blastoff exit state, and the row popped
           back solid for the 0.45s it took the t1 rule to fade it. Pin it
           transparent AFTER layoutRail (placeRail rewrites the transition),
           release once the phase rule owns the zero. */
        var pinTrack = function () {
          trackEl.style.transition = 'none';
          trackEl.style.opacity = '0';
          requestAnimationFrame(function () {
            trackEl.style.opacity = '';
            placeRail(false);        /* restores the standard opacity transition */
          });
        };
        hasLook = true;
        firstLook();
        if (surpriseMode) {
          /* the dice decides everything except the phone: case, colours, stand.
             It lands on the paint step — every dial is one tap away from here. */
          /* The dice settle the CASE only, and drop the user at 03 — landing on
             the stand-colour step decided all four dials for them and left nothing
             to play with. One roll, three steps still yours. */
          surpriseMode = false;
          state.caseType = (Math.random() < .5 && typeHasStock('frame')) ? 'frame' : 'movas';
          state.standPicked = false;
          setStep('casePaint');
          renderPhone(); layoutRail(); renderStand();
          pinTrack();
          buildFan(); buildCaseRing();
          /* 2026-09-04: the small line above the colour dial (colorLine, e.g.
             "Misty Cove x Cement. MOVAS™ leather.") is a leftover — drop it. The
             micro slot still carries the product name and the ⓘ box on t2/t3, so
             this writes nothing into it rather than removing the slot. */
          micro('');
          return;
        }
        setPhase('t1');
        renderPhone(); layoutRail(); renderStand();
        pinTrack();
        buildFan();
        enterCase();
      });
    }, 300);
  }

  /* ================= big bang (clipped to the device mockup) ================= */
  function frameRect() {
    var r = root.querySelector('.mfs-phone-frame').getBoundingClientRect();
    var left = Math.max(r.left, 0), top = Math.max(r.top, 0);
    var right = Math.min(r.right, innerWidth), bottom = Math.min(r.bottom, innerHeight);
    return { left: left, top: top, width: Math.max(60, right - left), height: Math.max(60, bottom - top) };
  }
  function bigBang(then) {
    if (reduced || !('animate' in Element.prototype)) { then(); return; }
    var box = $('bang'); box.innerHTML = '';
    var fr = frameRect();
    box.style.left = fr.left + 'px'; box.style.top = fr.top + 'px';
    box.style.width = fr.width + 'px'; box.style.height = fr.height + 'px';
    var W = fr.width, H = fr.height, N = 60, els = [];
    for (var i = 0; i < N; i++) {
      var s = document.createElement('span');
      var c = MOVAS[i % MOVAS.length].h;
      var t = i % 4;
      if (t === 0) { s.className = 'mfs-bg-case'; s.style.width = '38px'; s.style.height = '64px'; s.style.background = c; }
      else if (t === 1) { s.className = 'mfs-bg-stand'; s.style.width = '42px'; s.style.height = '56px'; s.style.background = c; }
      else if (t === 2) { s.className = 'mfs-bg-ring'; s.style.width = '34px'; s.style.height = '34px'; s.style.borderColor = c; }
      else { s.className = 'mfs-bg-band'; s.style.width = '20px'; s.style.height = '66px'; s.style.background = c; }
      s.style.opacity = '0';
      box.appendChild(s); els.push(s);
    }
    try {
      els.forEach(function (s, i) {
        var a = Math.random() * Math.PI * 2;
        var r = (Math.min(W, H) * .16) + Math.random() * (Math.max(W, H) * .48);
        var x = Math.cos(a) * r, y = Math.sin(a) * r;
        var rot = (Math.random() * 720 - 360);
        s.animate([
          { transform: 'translate(-50%,-50%) scale(.2) rotate(0deg)', opacity: 0 },
          { transform: 'translate(calc(-50% + ' + x + 'px), calc(-50% + ' + y + 'px)) scale(1) rotate(' + rot + 'deg)', opacity: 1 }
        ], { duration: 640, delay: i * 4, easing: 'cubic-bezier(.16,.9,.3,1)', fill: 'forwards' });
      });
      setTimeout(function () {
        els.forEach(function (s, i) {
          var cur = getComputedStyle(s).transform;
          s.animate([
            { transform: cur, opacity: 1 },
            { transform: 'translate(-50%, -46%) scale(.06) rotate(0deg)', opacity: 0 }
          ], { duration: 470, delay: i * 3, easing: 'cubic-bezier(.6,0,.9,.4)', fill: 'forwards' });
        });
        setTimeout(function () { box.innerHTML = ''; then(); }, 740);
      }, 1050);
    } catch (e) { box.innerHTML = ''; then(); }
  }

  /* ================= roll (slot machine) ================= */
  /* The roll is made ONE TICK EARLY. The attract loop changes the case colour
     every 2000ms and the old warm-up ladder sat behind requestIdleCallback with
     a 2500ms timeout, so the very first roll was always cold: the row moved on
     schedule and the case caught up a beat later. Pre-rolling also means the
     ladder no longer has to decode all thirteen colours up front just to cover
     whichever one the dice picks - it decodes exactly the next one. */
  var nextPaint = null;
  function rollNext() {
    var pool = caseColors(curModel(), curBody());
    /* attract only rolls colours that have a delivered layer: a miss falls to
       the vector build, which reshapes the 572 stage box mid-loop (and the
       attract face is supposed to be all real product anyway) */
    var real = pool.filter(function (k) { return !!caseLayer('movas', curModel(), k); });
    var take = real.length ? real : pool;
    nextPaint = take[Math.floor(Math.random() * take.length)];
    warm(stageLay(caseLayer('movas', curModel(), nextPaint)));
    return nextPaint;
  }
  function rollPaint() {
    state.color = nextPaint || rollNext();
    state.caseType = 'movas';
    nextPaint = null;
    rollNext();
  }
  /* the stand is no longer computed — it's whatever the rail is parked on, so a
     fresh look is a fresh paint plus a jump to a random slot */
  function firstLook() {
    /* The FIRST frame is not a dice roll. Every visit used to open on a random
       case colour and a random seat, which meant nothing about the opening
       image could be known ahead of time - no preload, and three cold layers
       decoding while the user watched an empty stage. heroSeat() parks the row
       on the seat the theme names (section setting "Preload assets", which also
       emits the <link rel=preload> for it) so that stand is already in flight
       before the module has even run. Everything after this call is random
       exactly as before. */
    var seat = hasLook ? -1 : heroSeat();
    /* the case goes the same way: if the theme preloads one, open on it (the
       Duo page preloads MD030-1-DUO-MCGY, and the dice used to put some other
       colour on the phone while that file sat unused — NO.264) */
    if (!hasLook) nextPaint = heroCase() || nextPaint;
    rollPaint();
    if (DECK) railIdx = (cyclic() ? DECK : 0) + (seat >= 0 ? seat : Math.floor(Math.random() * DECK));
    readDeck();
    state.standManual = false;
  }
  /* the case colour whose layer the section preloads, or null */
  function heroCase() {
    var hrefs = Array.prototype.map.call(
      document.querySelectorAll('link[rel="preload"][as="image"]'),
      function (l) { return l.getAttribute('href') || ''; }).join(' ');
    var pool = caseColors(), i, n;
    for (i = 0; i < pool.length; i++) {
      n = caseLayer('movas', curModel(), pool[i]);
      if (n && hrefs.indexOf('/' + n + '.') > -1) return pool[i];
    }
    return null;
  }
  /* which deck seat carries the preloaded stand, or -1 if the theme named none
     (or named one this shop cannot draw). Matched on the RESOLVED layer name,
     so the setting stays a plain filename and the SKU fallback chain still
     applies - if they ever disagree, the row simply opens where it used to. */
  function heroSeat() {
    var want = (DATA.hero || '').replace(/\.[a-z0-9]+$/i, '');
    if (!want || !DECK) return -1;
    for (var i = 0; i < DECK; i++)
      if (standLayer(deck[i].fam, deck[i].color) === want) return i;
    return -1;
  }
  /* ================= step 1: which case ================= */
  var altEl = $('altCase');
  function altStep() { return Math.round(find(MODELS, DEF.m).w * Z() * 1.22); }
  function altOffset() { return state.caseType === CASES[0].k ? altStep() : -altStep(); }
  function altCaseKey() { return state.caseType === 'movas' ? 'frame' : 'movas'; }
  function renderAltCase() {
    var altOk = typeHasStock(altCaseKey());
    altEl.style.display = altOk ? '' : 'none';
    if (!altOk) { renderWornCase(); return; }
    var m = find(MODELS, DEF.m);
    altEl.innerHTML = '<div class="mfs-phone mfs-layered"><div class="mfs-lay-slot"></div><div class="mfs-shadow"></div></div>'
      /* hit area = the case's visible extent, not the transparent 1080 square —
         squares overlap their neighbours and swallow clicks meant for them */
      + (COREG ? '<i class="mfs-hit"></i>' : '');
    altEl.style.transform = 'translate(calc(-50% + ' + altOffset() + 'px), -50%)';
    paintAlt();
    renderWornCase();
  }
  /* the case the device wears, as its own layer over the bare hero — a swap
     moves this and the spare, and the device itself never travels (Kay) */
  function renderWornCase() {
    var w = $('wornCase');
    if (!w) return;
    var m = find(MODELS, DEF.m), z = Z(), pw = Math.round(m.w * z);
    var ck = modelPicking() ? pickDefault(state.caseType) : state.color;
    var lay = stageLay(caseLayer(state.caseType, curModel(), ck));
    if (lay) {
      var cw = COREG ? sqPx() : pw, chh = COREG ? sqPx() : Math.round(pw / layerAR(lay));
      /* reuse the skeleton across repaints — rebuilding it killed the old bitmap
         (blank frame) and threw away the caption hanging off this box */
      var box = w.firstChild && w.firstChild.classList && w.firstChild.classList.contains('mfs-layered')
        ? w.firstChild : null;
      if (!box) {
        w.innerHTML = '<div class="mfs-phone mfs-layered"><div class="mfs-lay-slot"></div></div>';
        box = w.firstChild;
      }
      box.style.width = cw + 'px'; box.style.height = chh + 'px';
      swapLayer(box.firstChild, 'mfs-lay-case', lay, 'left:0;top:0;width:100%');
    } else {
      /* deviation #27 tightened: no vector case, no block — just the empty frame */
      var box2 = w.firstChild && w.firstChild.classList && w.firstChild.classList.contains('mfs-layered')
        ? w.firstChild : null;
      if (!box2) {
        w.innerHTML = '<div class="mfs-phone mfs-layered"><div class="mfs-lay-slot"></div></div>';
        box2 = w.firstChild;
      }
      box2.style.width = (COREG ? sqPx() : pw) + 'px';
      box2.style.height = (COREG ? sqPx() : Math.round(m.h * z)) + 'px';
      dropLayer(box2.firstChild, 'mfs-lay-case');
    }
    w.style.transform = 'translate(-50%, -50%)';
  }
  /* repaint in place — rebuilding the node mid-swap is what made it blink */
  function paintAlt() {
    var el = altEl.firstChild;
    if (!el) return renderAltCase();
    var m = find(MODELS, DEF.m), ph = bodyRec(curModel(), curBody()), z = Z();
    var altType = altCaseKey();
    var ck = modelPicking() ? pickDefault(altType) : state.color;
    var w = Math.round(m.w * z);

    /* bare case, no body behind it: on the board the case not on the phone shows
       an empty camera cutout — that emptiness is what says "this is just a case".
       (A phone-build variant that gave the spare its own body layer was tried and
       rejected on sight: two phones side by side read as two phones, and this step
       is one phone trying on two cases — user, 2026-09-03: "现在两个手机在了，这里
       应该和 PC 一样就好啦，就只是壳在滑动".) */
    var lay = stageLay(caseLayer(altType, curModel(), ck));
    if (lay) {
      el.classList.add('mfs-layered');
      el.style.width = (COREG ? sqPx() : w) + 'px';
      el.style.height = (COREG ? sqPx() : Math.round(w / layerAR(lay))) + 'px';
      var slot = el.querySelector('.mfs-lay-slot');
      if (!slot) { slot = document.createElement('div'); slot.className = 'mfs-lay-slot'; el.insertBefore(slot, el.firstChild); }
      swapLayer(slot, 'mfs-lay-case', lay, 'left:0;top:0;width:100%');
      return;
    }
    /* deviation #27 tightened: no vector spare case, no block — empty frame */
    el.classList.add('mfs-layered');
    el.style.width = (COREG ? sqPx() : w) + 'px';
    el.style.height = (COREG ? sqPx() : Math.round(m.h * z)) + 'px';
    var slotA = el.querySelector('.mfs-lay-slot');
    if (!slotA) { slotA = document.createElement('div'); slotA.className = 'mfs-lay-slot'; el.insertBefore(slotA, el.firstChild); }
    dropLayer(slotA, 'mfs-lay-case');
  }
  /* Nothing left to align: the device keeps its one size at the finale and the
     receipt sits under it in the flow. Kept as the hook renderDone already calls
     (and as the desk build's --donelift reset), rather than unpicking three
     call sites for a no-op. */
  function alignDonePlate() {
    if (root.getAttribute('data-ph') !== 'done') return;
    root.style.setProperty('--donelift', '0px');
  }
  function buildCasePick() {
    var here = find(CASES, state.caseType), there = find(CASES, altCaseKey());
    micro('');
    root.style.setProperty('--miclift', '0px');
    var old = root.querySelectorAll('.mfs-case-cap');
    Array.prototype.forEach.call(old, function (n) { n.remove(); });
    /* The 2026-09-04 case board (1056:3241) puts one line under each case — the
       material name, "MOVAS™ Case" / "Frame Case" — and no tagline. The board head
       (Choose A Case / Snap Or Frame) has already said what this step asks; a
       third line under the case would be saying it again. */
    /* The design names them "MOVAS™ Case" and "Frame Case". caseShort strips the
       material string out of the product title, and the frame one carries both
       words ("Frame Case MOVAS™"), which strips down to "Frame MOVAS™" — so the
       frame type is named off the type key instead. */
    var capOf = function (c) {
      return '<span class="mfs-case-cap"><b>'
           + (c.k === 'frame' ? 'Frame' : caseShort(c.k)) + ' Case</b></span>';
    };
    $('wornCase').insertAdjacentHTML('beforeend', capOf(here));
    if (typeHasStock(altCaseKey()))
      altEl.insertAdjacentHTML('beforeend', capOf(there));
  }
  /* sliding is the selection: the other case walks in, the current one walks out */
  /* ---- the glide between the two cases ----
     Reworked 2026-09-05 (user, phone pass: "两个壳切换时的动效现在比较生硬,
     需要优化得更丝滑"). Two things made it read as mechanical:

       1. cubic-bezier(.16,.86,.22,1) spends ~80% of the distance in the first
          third of the time, so the pair snapped sideways and then crawled the
          last few pixels. SWAP_EASE is the settle curve instead — it leaves
          slowly, covers the middle fast, and arrives without a visible crawl.
       2. the identity swap was hung on a bare setTimeout(500) racing a 500ms
          transition. Whichever landed first showed: early and the reset cut
          into live motion, late and the pair sat still before snapping. It is
          driven off `transitionend` now, with the timer kept only as the
          fallback for a transition that never fires (an off-screen tab, a
          transform that did not actually change).

     The distance and the choreography are untouched — still one band gliding
     by one delta, the device underneath still never moves (Kay, 2026-08-31). */
  var SWAP_MS = 560, SWAP_EASE = 'cubic-bezier(.32,.72,0,1)';
  function swapCase() {
    if (cpSwapping || !typeHasStock(altCaseKey())) return;
    cpSwapping = true;
    /* The title used to yield here (Kay, 2026-08-31: a sliding case crossed its
       column). It no longer does — the board has been re-laid out several times
       since, and measured on the pick board the title's ink ends at x=478 while
       the case at its leftmost reaches ~857, so nothing crosses anything. The
       step's title is now fixed, which is what it reads as: one heading for one
       question (user, 2026-09-10: 「这一步应该标题是固定不变的」).
       ⚠ The clearance is width-dependent: below roughly 1200px the case does
       reach the title's ink. The head is z-index 8 over the case band's 7, so it
       would be text over product rather than a clipped title — if that shows up
       on a narrow desk window, gate this on a measured collision rather than
       bringing the blanket hide back. */
    var worn = $('wornCase');
    /* one strip, one delta: both cases translate by the same amount in the same
       direction (the spare's offset collapsing to zero), so the pair glides
       sideways as a band instead of the two crossing through each other */
    var exit = -altOffset();
    var settled = false;
    function settle() {
      if (settled) return;
      settled = true;
      altEl.removeEventListener('transitionend', onEnd);
      clearTimeout(tid);
      /* the board may have moved on (a choose fired before this landed):
         flipping the type now would desync the ring from the state */
      if (root.getAttribute('data-cs') !== 'pick') { cpSwapping = false; return; }
      /* identities swap where the pixels already are, so the reset is invisible —
         and the DEVICE under them never moved (Kay, 2026-08-31) */
      state.caseType = altCaseKey();
      worn.style.transition = 'none';
      altEl.style.transition = 'none';
      renderPhone(); paintAlt(); renderWornCase();
      altEl.style.transform = 'translate(calc(-50% + ' + altOffset() + 'px), -50%)';
      buildCasePick();
      requestAnimationFrame(function () {
        worn.style.transition = '';
        altEl.style.transition = '';
        cpSwapping = false;
      });
    }
    /* only the incoming case's own transform ends this — a bubbled transitionend
       from a child (the caption, a layer) would settle the swap early */
    function onEnd(e) { if (e.target === altEl && e.propertyName === 'transform') settle(); }
    var tid;
    if (!reduced) {
      worn.style.transition = 'transform ' + SWAP_MS + 'ms ' + SWAP_EASE;
      worn.style.transform = 'translate(calc(-50% + ' + exit + 'px), -50%)';
      altEl.style.transition = 'transform ' + SWAP_MS + 'ms ' + SWAP_EASE;
      altEl.style.transform = 'translate(-50%, -50%)';
      altEl.addEventListener('transitionend', onEnd);
      tid = setTimeout(settle, SWAP_MS + 120);   /* fallback, not the schedule */
    } else {
      tid = setTimeout(settle, 0);
    }
  }
  var cpSwapping = false;
  /* ---- which models the picker offers ----
     Art and the phone dictionary land a season ahead of the catalogue, and a
     model you cannot buy a case for is a dead end: the board would price off
     the product base and Checkout would refuse. So a model stays out of the
     picker until at least one case variant exists for it, and walks in on its
     own the day ops creates them — no code change, no redeploy.
     ?debug=1 and the theme editor list every model: that is how the new season
     gets previewed and signed off before it goes on sale. */
  function modelSellable(mk) {
    for (var i = 0; i < CASES.length; i++) if (!typeUnlisted(CASES[i].k, mk)) return true;
    return false;
  }
  function pickableModels() {
    if (SHOW_UNLISTED) return MODELS;
    var out = MODELS.filter(function (m) { return modelSellable(m.k); });
    /* never empty the picker — a catalogue we failed to read is not a reason
       to show the customer nothing */
    return out.length ? out : MODELS;
  }

  /* How many case TYPES this model can actually wear. Frame is Pro / Pro Max
     only, so on an iPhone 17 or Air there is exactly one — and a board that
     asks you to choose between one thing is a board asking nothing. */
  function caseTypeCount() {
    var n = 0;
    for (var i = 0; i < CASES.length; i++) if (typeHasStock(CASES[i].k)) n++;
    return n;
  }
  function onlyCaseType() {
    for (var i = 0; i < CASES.length; i++) if (typeHasStock(CASES[i].k)) return CASES[i].k;
    return CASES[0].k;
  }
  /* The case-TYPE board skips itself when there is nothing to choose (user,
     2026-09-04: "选壳类型这一步啊，如果只有一个，要不直接帮我 skip 自动到下一步吧,
     用户也不用看到这一步了"). The skip lives here rather than at the eight call
     sites, so every way into this step — the bang, the crumb, the back button,
     "different case" — is covered by one rule.
     It lands on the colour board directly instead of calling chooseCase(): that
     one sweeps the REJECTED case off to the left, and with a single type there is
     no rejected case to sweep. Same end state, no animation for a thing that
     was never on stage. */
  function enterCase() {
    /* the ring's bytes came down at entry as tier B — turn them into bitmaps
       now that the board is opening, then look one step further on */
    warmCases(curModel(), true);
    idleWarm(lookahead);
    if (caseTypeCount() < 2) {
      state.caseType = onlyCaseType();
      setStep('casePaint');
      renderPhone();
      buildFan();
      buildCaseRing();
      ringEnter();
      micro('');
      return;
    }
    setStep('casePick');
    renderPhone();               /* both cases go white the moment the step opens */
    renderAltCase(); buildCasePick();
    caseStepEnter();
  }

  /* The pair walks in from off-frame right, the far one a beat behind the near
     one, and the title lifts under them.

     Web Animations, not inline styles: renderAltCase(), swapCase() and
     chooseCase() all write these same two transforms, and an inline tween that
     loses a race with any of them strands a case 300px off its mark. An
     animation with no fill borrows the element for its duration and hands the
     transform straight back, so it cannot leave anything behind. */
  function caseStepEnter() {
    var head = $('boardHead');
    if (reduced || !('animate' in Element.prototype)) return;
    if (head) head.animate(
      [{ opacity: 0, transform: 'translateY(16px)' }, { opacity: 1, transform: 'none' }],
      { duration: 500, easing: 'cubic-bezier(.16,.9,.3,1)' });
    function at(x) { return 'translate(calc(-50% + ' + x + 'px), -50%)'; }
    /* only the CASES travel — the device is already home and never moves */
    var worn = $('wornCase');
    [[worn, 0, 300, 0], [altEl, altOffset(), 380, 80]].forEach(function (p) {
      var el = p[0], home = p[1], dx = p[2], delay = p[3];
      if (!el) return;
      /* transform only. Animating opacity here promotes .hero to its own
         compositing layer, and the absolutely-positioned .case-cap hanging
         outside its box stops painting for as long as that layer lives — the
         labels under both cases simply vanish. The slide alone reads fine. */
      el.animate(
        [{ transform: at(home + dx) }, { transform: at(home) }],
        { duration: 620, delay: delay, easing: 'cubic-bezier(.16,.86,.22,1)' }
      );
    });
  }

  $('chooseCaseBtn').addEventListener('click', function () { chooseCase(state.caseType); });
  /* ---- swipe the pair, the way the stand row is swiped ----
     Two cases, so a horizontal swipe can only mean "the other one" (user,
     2026-09-02: "和 pc 一样，信息在下面，然后横滑切换吧" — the names go back under
     the cases where desk keeps them, and the segmented control that stood in for
     this gesture is gone).

     The band follows the finger now. It could not before, because a part-dragged
     transform on the two cases would fight the inline styles renderAltCase and
     chooseCase write — but the RAIL underneath is nobody's business, and both
     cases are inside it, so dragging the rail drags the pair as one piece and
     leaves every existing transform alone. It also composes with the commit:
     swapCase glides the two children by -altOffset() while the rail glides back
     to zero on the same curve, so the pixels carry straight on from wherever the
     finger let go instead of snapping back first. */
  /* the two cases, and nothing else: .mfs-rail also holds .mfs-hero, and pushing
     that carries the phone body along with the cases — which is what the screen
     recording caught on both builds (user, 2026-09-03: "为什么手机也有一个手机壳在
     动？…PC 在滑动换手机壳产品，背后也会手机动一下，之前应该手机就不动的，只是上层
     的手机壳在滑动切换"). The device never travels; the cases slide over it. */
  var cpDragT = 0, casesEl = $('cases') || railEl;
  function cpBandRest(dur, ease) {
    casesEl.style.transition = 'transform ' + dur + 'ms ' + ease;
    casesEl.style.transform = 'translateX(0px)';
    clearTimeout(cpDragT);
    /* back to no transform at all: a permanent one is a permanent layer */
    cpDragT = setTimeout(function () {
      casesEl.style.transition = ''; casesEl.style.transform = '';
    }, dur + 60);
  }
  hSwipe(swipeSurf, {
    can: function () {
      return root.getAttribute('data-ph') === 't1'
          && root.getAttribute('data-cs') === 'pick' && !cpSwapping;
    },
    start: function () { clearTimeout(cpDragT); casesEl.style.transition = ''; },
    /* 0.55: the pair reads as pulled, not as already swapped */
    move: function (dx) { casesEl.style.transform = 'translateX(' + (dx * 0.55).toFixed(1) + 'px)'; },
    end: function (dx, v) {
      if (Math.abs(dx) > 26 || Math.abs(v) > FLICK_V) {
        if (reduced) { casesEl.style.transition = ''; casesEl.style.transform = ''; }
        /* swapCase's own curve and clock, so the two add up to one motion */
        else cpBandRest(500, 'cubic-bezier(.16,.86,.22,1)');
        swapCase();
        return;
      }
      cpBandRest(220, 'cubic-bezier(.2,.8,.3,1)');
    }
  });
  altEl.addEventListener('click', function () {
    if (root.getAttribute('data-cs') === 'pick') swapCase();
  });
  function chooseCase(ct) {
    /* mutual exclusion with swapCase: its 500ms callback flips the type — a
       choose landing inside that window would build the ring for the old type */
    if (cpSwapping) return;
    cpSwapping = true;
    state.caseType = ct;
    /* the case you did not take leaves to the left, whichever side it was
       standing on — the board reads as the rejected option being swept off */
    if (!reduced) {
      altEl.style.transition = 'transform .52s cubic-bezier(.5,0,.85,.4), opacity .4s ease';
      altEl.style.opacity = '0';
      altEl.style.transform = 'translate(calc(-50% + ' + (altOffset() - 260) + 'px), -50%)';
    }
    setTimeout(function () {
      cpSwapping = false;
      altEl.style.transition = '';
      altEl.style.opacity = '';
      setStep('casePaint');
      renderPhone();
      buildFan();
      buildCaseRing();   /* the strip stays as the source of truth off-desk */
      ringEnter();
      micro('');         /* the caption is the colour name alone (Kay) */
    }, reduced ? 0 : 480);
  }

  /* ================= tune 1: paint ================= */
  var fanEl = $('fanScroller');
  function paintFanCap(k) {
    var c = find(MOVAS, k);
    /* just the name (Kay: the board carries the colour twice already) */
    /* two lines, label over value (design 802:5163) — the label is fixed copy
       in the markup, so only the value is written here */
    var el = $('fanCap') && $('fanCap').querySelector('i');
    if (el) el.textContent = c ? c.n : '\u2014';
    paintModuleNote(k);
  }
  /* ---- "this colour includes a module, and costs more" ----
     iPhone Duo's MOVAS Modular Case sells the plain shell and shell + colour
     module under one product; the four module colourways are $19.99 dearer
     (kk 2026-10-22: customers must see it while picking the colour, not at the
     total). The island marks those variants with `mod` = the shell's own code,
     so the surcharge is read off the two live prices — never a fixed number —
     and a dual code without `mod` (Frame's two-tone corners) never shows it. */
  function paintModuleNote(k) {
    var note = $('fanNote');
    if (!note) return;
    var v = caseVariant(state.caseType, curModel(), k || ''), base = v && v.mod ? caseVariant(state.caseType, curModel(), v.mod) : null;
    var up = base ? Math.round((v.price - base.price) * 100) / 100 : 0;
    note.textContent = up > 0 ? 'Includes a color module \u00b7 +' + money(up) : '';
    note.hidden = !(up > 0);
  }
  /* ================= case colour ring =================
     Cases on a circle whose top is the phone. `ringPos` is a float SEAT index —
     seats, not colours: a short pool is repeated around the orbit (see
     RING_SEATS), so the same colour can hold several seats. A drag moves the
     orbit continuously and releases onto the nearest seat; whatever lands at the
     top is what the phone is wearing, which is why the item at k≈0 fades out —
     the phone is already showing it. */
  var ringEl = $('caseRing'), ringPos = 0, ringAnim = 0;
  var ringMoved = false, ringFrom = 0;   /* the drag's own two pieces of state */
  /* Tuned so two colours sit clearly either side of the phone and the third pair is
     half-cut by the frame, which is the density the artboard shows. At 22°/2.77 the
     neighbour lands ~330px out and ~60px down; the second ~610 out and ~250 down. */
  var RING_STEP = 22;                    /* degrees between neighbours */
  function ringPool() { return caseColors(curModel(), curBody()); }
  /* COREG: the orbit, like the rail, is laid out around the fixed 572px stage
     square — a radius re-derived per model re-flows the whole ring on a switch */
  /* A landscape case is ~1.9x as wide on the canvas as a portrait one, so its
     orbit opens up by RING_LAND to keep the same clear gap either side of the
     phone (the radius itself comes off the stage reference, see stageRef). */
  var RING_LAND = 1.75;
  function ringR() {
    return Math.round(stageRef().h * Z() * 2.16 * (COREG && landscapeModel() ? RING_LAND : 1));
  }
  /* ---- the same ring, re-seated under the thumb (phone build) ----
     Everything that makes the ring a control — the float seat index, the drag,
     the release onto the nearest seat, the roll, the commit — is geometry-free.
     Only four numbers say where a seat sits, so the phone gets four different
     ones and not a line of new interaction: the pivot drops far below the screen
     (so the arc reads as a gentle wheel rather than an orbit), the seats shrink
     to thumbnails, they hang in the deck band instead of on the device, and the
     distance falloff steepens so the pair the frame cuts in half is the last one
     you see — the density the desk board shows at 22°, arrived at differently. */
  var DIAL_STEP = 4;        /* degrees between seats — desk uses 22 */
  var DIAL_FALL = 0.34;     /* opacity lost per seat out — desk uses 0.22 */
  /* 0.1615 = design 1147:3471's 63px centre pitch over the 390 board width. A seat
     is 89 wide, so neighbours overlap by 26px — that is where the overlapping strip
     of case thumbnails comes from (0.25 was the dial's sparse spacing). */
  var DIAL_PITCH = 0.1615;  /* seat spacing as a share of the frame width */
  /* 0.845, not 0.93: the band is 10px deeper than what sits in it, so the thumbs
     and the seats clear the caption row under them instead of meeting it at 4px.
     The seat itself is the same size it was — the clearance came out of the band,
     not out of the product (see the .mfs-sp-vers note in the stylesheet). */
  var DIAL_SEAT = 0.845;    /* seat height as a share of the deck band */
  function dialSeatPx() { return Math.round(MOB_DECK * DIAL_SEAT / MOB_FILL); }
  /* A landscape model's case fills LAND_HALF*2 of its seat — about 105px of a
     122px seat at 440 wide — against a ~71px pitch, so each thumbnail sat on the
     right half (the front panel) of the one before it: "cut in half" (client,
     iPhone Duo, NO.250). Those seats step by the case's own width plus a gap;
     portrait models keep the design's pitch. Drag distance reads this too, so
     one seat is still one seat under the thumb. */
  var DIAL_LAND_GAP = 8;
  function dialPitchPx() {
    var p = Math.round(frameW() * DIAL_PITCH);
    if (COREG && landscapeModel()) p = Math.max(p, Math.round(dialSeatPx() * LAND_HALF * 2) + DIAL_LAND_GAP);
    return p;
  }
  function ringGeo() {
    if (!isMob()) return { R: ringR(), cy: 0, step: RING_STEP, fall: 0.22, side: sqPx() };
    var pitch = dialPitchPx();
    return {
      R: Math.round(pitch / Math.sin(DIAL_STEP * Math.PI / 180)),
      /* seat centres hang in the band the zone reserves below the stage */
      cy: Math.round(mobStageH() / 2 + MOB_DECK / 2 + 4),
      step: DIAL_STEP, fall: DIAL_FALL, side: dialSeatPx(),
      /* the new design (1147:3471) lays these out as a flat overlapping strip
         rather than a wheel: no arc, no tilt, no falloff — see placeCaseRing */
      linear: true, pitch: pitch
    };
  }
  /* A loop has to wrap somewhere, and the wrap is a teleport: the item at
     -n/2 is re-read as +n/2 the instant it crosses. That only passes unnoticed
     where the orbit has already carried it out of the frame — six colours put
     the crossing at |k| = 3, x ≈ ±805, past the edge of a 1440 stage (a much
     wider viewport can still catch it; the falloff there is 0.56). With FEWER
     than six it lands inside the frame at FULL opacity: a two-colour pool put it
     at |k| = 1 and the spare case visibly jumped from the left of the phone to
     its right (user, 2026-09-01).
     So a short pool is a plain arc with real ends — first and last, chevrons
     greyed out there — and only six or more colours orbit. An arc that stops is
     honest about how many colours exist; a loop that repeats them is not. */
  var RING_LOOP_MIN = 6;
  function ringCyclic() { return ringPool().length >= RING_LOOP_MIN; }
  /* the built DOM wins: placeCaseRing works off kids.length, and a pool that
     changed without a rebuild would otherwise leave the two out of step */
  function ringTotal() { return ringEl.children.length || ringPool().length; }

  /* the unpicked board opens on its middle case (see the note in buildCaseRing).
     buildFan() runs first at every call site and paints the caption and the
     strip's centre chip, so it has to land here before either is drawn — doing
     it only in buildCaseRing opened the board naming the attract colour while
     the phone wore the middle one. Returns whether the colour moved. */
  function openOnMiddle(pool) {
    if (state.colorManual || !pool.length) return false;
    var mid = pool[Math.floor(pool.length / 2)];
    if (!mid || mid === state.color) return false;
    state.color = mid; renderPhone();
    return true;
  }
  function buildCaseRing() {
    var pool = ringPool();
    ringEl.innerHTML = pool.map(function (k, i) {
      return '<button class="mfs-ring-item" type="button" data-i="' + i + '" data-k="' + k + '"'
           + ' aria-label="' + find(MOVAS, k).n + '" tabindex="-1">'
           + '<div class="mfs-phone mfs-layered"><div class="mfs-lay-slot"></div><div class="mfs-shadow"></div></div>'
           /* same content-sized hit area as the pick cards: neighbouring squares
              overlap, and a click on a visible case must not land on the
              transparent corner of a nearer one */
           + (COREG ? '<i class="mfs-hit"></i>' : '') + '</button>';
    }).join('');
    /* 第一次进这一幕时，state.color 还是首屏 attract 随机换装留下的那一个，
       于是色环的起始位置看起来是随机的（用户 2026-09-10：「当只有 5 个时，
       好像一开始的位置是随机，要不直接定位在中间那一个」）。没手动选过色就
       落到正中那一颗——五只壳时是第三只，任何数量都是 floor(n/2)。
       选过之后 colorManual 为真，这里不再动它，回退再进仍是用户选的那个。 */
    if (openOnMiddle(pool)) paintAlt();
    ringPos = Math.max(0, pool.indexOf(state.color));
    paintCaseRing();
    placeCaseRing();
    markRingEnds();
  }
  /* a finite arc has to admit where it ends — same contract as the stand rail */
  function markRingEnds() {
    var open = ringCyclic(), n = ringTotal(), r = Math.round(ringPos);
    var p = $('ringPrev'), x = $('ringNext');
    if (p) p.classList.toggle('mfs-off', !open && r <= 0);
    if (x) x.classList.toggle('mfs-off', !open && r >= n - 1);
  }
  /* colour and size are independent of position — repaint without re-laying out */
  function paintCaseRing() {
    var pool = ringPool(), ph = bodyRec(curModel(), curBody()), m = find(MODELS, DEF.m), z = Z();
    var kids = ringEl.children;
    var w = Math.round(m.w * z);
    /* the seat's canvas: the stage square on desk (the centre seat IS the worn
       case there), a deck-band thumbnail on the phone build */
    var seat = ringGeo().side;
    for (var i = 0; i < kids.length; i++) {
      var el = kids[i].firstChild; if (!el) continue;
      /* a delivered layer wins: the orbit shows the real leather, and its cutout
         is genuinely transparent so the stage shows through the aperture */
      var lay = caseLayer(state.caseType, curModel(), pool[i]);
      /* the phone build's seats are deck-band thumbnails, not the worn case;
         on desk each seat is a full stage square, and at DPR 1 that is a 720
         copy rather than the 1080 original — thirteen seats is 26MB of bitmap
         instead of 58 */
      if (lay) lay = atSize(lay, isMob() ? THUMB_PX : fitSize(seat));
      if (lay) {
        el.classList.add('mfs-layered');
        el.style.width = (COREG ? seat : w) + 'px';
        el.style.height = (COREG ? seat : Math.round(w / layerAR(lay))) + 'px';
        var slot = el.querySelector('.mfs-lay-slot');
        if (!slot) {
          el.innerHTML = '<div class="mfs-lay-slot"></div><div class="mfs-shadow"></div>';
          slot = el.firstChild;
        }
        swapLayer(slot, 'mfs-lay-case', lay, 'left:0;top:0;width:100%');
        continue;
      }
      /* deviation #27 tightened: no vector ring case, no block — empty frame */
      el.classList.add('mfs-layered');
      el.style.width = (COREG ? seat : w) + 'px';
      el.style.height = (COREG ? seat : Math.round(m.h * z)) + 'px';
      var slotR = el.querySelector('.mfs-lay-slot');
      if (!slotR) { el.innerHTML = '<div class="mfs-lay-slot"></div><div class="mfs-shadow"></div>'; slotR = el.firstChild; }
      dropLayer(slotR, 'mfs-lay-case');
    }
  }
  var ringSqueeze = 1;   /* 1 = laid out · 0 = gathered behind the phone */
  function placeCaseRing() {
    var g = ringGeo(), R = g.R, kids = ringEl.children, n = kids.length, cyc = ringCyclic();
    for (var i = 0; i < n; i++) {
      /* wrap to the shortest way round: an orbit has no ends, and a finite one left
         half the frame empty whenever the selection sat at either extreme */
      var k = i - ringPos;
      /* only a looping pool wraps; an arc keeps its ends where they are, so the
         frame simply empties on the side you have run out of colours */
      if (cyc) {
        while (k > n / 2) k -= n;
        while (k <= -n / 2) k += n;
      }
      k *= ringSqueeze;
      var th = k * g.step * Math.PI / 180, ak = Math.abs(k);
      var x, y, sc, op, rot;
      if (g.linear) {
        /* Design 1147:3471: a row of overlapping case thumbnails — horizontal, no
           tilt, no shrink, no fade, with the chosen one dead centre. The seats at
           either end are CROPPED by the board, not faded out, which is why there is
           no falloff here: a strip that fades reads as "that is all of them", and it
           is not. Drag, release-snap, roll and commit all read ringPos only, so none
           of them care which formula draws the seats. */
        x = k * g.pitch; y = g.cy; sc = 1; rot = 0;
        op = 1;
      } else {
        x = R * Math.sin(th); y = g.cy + R * (1 - Math.cos(th));
        sc = Math.max(0.6, 1 - 0.08 * ak);
        rot = k * g.step;
        /* opaque through the centre: the centre item IS the case the phone wears
           (the hero underneath is bare), so it must arrive solid, not dissolve */
        op = ak < 1 ? 1 : Math.max(0, 1 - g.fall * (ak - 1));
      }
      var el = kids[i];
      el.style.transform = 'translate(-50%,-50%) translate(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px)'
                         + ' rotate(' + rot.toFixed(1) + 'deg) scale(' + sc.toFixed(3) + ')';
      /* the phone build's seats are thumbnails beside the device rather than the
         case on it, so the seated one has to say so for itself */
      el.classList.toggle('mfs-seat-on', ak < 0.5);
      el.style.opacity = op.toFixed(3);
      el.style.zIndex = String(100 - Math.round(ak * 10));
      /* COREG: the button's own box is the transparent 1080 square and must never
         take hits — eligibility moves to a class that arms the content-sized
         .mfs-hit child instead (centre item stays inert: the stage drags there) */
      /* Every seat in the linear strip is solid, so hit-eligibility has to go by
         distance here (the dial goes by opacity) */
      var live = (g.linear ? ak < 4.5 : op > 0.25) && (!COREG || ak > 0.5);
      if (COREG) { el.style.pointerEvents = 'none'; el.classList.toggle('mfs-live', live); }
      else el.style.pointerEvents = live ? 'auto' : 'none';
    }
  }
  /* glide to an index, then commit the colour once it lands. On a looping pool the
     target is the nearest equivalent of i around the ring, so stepping past the
     last colour rolls into the first; on an arc it simply stops at the end. */
  function ringGoTo(i, instant) {
    var n = ringTotal();
    if (ringCyclic()) {
      while (i - ringPos > n / 2) i -= n;
      while (i - ringPos <= -n / 2) i += n;
    } else {
      i = Math.max(0, Math.min(n - 1, i));
    }
    cancelAnimationFrame(ringAnim);
    if (instant || reduced) { ringPos = i; placeCaseRing(); ringCommit(); return; }
    var from = ringPos, t0 = 0;
    function step(t) {
      if (!t0) t0 = t;
      var p = Math.min(1, (t - t0) / 420), e = 1 - Math.pow(1 - p, 3);
      ringPos = from + (i - from) * e;
      placeCaseRing();
      if (p < 1) ringAnim = requestAnimationFrame(step); else ringCommit();
    }
    ringAnim = requestAnimationFrame(step);
  }
  function ringCommit() {
    var pool = ringPool(), n = ringTotal(), r = Math.round(ringPos);
    var i = ringCyclic() ? ((r % n) + n) % n              /* wrap into range */
                         : Math.max(0, Math.min(n - 1, r));
    ringPos = i; placeCaseRing(); markRingEnds();          /* park on the canonical index */
    var k = pool[i];
    if (!k || k === state.color) return;
    state.color = k;
    state.colorManual = true;        /* 用户自己转到这一颗，别再被「落中间」改掉 */
    renderPhone(); paintAlt(); paintFanCap(k); paintCaseRing();
    /* the phone build's breadcrumb carries the colour itself, as a dot, so a
       colour landing has to re-sync it — desk's crumb only names the case TYPE
       and never needed this call */
    flowSync();
    var chip = fanEl.querySelector('.mfs-chip[data-k="' + k + '"]');
    if (chip) {
      /* mark it, do not selectChip(): that fires the strip's own onChange, and
         the ring has already done everything it would do */
      var was = fanEl.querySelector('.mfs-center'); if (was) was.classList.remove('mfs-center');
      chip.classList.add('mfs-center');
      centerChip(chip);
    }
  }
  function ringShift(d) { ringGoTo(Math.round(ringPos) + d); }

  /* ---- roll: the orbit IS a wheel of fortune — but a wheel is thrown, it does
     not start at full speed. The old quartic ease-out did, and the arithmetic is
     brutal: over two laps it gave the first twenty colours 18–60ms each. Twenty
     of the twenty-four colours the wheel exists to show were literally
     unreadable, and the whole thing landed as one blur and one prize (user,
     2026-09-01).
     Three beats now — wind up, CRUISE, park — with the shares chosen so all
     three meet at the same speed and nothing kicks where they join:
       · 20% of the clock winding up from a standstill
       · 32% at a genuinely constant speed. This is the fix. An eased middle is
         always fastest exactly where the colours are, whereas a flat one hands
         every colour the same ~100ms — a quarter of a second's read each, which
         is what turns a blur back into a parade.
       · 48% parking, the last colour taking ~0.6s to arrive.
     Cruise speed falls out of the shares (V = 1 / (A/2 + B + C/(K+1))), so the
     three numbers are the only knobs. */
  var ROLL_A = 0.20, ROLL_B = 0.32, ROLL_K = 2;      /* wind-up · cruise · park curve */
  var ROLL_C = 1 - ROLL_A - ROLL_B;
  var ROLL_V = 1 / (ROLL_A / 2 + ROLL_B + ROLL_C / (ROLL_K + 1));
  function rollEase(p) {
    if (p < ROLL_A) return ROLL_V * p * p / (2 * ROLL_A);          /* v: 0 → V */
    var wound = ROLL_V * ROLL_A / 2;
    if (p < ROLL_A + ROLL_B) return wound + ROLL_V * (p - ROLL_A); /* v: V      */
    var r = (p - ROLL_A - ROLL_B) / ROLL_C;                        /* v: V → 0  */
    return wound + ROLL_V * ROLL_B
         + ROLL_V * ROLL_C * (1 - Math.pow(1 - r, ROLL_K + 1)) / (ROLL_K + 1);
  }
  /* ---- and the landing is a fitting, not a stop. The centre item IS the case
     the phone wears, so when the wheel parks the case is pressed home: it lifts
     a hair, comes down past flush, and settles — while the phone gives under it
     for a frame. Same magnet the stand snap plays, at a tenth the amplitude. */
  function ringSnapHome() {
    if (reduced || !('animate' in Element.prototype)) return;
    var el = ringEl.children[Math.round(ringPos)];
    if (el) {
      /* placeCaseRing has just written the resting transform; the scale rides on
         top of it rather than replacing it */
      var base = el.style.transform || '';
      el.animate([
        { transform: base + ' scale(1)',     offset: 0,   easing: 'cubic-bezier(.35,0,.65,.45)' },
        { transform: base + ' scale(1.052)', offset: .3,  easing: 'cubic-bezier(.55,0,.25,1)' },
        { transform: base + ' scale(.984)',  offset: .62, easing: 'cubic-bezier(.3,.85,.35,1)' },
        { transform: base + ' scale(1)',     offset: 1 }
      ], { duration: 430 });
    }
    var ph = $('phoneEl');
    if (ph) ph.animate([
      { transform: 'scale(1)', offset: 0 },
      { transform: 'scale(.993)', offset: .4 },
      { transform: 'scale(1)', offset: 1 }
    ], { duration: 360, delay: 90, easing: 'cubic-bezier(.3,.9,.35,1)', composite: 'add' });
  }
  function ringRoll() {
    if (root.getAttribute('data-cs') !== 'paint') return;
    var pool = ringPool();
    if (pool.length < 2) return;
    var cands = pool.filter(function (k) { return k !== state.color && caseLayer(state.caseType, curModel(), k); });
    if (!cands.length) cands = pool.filter(function (k) { return k !== state.color; });
    var i = pool.indexOf(cands[Math.floor(Math.random() * cands.length)]);
    /* an arc cannot be flung: there is nowhere to spin to and no seam to hide
       the laps in, so a short pool just glides to the prize */
    if (!ringCyclic()) { ringGoTo(i); return; }
    /* ONE lap, not two. Two laps is not twice the spectacle, it is the same
       spectacle at half the frame rate: the clock cannot grow to match, so
       every colour gets half the time. A single lap plus the walk to the prize
       is 11–21 colours, which at cruise is ~100ms each. */
    while (i <= ringPos + pool.length) i += pool.length;
    cancelAnimationFrame(ringAnim);
    if (reduced) { ringPos = i; placeCaseRing(); ringCommit(); return; }
    var from = ringPos, t0 = 0;
    /* a longer deck is a longer throw, but not proportionally so — past a point
       the extra laps are all cruise, and cruise is the part nobody reads */
    var dur = Math.round(1700 + Math.min(700, Math.abs(i - from) * 26));
    function step(t) {
      if (!t0) t0 = t;
      var p = Math.min(1, (t - t0) / dur);
      ringPos = from + (i - from) * rollEase(p);
      placeCaseRing();
      if (p < 1) ringAnim = requestAnimationFrame(step);
      else { ringCommit(); ringSnapHome(); }
    }
    ringAnim = requestAnimationFrame(step);
  }

  /* ---- entrance: the cases sweep in from the lower right along the orbit ----
     Not a fade: the whole ring starts rotated +3.2 steps (~70° — everything off
     to the lower right, mostly transparent by the distance falloff) and glides
     back to its resting position, so each case arrives along the same arc it
     will later travel when picked. */
  function ringEnter() {
    if (reduced) { placeCaseRing(); return; }
    ringSqueeze = 1;
    var target = ringPos;
    cancelAnimationFrame(ringAnim);
    ringPos = target + 3.2;
    placeCaseRing();
    var from = ringPos, t0 = 0;
    (function step(t) {
      if (!t0) t0 = t;
      var p = Math.min(1, (t - t0) / 560), e = 1 - Math.pow(1 - p, 3);
      ringPos = from + (target - from) * e;
      placeCaseRing();
      if (p < 1) ringAnim = requestAnimationFrame(step);
      else { ringPos = target; placeCaseRing(); }
    })(0);
  }

  /* ---- exit: the unpicked colours gather along the ring into the chosen one ----
     Squeezing every angle toward zero slides each case along the orbit to the
     top, where the distance-based opacity has already faded it out — so the
     gather and the vanish are one motion, not two effects. */
  function ringCollapse(then) {
    if (reduced || !ringEl.children.length) { if (then) then(); return; }
    cancelAnimationFrame(ringAnim);
    var t0 = 0, landed = false;
    function arrive() {
      if (landed) return;
      landed = true;
      cancelAnimationFrame(ringAnim);
      ringSqueeze = 1; placeCaseRing();
      if (then) then();
    }
    /* This call gates a NAVIGATION (the stand step follows the gather), and rAF
       stops in a background tab — the house rule is that a decision never hangs
       off a frame callback. The animation still drives the gather; the clock
       guarantees the arrival. Both builds go through here now: the phone's dial
       gathers the same way, in seat space. */
    setTimeout(arrive, 520);
    (function step(t) {
      if (landed) return;
      if (!t0) t0 = t;
      var p = Math.min(1, (t - t0) / 380);
      ringSqueeze = 1 - p * p;             /* ease-in: slow start, quick gather */
      placeCaseRing();
      if (p < 1) ringAnim = requestAnimationFrame(step);
      else arrive();
    })(0);
  }

  ringEl.addEventListener('click', function (e) {
    if (ringMoved) return;
    var it = e.target.closest ? e.target.closest('.mfs-ring-item') : null;
    if (it) ringGoTo(+it.getAttribute('data-i'));
  });
  $('ringPrev').addEventListener('click', function () { ringShift(-1); });
  $('ringNext').addEventListener('click', function () { ringShift(1); });

  /* drag the orbit: one full RING_STEP per ~130px of travel. On the phone build
     one seat is one PITCH of travel instead — the seats are right under the
     thumb there, so anything but 1:1 with the finger reads as slip. */
  function ringDragPx() { return isMob() ? dialPitchPx() : 130; }
  /* One seat per PITCH of travel, and a throw carries. Only the glide in flight
     is cancelled, and only once the drag is real: a plain tap used to cancel it
     from down() and then have nothing to park it with, which is how the orbit
     ended up stranded between colours (deviation #16). */
  /* 60ms of coast, not 200: a deliberate one-pitch swipe travels 1.0 seats and a
     hand moving at ~0.7px/ms would then be handed another 1.3 - it rounded to
     two colours for a gesture that asked for one (measured on desk: a 140px drag
     at 0.35px/ms jumped MCGY -> TPGY). At 60ms the same swipe lands on the
     neighbour and only a real throw (1.2px/ms and up) crosses two or more. */
  var FLING_MS = 60;       /* how long a throw is allowed to coast, in seats */
  var FLING_MAX = 5;       /* and the most seats it may cross */
  hSwipe(swipeSurf, {
    can: function () {
      return root.getAttribute('data-ph') === 't1' && root.getAttribute('data-cs') === 'paint';
    },
    down: function () { ringMoved = false; },
    start: function () { cancelAnimationFrame(ringAnim); ringFrom = ringPos; ringMoved = true; },
    move: function (dx) {
      var at = ringFrom - dx / ringDragPx();                  /* no clamp: the orbit loops */
      /* an arc has ends, so dragging past one gives a little rubber band and
         springs back on release rather than running off into empty seats */
      if (!ringCyclic()) at = Math.max(-0.6, Math.min(ringTotal() - 1 + 0.6, at));
      ringPos = at;
      placeCaseRing();
    },
    end: function (dx, v) {
      var at = ringPos;
      if (Math.abs(v) > FLICK_V) {
        var seats = -v * FLING_MS / ringDragPx();
        at += Math.max(-FLING_MAX, Math.min(FLING_MAX, seats));
      }
      ringGoTo(Math.round(at));
    }
  });

  function buildFan() {
    fanEl.innerHTML = '<span class="mfs-sp"></span>';
    var pool = caseColors(curModel(), curBody());
    var wrapF = fanEl.closest ? fanEl.closest('.mfs-qwrap') : fanEl.parentNode;
    if (wrapF) wrapF.classList.toggle('mfs-paged', pool.length > 12);
    if (pool.indexOf(state.color) < 0) { state.color = nearestIn(state.color, pool) || pool[0]; renderPhone(); }
    openOnMiddle(pool);
    /* after the clamp, not before: the clamp can move state.color, and painting the
       caption first left it naming a colour the phone was no longer wearing */
    paintFanCap(state.color);
    pool.map(function (k) { return find(MOVAS, k); }).forEach(function (c) {
      var b = document.createElement('button');
      b.className = 'mfs-chip mfs-item' + (state.color === c.k ? ' mfs-center' : '');
      b.setAttribute('data-k', c.k);
      b.tabIndex = -1; /* Tab focus would auto-scroll the band and hijack the selection */
      b.innerHTML = '<i style="background:' + swatchBg(c) + '"></i><span>' + c.n + '</span>';
      b.addEventListener('click', function () { centerChip(b); selectChip(fanEl, b); });
      fanEl.appendChild(b);
    });
    fanEl.appendChild(spacer());
    sizeSpacers(fanEl);
    var cur = fanEl.querySelector('.mfs-chip[data-k="' + state.color + '"]');
    if (cur) centerChip(cur, true);
  }
  function spacer() { var s = document.createElement('span'); s.className = 'mfs-sp'; return s; }
  function sizeSpacers(sc) {
    var first = sc.querySelector('.mfs-item');
    if (!first) return;
    if (!sc.clientWidth || !first.offsetWidth) { requestAnimationFrame(function () { sizeSpacers(sc); }); return; }
    var w = Math.max(0, (sc.clientWidth - first.offsetWidth) / 2);
    var moved = false;
    Array.prototype.forEach.call(sc.querySelectorAll('.mfs-sp'), function (s) {
      if (s.style.width !== w + 'px') { s.style.width = w + 'px'; moved = true; }
    });
    /* The end padding IS this strip's coordinate system: changing it slides every
       chip under the centre line, and the strip reads its selection back off that
       line. So whatever is seated has to be put back under it in the same pass —
       otherwise the reflow arrives as a pick nobody made. This runs late by
       design (setPhase re-sizes on the next frame, once the phase is measurable),
       which is exactly when it would otherwise do damage. */
    if (moved) {
      var seated = sc.querySelector('.mfs-center');
      if (seated) centerChip(seated, true);
    }
  }
  /* scrollLeft, not scrollIntoView: the latter walks up the tree and scrolls the
     .phone-frame (which is scrollable because the closed sheets overhang it),
     dragging the whole mockup out of view */
  /* ---- a scroll we started is not a pick ----
     The strip reads its selection back off its own scroll position, so every
     programmatic centring has to be told apart from a thumb. That used to be a
     300ms timer, and a timer cannot do it: the read-back runs on rAF, and rAF is
     late whenever the frame is (a heavy decode on a phone, a backgrounded tab).
     When the timer wins the race the strip reads its own scroll as a choice —
     measured 2026-09-02: the basket named Peony while state.standColor had moved
     to Light Pink, which is the colour checkout would have added. So the marker
     is the chip we are travelling TO, and it is cleared by ARRIVING. */
  function centerChip(el, instant) {
    var sc = el.parentNode;
    var left = Math.max(0, el.offsetLeft - (sc.clientWidth - el.offsetWidth) / 2);
    sc.__mfsSeek = el.getAttribute('data-k');
    /* backstop for a seek that never arrives (an interrupted smooth scroll): its
       failure mode is the honest one — a real pick is heard again */
    clearTimeout(sc.__mfsSeekT);
    sc.__mfsSeekT = setTimeout(function () { sc.__mfsSeek = null; }, 1400);
    if (instant || reduced || !sc.scrollTo) { sc.scrollLeft = left; return; }
    sc.scrollTo({ left: left, behavior: 'smooth' });
  }
  function selectChip(sc, el) {
    if (!el) return;
    var old = sc.querySelector('.mfs-center'); if (old) old.classList.remove('mfs-center');
    el.classList.add('mfs-center');
    if (sc.__onCenter) sc.__onCenter(el.getAttribute('data-k'));
  }
  function watchCenter(sc, onChange) {
    var raf = 0;
    sc.__onCenter = onChange;
    sc.addEventListener('scroll', function () {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        /* A strip that is not on screen cannot be read: on the phone build the
           case strip is hidden (the dial picks), every chip measures 0×0, and the
           nearest-to-centre search returns the FIRST chip. A programmatic centring
           then read back as that chip and, flagged as our own seek, was written
           to state.color — the dial showed navy, the caption said Misty Cove, and
           the cart would have added Misty Cove (client, NO.250). */
        if (!sc.clientWidth) return;
        var mid = sc.getBoundingClientRect().left + sc.clientWidth / 2;
        var best = null, bd = 1e9;
        var items = sc.querySelectorAll('.mfs-item');
        for (var i = 0; i < items.length; i++) {
          var r = items[i].getBoundingClientRect();
          var d = Math.abs(r.left + r.width / 2 - mid);
          if (d < bd) { bd = d; best = items[i]; }
        }
        if (!best || best.classList.contains('mfs-center')) return;
        var old = sc.querySelector('.mfs-center'); if (old) old.classList.remove('mfs-center');
        best.classList.add('mfs-center');
        var k = best.getAttribute('data-k'), ours = !!sc.__mfsSeek;
        if (k === sc.__mfsSeek) { sc.__mfsSeek = null; clearTimeout(sc.__mfsSeekT); }
        onChange(k, ours);
      });
    }, { passive: true });
  }
  watchCenter(fanEl, function (k, ours) {
    if (ours) { state.color = k; state.colorManual = true; paintFanCap(k); return; }
    state.color = k;
    state.colorManual = true;        /* 色条停在哪一格也是用户选的 */
    paintFanCap(k);
    if (!state.standManual) state.standColor = pickLook(k, standColors(state.fam), 'tonal');
    renderPhone(); renderStand();
    micro('');            /* as above: the colour dial no longer writes that line */
  });

  /* ================= tune 2: snap =================
     the product line is chosen on the phone — the row of stands beside it turns
     into the four lines, so sliding it sideways swaps product. Down here there is
     only colour left. */
  function enterSnap() {
    idleWarm(lookahead);
    setStep('standPick');
    renderPhone();               /* back from the wheel: the hero wears its case again */
    if (!state.standPicked && !state.standManual) state.fam = FAMS[0].k;
    famRailSync();
    readDeck();
    renderStand();
    renderStandStep();
    buildStandFan();
    micro('');
    railEnter();
  }
  /* pick → stand → snap: the magnet is the product's one trick, so it is played */
  var flyGhost = null, flyHidden = null;
  /* liftCentre hides the slot it cloned, and this is what gives it back. It used
     to rely on layoutRail's `innerHTML =` wiping the whole row on the way back
     in — once that rebuild became conditional (the version-switch cross-fade),
     the inline visibility survived and the seat stayed blank. Own the state
     instead of depending on someone else's side effect. */
  function clearFly() {
    if (flyGhost) { flyGhost.remove(); flyGhost = null; }
    if (flyHidden) { flyHidden.style.visibility = ''; flyHidden = null; }
  }
  /* The centred stand is lifted out of the row before the row leaves. It is the
     one thing on stage that must not blink: the promise of this step is that
     THIS stand goes onto the phone, so it stays on screen, at its own size,
     from the click through to the snap. The clone carries the delivered artwork
     itself — no re-render, so no seam. */
  function liftCentre() {
    clearFly();
    var slot = trackEl.children[railIdx];
    var art = slot && slot.querySelector('.mfs-stand-bmp');
    if (!art) return null;
    /* the ghost is a clone, so whatever is missing here is missing for the whole
       flight — force the picture in if the window has not got to it yet */
    if (!art.firstChild && art.getAttribute('data-lay')) mountSlotArt(art, art.getAttribute('data-lay'));
    var r = art.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    var g = document.createElement('div');
    g.className = 'mfs-fly';
    g.style.cssText = 'left:' + r.left + 'px;top:' + r.top + 'px;width:' + r.width
      + 'px;height:' + r.height + 'px;perspective:760px';
    var c = art.cloneNode(true);
    /* the rail box centres itself on the slot; inside the ghost it just fills it */
    c.style.cssText = 'position:absolute;left:0;top:0;width:100%;height:100%;transform:none';
    g.appendChild(c);
    root.appendChild(g);
    slot.style.visibility = 'hidden';
    flyHidden = slot;
    flyGhost = g;
    return g;
  }
  function chooseStand(i) {
    if (!DECK || root.classList.contains('mfs-flying')) return;
    var d = deck[((i % DECK) + DECK) % DECK];
    /* The pick board's row wears the default white (2026-09-04) — display art,
       not a choice. Taking d.color into state would land the colour board on white
       and swallow both the user's earlier pick and the "follow the case" tonal
       suggestion. So the colour is worked out here: keep a manual pick, otherwise
       re-derive the suggestion from the case colour (the same rule famDeck's
       non-pick branch uses). */
    var pool = standColors(d.fam);
    var pickedColor = modelPicking()
      ? (state.standManual ? (nearestIn(state.standColor, pool) || pool[0])
                           : pickLook(state.color, pool, 'tonal'))
      : d.color;
    state.fam = d.fam; state.standColor = pickedColor; state.standPicked = true;
    /* the fly-in reads this same d, so its colour has to be the real one too */
    d = { fam: d.fam, color: pickedColor };
    var lifted = liftCentre();
    /* the cards clear the stage first, then the stand comes in for the ring */
    var slots = trackEl.children;
    for (var s = 0; s < slots.length; s++) {
      if (lifted && s === railIdx) continue;   /* the ghost holds that slot now */
      slots[s].classList.remove('mfs-in', 'mfs-inR');
      /* a sweep, not a radiation: everything is leaving the same way, so the
         delay follows the row's order and the whole line reads as one gesture */
      slots[s].style.animationDelay = (Math.max(0, s - railIdx + 3) * 0.045) + 's';
      void slots[s].offsetWidth;
      slots[s].classList.add('mfs-out');
    }
    root.classList.add('mfs-flying');
    setTimeout(function () { root.setAttribute('data-sn', 'color'); }, 300);
    setTimeout(function () { startFly(d); }, 420);
  }
  function startFly(d) {
    flyStand(d.fam, d.color, function () {
      root.classList.remove('mfs-flying');
      renderStand();
      var rig = $('rig');
      rig.classList.remove('mfs-snap-pop'); void rig.offsetWidth; rig.classList.add('mfs-snap-pop');
      setTimeout(function () { rig.classList.remove('mfs-snap-pop'); }, 480);
      enterPaint();
    });
  }
  /* The snap is not an arrival from off-stage. While the row is standing there
     the centred stand IS the one on the phone's back, so posting a shrunken
     copy in from the page edge read as a different, smaller product being
     delivered (user, 2026-09-01). What the magnet should read as: the stand
     already on show lifts a few millimetres off the case, hangs for a beat, and
     is pulled flat. Both ends of the travel are measured off live boxes — the
     lifted clone and the settled rig — so nothing here has to be kept in step
     with the shared-canvas maths by hand. */
  function flyStand(fk, colorKey, done) {
    var g = flyGhost;
    renderStand();                       /* settle the target; mfs-flying keeps it hidden */
    if (!g || reduced || !('animate' in Element.prototype)) { clearFly(); done(); return; }
    var from = g.getBoundingClientRect(), to = $('standEl').getBoundingClientRect();
    if (!from.width || !to.width) { clearFly(); done(); return; }
    var dx = (to.left + to.width / 2) - (from.left + from.width / 2);
    var dy = (to.top + to.height / 2) - (from.top + from.height / 2);
    /* the shared square is sized off the phone box, and the phone grows on the
       way into paint — so the ghost has to arrive at the rig's scale, not its own */
    var sc = to.width / from.width;
    var lift = Math.round(from.width * 0.055);
    function at(p, ox, oy, s, rot) {
      return 'translate(' + (dx * p + ox).toFixed(1) + 'px,' + (dy * p + oy).toFixed(1) + 'px)'
           + ' scale(' + ((1 + (sc - 1) * p) * s).toFixed(4) + ') rotate(' + rot + 'deg)';
    }
    g.animate([
      /* on the case, exactly where the row left it */
      { transform: at(0, 0, 0, 1, 0), easing: 'cubic-bezier(.3,0,.62,.35)', offset: 0 },
      /* held off it: almost no ground covered, all of it hang time */
      { transform: at(.12, lift, -lift * 1.35, 1.05, 2.2), easing: 'cubic-bezier(.66,0,.16,1)', offset: .36 },
      /* the magnet takes it — the rest of the distance in a third of the time,
         landing a hair past flat */
      { transform: at(1, 0, 0, .982, -.5), easing: 'cubic-bezier(.24,.9,.36,1)', offset: .82 },
      { transform: at(1, 0, 0, 1, 0), offset: 1 }
    ], { duration: 540, fill: 'forwards' });
    setTimeout(function () { clearFly(); done(); }, 520);
  }
  function enterPaint() {
    setStep('standPaint');
    buildStandFan();
    micro(famLine());
    idleWarm(lookahead);
  }
  $('fbSnap').addEventListener('click', function () {
    if (state.standPicked) { state.standPicked = false; clearFly(); root.classList.remove('mfs-flying'); }
    enterSnap();
  });
  $('fbPaint').addEventListener('click', function () {
    /* Two-stage back. The Case crumb covers two boards (pick 02, paint 03), and
       the board keeps no other way back to the type choice — so from elsewhere
       it lands on the finished paint view (colour kept), and a second press,
       already standing on paint, steps the rest of the way back to the pick. */
    var onPaint = root.getAttribute('data-ph') === 't1'
               && root.getAttribute('data-cs') === 'paint';
    /* With a single case type there is no pick board to step back to (enterCase
       skips it), so a second press while standing on paint has nowhere further
       back to go — stay put rather than spin in place. */
    if (onPaint) { if (caseTypeCount() > 1) enterCase(); return; }
    if (root.getAttribute('data-cs') === 'paint') {
      setStep('casePaint'); renderPhone(); buildFan(); buildCaseRing(); ringEnter(); micro('');
    } else enterCase();
  });
  $('toSnapBtn').addEventListener('click', function () {
    /* the unchosen colours fold into the chosen one before the stand row enters.
       Both builds have a ring now — the phone's is the dial — and ringCollapse
       works in seat space, so it is the same gather in either geometry. */
    ringCollapse(enterSnap);
  });
  $('chooseStandBtn').addEventListener('click', function () { chooseStand(railIdx); });

  /* product info: tap to open, tap anywhere (or Esc) to close */
  function closeInfo() {
    var o = root.querySelector('.mfs-infowrap.mfs-open');
    root.classList.remove('mfs-info-on');
    if (!o) return;
    var pop = o.querySelector('.mfs-infopop');
    if (pop && !reduced && 'animate' in Element.prototype && root.classList.contains('mfs-desk')) {
      /* fold shut from the middle — the reverse of how it opened */
      var a = pop.animate(
        [{ transform: 'scaleY(1)', opacity: 1 }, { transform: 'scaleY(0)', opacity: 0 }],
        { duration: 240, easing: 'cubic-bezier(.5,0,.85,.4)' });
      a.onfinish = function () { o.classList.remove('mfs-open'); };
      setTimeout(function () { o.classList.remove('mfs-open'); }, 300);   /* belt for frozen clocks */
    } else {
      o.classList.remove('mfs-open');
    }
  }
  function openInfoPop(wrap) {
    wrap.classList.add('mfs-open');
    var pop = wrap.querySelector('.mfs-infopop');
    if (pop && root.classList.contains('mfs-desk')) {
      /* Board 571:1332: the box owns the blurb column and is centred on the
         stage midline — the same line the phone hangs on. Measured, not
         inherited: the blurb's own left edge, and 328px under the header. */
      var mic = root.querySelector('.mfs-micro');
      var st = $('phStudio').getBoundingClientRect();
      var mx = mic ? mic.getBoundingClientRect().left : st.left + st.width * 0.657;
      pop.style.left = Math.round(mx) + 'px';
      /* final geometry is arithmetic, not a transform an animation can drop:
         measure the box (it is visible now that .open is on) and centre it on
         the 328 stage line by hand */
      pop.style.top = Math.round(st.top + 328 - pop.offsetHeight / 2) + 'px';
    }
    if (pop && !reduced && 'animate' in Element.prototype && root.classList.contains('mfs-desk')) {
      pop.animate(
        [{ transform: 'scaleY(0)', opacity: 0 }, { transform: 'scaleY(1)', opacity: 1 }],
        { duration: 320, easing: 'cubic-bezier(.16,.86,.22,1)' });
    }
  }
  /* Hover opens the box (Kay: the + hid the info too deep); leaving closes it
     after a beat unless the pointer re-enters — the pop is a DOM child of the
     wrap, so hovering the box itself keeps it open even though it is positioned
     fixed. Click still toggles, for touch and for pinning. */
  var infoLeaveT = 0;
  on(document, 'mouseover', function (e) {
    if (!root.classList.contains('mfs-desk')) return;
    var wrap = e.target.closest ? e.target.closest('.mfs-infowrap') : null;
    if (!wrap) return;
    clearTimeout(infoLeaveT);
    if (!wrap.classList.contains('mfs-open')) openInfoPop(wrap);
  });
  on(document, 'mouseout', function (e) {
    if (!root.classList.contains('mfs-desk')) return;
    var wrap = e.target.closest ? e.target.closest('.mfs-infowrap') : null;
    if (!wrap) return;
    var to = e.relatedTarget;
    if (to && wrap.contains(to)) return;         /* still inside wrap or box */
    clearTimeout(infoLeaveT);
    infoLeaveT = setTimeout(closeInfo, 160);
  });

  on(document, 'click', function (e) {
    /* the box now carries its own ✕ (Figma 1:285); it sits inside .infopop, which the
       outside-click branch below deliberately ignores, so it needs handling first */
    if (e.target.closest && e.target.closest('.mfs-ip-x')) {
      e.preventDefault(); e.stopPropagation(); closeInfo(); return;
    }
    var btn = e.target.closest ? e.target.closest('.mfs-infobtn') : null;
    if (btn) {
      e.preventDefault(); e.stopPropagation();
      var wrap = btn.parentNode, was = wrap.classList.contains('mfs-open');
      closeInfo();
      if (!was) {
        openInfoPop(wrap);
        if (!root.classList.contains('mfs-desk')) {
          var fr = root.querySelector('.mfs-phone-frame').getBoundingClientRect();
          var pop = wrap.querySelector('.mfs-infopop');
          if (pop) {
            pop.style.left = Math.round(fr.left + fr.width / 2) + 'px';
            pop.style.bottom = Math.round(window.innerHeight - fr.bottom + 12) + 'px';
            pop.style.width = Math.min(fr.width - 24, 380) + 'px';
          }
          var sc = root.querySelector('.mfs-info-scrim');
          if (sc) { sc.style.inset = 'auto'; sc.style.left = fr.left + 'px'; sc.style.top = fr.top + 'px'; sc.style.width = fr.width + 'px'; sc.style.height = fr.height + 'px'; }
          requestAnimationFrame(function () { root.classList.add('mfs-info-on'); });
        }
      }
      return;
    }
    if (!(e.target.closest && e.target.closest('.mfs-infopop'))) closeInfo();
  });
  on(document, 'keydown', function (e) { if (e.key === 'Escape') closeInfo(); });
  $('fbColor').addEventListener('click', function () {
    if (state.standPicked) enterPaint(); else enterSnap();
  });

  /* ================= product sheet (phone build) =================
     Desk spreads the product's story over three surfaces: the angle card on the
     left of the stage, the blurb column on its right, and a hover box over that.
     A phone column has none of the three, and no hover to open anything with. So
     they become one sheet, reachable from the two things on screen that stand for
     the product — the angle PiP in the stand step and the ⓘ pill in the paint
     step. It also prints m[2] of each posture, which the desk tiles have never
     had room for. */
  function openProdSheet() {
    var m = $('prodModal');
    if (!m) return;
    var f = find(FAMS, state.fam), vi = (state.ver || {})[f.k] || 0;
    /* no colour on the stand-pick board (NO.264): nothing has been painted yet,
       and the name there was the attract row's leftover */
    var col = modelPicking() ? null : find(MOVAS, state.standColor);
    $('psName').textContent = f.n;
    $('psVer').textContent = [f.vers ? f.vers[vi] : '', col ? col.n : ''].filter(Boolean).join(' · ');
    /* same pair as the desk box — one product must not show two different sets
       of photos depending on which build you opened it from. The angle art stays
       as the fallback for colourways the gallery batch has not reached. */
    var shots = sheetShots(f, vi), shEl = $('psShots');
    shEl.innerHTML = shots.map(function (u, i) {
      return '<span class="mfs-ps-shot"><b class="mfs-ps-num">' + (i + 1) + '</b>'
           + '<img src="' + u + '" alt=""></span>';
    }).join('');
    shEl.hidden = !shots.length;
    /* no photography for this colourway = no strip. The CSS posture tiles that
       used to fill the gap are gone from both builds (user, 2026-09-10) — the
       sheet is then name, version and blurb. */
    /* same rule as the desk info box: this sheet's header names the version, so
       the blurb under it is the version's line, not the product's */
    $('psLong').textContent = verWhat(f) || f.long || '';
    m.classList.add('mfs-open');
    settleModal(m, true);
    mobSheets();
  }
  function closeProdSheet() {
    var m = $('prodModal');
    if (!m || !m.classList.contains('mfs-open')) return;
    m.classList.remove('mfs-open');
    settleModal(m, false);
    mobSheets();
  }
  if ($('mobInfo')) $('mobInfo').addEventListener('click', openProdSheet);
  if ($('psClose')) $('psClose').addEventListener('click', closeProdSheet);
  if ($('prodModal')) $('prodModal').addEventListener('click', function (e) {
    if (e.target === this) closeProdSheet();
  });

  /* ================= stand color fan (slide with live feedback) ================= */
  var standFanEl = $('standFan');
  function paintStandCap(k) {
    var pool = standColors(state.fam);
    var rec = pickLook(state.color, pool, 'tonal') === k;
    var c = find(MOVAS, k);
    $('wheelCap').textContent = (c ? c.n.toUpperCase() : '') + (rec ? ' · MATCHED TO YOUR PAINT' : '');
  }
  function buildStandFan() {
    standFanEl.innerHTML = '<span class="mfs-sp"></span>';
    /* per-version pool (the version axis of deviation #13): a colour the
       picked version isn't sold in must not be offered — it used to borrow
       another version's photo and land the wrong SKU in the cart */
    var svi = (state.ver || {})[state.fam] || 0;
    var pool = standColors(state.fam).filter(function (k) {
      return !!standVariant(state.fam, k, svi);
    });
    if (!pool.length) pool = standColors(state.fam);
    warmStands(state.fam);   /* every chip in this strip is one tap from the stage */
    var wrap = standFanEl.closest ? standFanEl.closest('.mfs-qwrap') : standFanEl.parentNode;
    if (wrap) wrap.classList.toggle('mfs-paged', pool.length > 12);
    if (pool.indexOf(state.standColor) < 0) { state.standColor = nearestIn(state.standColor, pool) || pool[0]; renderStand(); }
    pool.map(function (k) { return find(MOVAS, k); }).forEach(function (c) {
      var b = document.createElement('button');
      b.className = 'mfs-chip mfs-item' + (state.standColor === c.k ? ' mfs-center' : '');
      b.setAttribute('data-k', c.k);
      b.tabIndex = -1;
      b.innerHTML = '<i style="background:' + swatchBg(c) + '"></i><span>' + c.n + '</span>';
      b.addEventListener('click', function () { centerChip(b); selectChip(standFanEl, b); });
      standFanEl.appendChild(b);
    });
    standFanEl.appendChild(spacer());
    sizeSpacers(standFanEl);
    paintStandCap(state.standColor);
    var cur = standFanEl.querySelector('.mfs-chip[data-k="' + state.standColor + '"]');
    if (cur) centerChip(cur, true);
  }
  /* The colour change is a SNAP of the stand ALREADY on the phone — the stand
     itself moves, no copy is flown in. Two earlier tries and why they were wrong:

     · the original swung a copy up the case ring's hidden arc: 22° out, leaning
       tangentially, brightest half a step from the seat, dissolved on arrival.
       Wrong verb — choosing a stand snaps it to the back (flyStand), landing a
       case colour presses it home (ringSnapHome), only this one floated (user).
     · replacing that with an opaque copy snapped onto the seat put TWO stands on
       screen at once (user, with video). swapLayer keeps the OLD bitmap on stage
       while a cold layer decodes — see its 'cold:' branch — so for those frames
       the seat still wore the previous colour with the new one pressed over it.
       That arc's fade was never decoration: it was cover for exactly this window.

     Animating the rig removes the window instead of covering it. One element, so
     there is nothing to overlap; swapLayer's own cross-fade carries the colour
     across underneath the motion. No rotation either — a mounted part sits flush
     against the case, so it presses, it does not lean. */
  var swAnim = null, swTimer = 0, swAt = 0;

  /* A snap interrupted — by another swatch, by leaving the step — must not leave
     the rig parked mid-press. Cancelling drops it straight back to the transform
     the stylesheet gives it, which is exactly where it belongs.
     (There is no longer any piece in the air to strand: back when a copy flew the
     arc, this also had to clear #swingEl and finish the colour handover.) */
  function swingSettle() {
    clearTimeout(swTimer);
    if (swAnim) { try { swAnim.cancel(); } catch (e) {} swAnim = null; }
  }
  function swingStandIn(colorKey) {
    /* Commit on the spot. Deferring it to a landing made the animation the only
       thing that could write state.standColor, and it lost every race against a
       direct write (the debug pin, a rail recolour) — leaving the strip naming one
       colour and the phone wearing another. */
    state.standColor = colorKey; renderStand(); famDeck();   /* layoutRail carries the row's colours; this only rebuilds the deck data */
    /* t3's title IS the colour now (Figma 571:1332), so the head has to follow
       the strip. Safe on every other board: setBoardHead reads data-ph, and
       pushLine returns early when the line has not actually changed. */
    setBoardHead();
    /* The PiP is per-variant too, and nothing on the colour path was refreshing
       it: it was built when the step opened and then kept whatever colourway it
       was born with (thumbnail said NVBUBHWT while the strip said MCGYSPBU).
       Pre-existing, but it only became visible now that most colourways have a
       real photo instead of the vector placeholder. renderStandStep holds the
       old card until the new frames decode (see mountAngles), so this does not
       flash while the strip is being dragged. */
    renderStandStep();
    syncInfoShots();               /* the shots are delivered per variant: a new colour is a new pair */
    flowSync();                    /* the third crumb's dot is this colour */
    var rig = $('rig');
    if (reduced || !rig || !rig.offsetWidth || !('animate' in Element.prototype)) return;
    /* Dragging the strip fires this once per swatch crossed. Restarting the lift
       on each one made the stand jitter — and read as a double trigger (user).
       Inside a snap already in flight the colour just changes underneath it: the
       magnet is playing, it does not need to be re-struck. */
    var now = (window.performance && performance.now) ? performance.now() : Date.now();
    if (swAnim && now - swAt < 240) return;
    swingSettle();
    swAt = now;
    /* half of flyStand's amplitude: that one lifts a loose piece and may be
       theatrical, this one nudges a part that is already fitted */
    var lift = Math.max(4, Math.round(rig.offsetWidth * 0.028));
    function at(ox, oy, s) {
      /* left:50% + translateX(-50%) is how the rig centres on the phone — the
         magnet rides on top of that, it does not replace it (same shape as the
         .mfs-snap-pop keyframes) */
      return 'translateX(-50%) translate(' + ox.toFixed(1) + 'px,' + oy.toFixed(1) + 'px)'
           + ' scale(' + s.toFixed(4) + ')';
    }
    swAnim = rig.animate([
      /* Always entered from the RIGHT (user, 2026-09-02). It used to lean in
         from whichever side of the strip the swatch sat on, which made the same
         gesture look like two different moves depending on which way you walked
         the colours — and walking back and forth flipped it every time. One
         direction reads as one move: the part comes off the right and presses on. */
      { transform: at(lift * 1.2, -lift, 1.028), easing: 'cubic-bezier(.62,0,.16,1)', offset: 0 },
      /* pulled flat and a touch past it — the click */
      { transform: at(0, 0, 0.988), easing: 'cubic-bezier(.24,.9,.36,1)', offset: .72 },
      { transform: at(0, 0, 1), offset: 1 }
    ], { duration: 420 });
    swTimer = setTimeout(function () { swAnim = null; }, 430);
  }

  watchCenter(standFanEl, function (k, ours) {
    if (ours) { paintStandCap(k); return; }
    state.standManual = true;
    swingStandIn(k);               /* commits state.standColor when it lands */
    paintStandCap(k);
  });


  /* ================= done ================= */
  function renderDone(stamp) {
    /* Desk stamps the combo in caps (571:1413, where it doubles as the poster's
       byline); the phone design (1147:3664) sets it in title case — 24px of caps
       in a 350 card reads as shouting, and the line right under it is already
       the pairing spelled out. */
    $('doneName').textContent = isMob() ? comboWords() : comboWords().toUpperCase();
    /* the panel sub is just the combo (design 571:1413); "A MOFT ORIGINAL"
       stays on the poster, where it is the byline */
    $('doneSub').textContent = comboSubText();
    var cs = find(CASES, state.caseType), fa = find(FAMS, state.fam);
    var cc = find(MOVAS, state.color), sc = find(MOVAS, state.standColor);
    /* Board 571:1422 rows: a 52px tile, the product name over TWO quiet lines
       (colourway, then model/fit), and the price as struck-original + bundle
       price — per row, so the discount reads at the line level and the two rows
       sum to the subtotal exactly.
       The tile is the catalogue photo of the exact variant the SKU resolves to.
       When that variant carries no image of its own the tile falls back to OUR
       OWN LAYER ART for the same variant — the very square the stage was just
       showing. A product-LEVEL photo is still deliberately refused (it would
       put a black case against the word "Sapphire"); the layer is a different
       thing entirely, keyed to the exact SKU, so it can never show the wrong
       colourway. Flat swatch only when neither exists.
       This is not hypothetical: the iPhone 18 MOVAS product ships 26 variants
       with zero variant images (the i17 product has 30/30, i18 Frame 10/10), so
       every i18 MOVAS row was a flat colour block (user, 2026-09-10:
       「商品的图片也没取到」). 52px tile, background-size: contain on white —
       a transparent 1080 square lands in it correctly. */
    var cvar = caseVariant(state.caseType, curModel(), state.color || '');
    var svar = standVariant(state.fam, state.standColor || '', (state.ver || {})[state.fam] || 0);
    /* no set discount configured (save_amount empty or 0 — the schema promises
       "show no discount") — drop the struck prices and the badge instead of
       striking every price through to itself next to "Save $0.00" */
    var saving = saveAmt() > 0;
    $('coList').innerHTML = [
      /* line 1 the colourway, line 2 what it fits — board 571:1439/1453 */
      { n: cs.n, l1: cc.n, l2: find(MODELS, curModel()).n, h: cc.h, h2: cc.h2, p: casePrice(),
        img: (cvar && cvar.img) || layerURL(atSize(caseLayer(state.caseType, curModel(), state.color || ''), THUMB_PX)) },
      { n: fa.n, l1: sc.n, l2: fa.vers ? verName(fa) : 'fits every model', h: sc.h, h2: sc.h2, p: standPrice(fa),
        img: (svar && svar.img) || layerURL(atSize(standLayer(state.fam, state.standColor || ''), THUMB_PX)) }
    ].map(function (r) {
      var tile = r.img
        ? '<i class="mfs-co-ph" style="background-image:url(\'' + String(r.img).replace(/'/g, '%27') + '\')"></i>'
        : '<i style="background:' + swatchBg(r) + '"></i>';
      return '<div class="mfs-co-row">' + tile
           + '<span class="mfs-co-txt"><span class="mfs-co-n">' + r.n + '</span>'
           + '<span class="mfs-co-m">' + r.l1 + '</span>'
           + '<span class="mfs-co-m">' + r.l2 + '</span>'
           + '</span>'
           + '<span class="mfs-co-p">' + (saving ? '<s>' + money(r.p) + '</s>' : '') + '<b>' + money(lineNet(r.p)) + '</b></span>'
           + '</div>';
    }).join('');
    $('finPrice').textContent = money(setPrice());
    $('finWas').textContent = money(fullPrice());
    $('finSave').textContent = saveLabel();
    $('finWas').style.display = saving ? '' : 'none';
    $('finSave').style.display = saving ? '' : 'none';
    /* the price sits right above the button in the total row, so the button
       just names the action (design 571:1413) */
    $('addBtn').textContent = 'Add to cart';
    /* the stamp used to hit .plate-name, which the 571:1422 cart removed — the
       null deref here killed the rest of the finish handler, taking the stand
       unfold AND the confetti with it. The combo name lives in the cart header
       now, so that is what gets the stamp. */
    if (stamp) {
      var pn = root.querySelector('.mfs-cart-head .mfs-ch-t');
      if (pn) { pn.classList.remove('mfs-stamp'); void pn.offsetWidth; pn.classList.add('mfs-stamp'); }
    }
    [60, 320, 700].forEach(function (ms) { setTimeout(alignDonePlate, ms); });
  }
  /* ---- the finale is a scene change, not a phase swap ----
     'done' re-lays the studio: #phStudio turns into a row, the stage gives up
     387px and the basket takes them. Both happened between two frames, so the
     phone teleported left and a card materialised on top of it (user,
     2026-09-01).
     The phone TRAVELS to its new place now, and the move is measured rather
     than guessed: apply the layout, read where the hero landed, invert it back
     to where it was, release (FLIP). Whatever the layout did — the 387px, the
     done board's own top offset, the short-viewport zoom steps — the animation
     is exactly that, on any window.
     The basket follows it in from off-frame on SnapPlay's own drawer motion
     (mf-buy-engine: 440ms · --mf-ease-sheet · translateX(100% + 24px) on desk,
     translateY(100%) on mobile), one beat later. The phone makes room, THEN the
     basket arrives — cause before effect. .mfs-phone-frame is overflow:hidden,
     so off-frame is genuinely off-screen and nothing widens the page. */
  var SHEET_EASE = 'cubic-bezier(.32,.72,0,1)';   /* = --mf-ease-sheet */
  function enterDone() {
    var hero = root.querySelector('.mfs-hero');
    var first = hero ? hero.getBoundingClientRect() : null;
    var still = reduced || !('animate' in Element.prototype);

    setPhase('done');
    renderDone(true);
    /* The basket is on screen now and Checkout is one tap away — take the
       starting cart count here so the click does not have to wait ~400ms for
       it (see primeCartCount). Fire-and-forget: a failure just means the click
       counts it live, which is what it always did. */
    primeCartCount();
    /* Same idea for Share. The poster reads its three layers with
       crossOrigin='anonymous' (the canvas must stay untainted — the files are on
       cdn.shopify.com), and a CORS request is a DIFFERENT cache entry from the
       plain one the stage already fetched: clicking Share re-downloaded all
       three. Warming the CORS copies here is what makes the sheet open with
       pixels already in hand (走查 0905 D12「点击后加载很慢」). */
    warmPosterLayers();
    /* 顺手把海报也画掉。原来它等到点 Share 才开始拉图→合成→编码，那段时间分享
       按钮是 Preparing，用户以为点了没反应（2026-09-10）。done 是终点，配置不会
       再变，所以这里画出来的就是最终那张；等用户看完收据再点，blob 早就备好，
       面板一开即可分享。放进 idle 回调，不跟进场动画抢主线程。 */
    idleWarm(function () { if (root.getAttribute('data-ph') === 'done') drawPoster(); });

    if (!still && hero && first) {
      var last = hero.getBoundingClientRect();        /* forces the reflow we need */
      var dx = first.left - last.left, dy = first.top - last.top;
      var ds = last.width ? first.width / last.width : 1;
      if (Math.abs(dx) > 1 || Math.abs(dy) > 1 || Math.abs(ds - 1) > 0.01) {
        var flip = hero.animate([
          /* the hero's resting transform is translate(-50%,-50%); the inversion
             rides on top of it rather than replacing it */
          { transform: 'translate(-50%,-50%) translate(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px) scale(' + ds.toFixed(4) + ')' },
          { transform: 'translate(-50%,-50%) translate(0,0) scale(1)' }
        ], { duration: 560, easing: SHEET_EASE });
        /* A WAAPI animation PAUSES in a background tab, and this one starts at
           the inverted keyframe — a page backgrounded mid-transition would come
           back with the device parked off-stage. The animation has no fill, so
           cancelling it hands the transform back to the stylesheet; on a wall
           clock, because the finish event is exactly what does not arrive. */
        setTimeout(function () { try { flip.cancel(); } catch (e) {} }, 800);
      }
    }
    var card = root.querySelector('.mfs-z-done');
    if (!still && card)
      card.animate(
        [{ transform: root.classList.contains('mfs-desk') ? 'translateX(calc(100% + 24px))' : 'translateY(100%)' },
         { transform: 'translate(0,0)' }],
        /* fill:backwards keeps it off-frame through the delay — without it the
           card sits at its final place for two frames and then jumps out to
           slide back in */
        { duration: 440, delay: 120, easing: SHEET_EASE, fill: 'backwards' });

    /* the fold and the confetti wait for the room to settle. Both used to fire
       while the phone was still on its way, which is what made the old cut read
       as three unrelated things happening at once. */
    /* both are late enough that the ✕ or Play Again can already have taken the
       board elsewhere — firing confetti over the paint step would be a ghost */
    var onDone = function (fn) {
      return function () { if (root.getAttribute('data-ph') === 'done') fn(); };
    };
    /* (the vector stand's unfold amount --p used to be driven from here. Layer art
     does not unfold, so that line went with its @property declaration) */
    setTimeout(onDone(confetti), still ? 0 : 760);
    micro('');
  }
  $('finishBtn').addEventListener('click', enterDone);
  /* the card's ✕ steps back one — out of checkout, into the paint step */
  $('cartClose').addEventListener('click', function () { enterPaint(); });
  /* ---- checkout (deviation #2, resolved 2026-09-01): real add-to-cart, then
     hand over to Shopify checkout. House rules apply (env facts): the upsell
     app swallows batched {items:[…]} bodies, so items go ONE per request; the
     cart is re-counted via /cart.js (no-store + timestamp — it gets cached)
     before and after, and the count difference is the only truth we trust. */
  function pickedVariants() {
    var out = [], devs = devCandidates(curModel()), i, v = null;
    for (i = 0; i < devs.length && !v; i++) v = CASE_VAR[state.caseType + ':' + devs[i] + ':' + (state.color || '')];
    if (v && v.id) out.push(v);
    var sv = standVariant(state.fam, state.standColor || '', (state.ver || {})[state.fam] || 0);
    if (sv && sv.id) out.push(sv);
    return out;
  }
  /* ---- an unwrapped fetch for cart WRITES ----
     window.fetch here is wrapped by the installed gift app. Measured back to
     back on the same request (2026-09-11): ~3.6s through the wrapper against
     ~0.27s unwrapped, because it runs its rule set inline on every add — and a
     second add moments later resolved with undefined in 1ms, never reaching the
     network. A same-origin iframe hands back a pristine fetch; the frame must
     stay in the document or its contentWindow (and our copy) dies with it.
     Bound to the outer window so the call still carries this page's cookies.
     Reads stay on window.fetch: they are not slow, and leaving them wrapped is
     the only signal the gift app still gets from us. */
  var RAW_FETCH = null;
  function rawFetch() {
    if (RAW_FETCH) return RAW_FETCH;
    var pristine = null;
    try {
      var frame = document.createElement('iframe');
      frame.setAttribute('aria-hidden', 'true');
      frame.tabIndex = -1;
      frame.style.cssText = 'position:absolute;width:0;height:0;border:0;visibility:hidden';
      document.body.appendChild(frame);
      if (frame.contentWindow && typeof frame.contentWindow.fetch === 'function') {
        pristine = frame.contentWindow.fetch.bind(window);
      } else {
        frame.remove();
      }
    } catch (e) { pristine = null; }
    RAW_FETCH = pristine || window.fetch.bind(window);
    return RAW_FETCH;
  }
  function cartCount() {
    return fetch('/cart.js?ts=' + Date.now(), { cache: 'no-store' })
      .then(function (r) { return r.json(); })
      .then(function (c) { return c.item_count; });
  }
  /* ---- the count we start from, fetched off the critical path ----
     Measured on the live preview (2026-09-05): window.fetch, XHR.open and
     XHR.send are all wrapped by installed apps, and one /cart.js round trip
     costs ~400ms through them. Checkout used to spend four of those in series
     before the navigation even began (count, add, add, count) — ~1.6-2s that
     the user reads as "点 check out 跳转到账单页极慢" (走查 E16), on top of
     Shopify's own checkout redirect which we do not control.
     The first count does not depend on the click: the basket is already on
     screen. So it is taken when the done board opens and reused. A stale or
     failed prime falls back to a live count, exactly as before. */
  var cartPrimed = null;
  function primeCartCount() {
    cartPrimed = null;
    cartCount().then(function (n) { cartPrimed = n; }, function () { cartPrimed = null; });
  }
  /* Resolves true only when the server actually answered with a cart payload.
     The upsell app can swallow a request whole (resolves undefined, or never
     settles) — that is what the count check was built to catch, and it still
     does. But a well-formed answer IS proof the line landed, and trusting it
     saves the second /cart.js in the normal case. */
  /* The whole set in one request. Shopify has always accepted {items:[…]};
     it was the wrapper that could not carry it, and we are no longer in it.
     Resolves with how many lines came back, 0 if the batch did not land. */
  function addBatch(ids) {
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    var t = setTimeout(function () { if (ctl) ctl.abort(); }, 6000);
    var body = { items: ids.map(function (id) { return { id: id, quantity: 1 }; }) };
    return rawFetch()('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctl ? ctl.signal : undefined
    })
      .then(function (r) { return (r && r.ok) ? r.json().catch(function () { return null; }) : null; })
      .catch(function () { return null; })
      .then(function (j) {
        clearTimeout(t);
        return (j && j.items && j.items.length) ? j.items.length : 0;
      });
  }
  function addOne(id) {
    var ctl = ('AbortController' in window) ? new AbortController() : null;
    var t = setTimeout(function () { if (ctl) ctl.abort(); }, 6000);
    return rawFetch()('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: id, quantity: 1 }),
      signal: ctl ? ctl.signal : undefined
    })
      .then(function (r) { return (r && r.ok) ? r.json().catch(function () { return null; }) : null; })
      .catch(function () { return null; })
      .then(function (j) {
        clearTimeout(t);
        /* /cart/add.js answers with the line item (single form) */
        return !!(j && (j.id || j.variant_id || (j.items && j.items.length)));
      });
  }
  /* ---- hand over to the theme's own cart drawer ----
     Same two steps as the buy engine on SnapPlay (handOffToThemeCart), and for
     the same reasons. `cart:refresh` makes the legacy theme re-fetch the mini
     cart so it shows what was just added; it is NOT `cart:refresh:opend`, whose
     open branch is gated on the shop's "after add to cart" setting and, when
     that setting is anything but "open the drawer", navigates the page away.
     Then open the drawer ourselves: the theme's Drawer API when it is there,
     otherwise the header's cart button, which is the route a shopper takes
     anyway. If the drawer is switched off in theme settings nothing opens and
     the shopper simply stays here with the count updated — better than an
     unasked-for navigation. */
  function openThemeCart() {
    document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    setTimeout(function () {
      var theme = window.T4SThemeSP, jq = window.jQuery;
      if (theme && theme.Drawer && typeof theme.Drawer.opend === 'function' && jq) {
        var panel = jq('#t4s-mini_cart');
        if (panel.length) {
          if (!theme.cartTabActive) jq('[data-is-tab-cart]').trigger('click');
          theme.Drawer.opend(panel);
          return;
        }
      }
      var trigger = document.querySelector('[data-drawer-options*="t4s-mini_cart"]');
      if (trigger) trigger.click();
    }, 260);
  }
  $('addBtn').addEventListener('click', function () {
    var btn = $('addBtn');
    if (btn.disabled) return;
    var picks = pickedVariants();
    /* both halves or nothing. A model ops has not listed yet still paints a
       case on the stage (see artLayer), but has no variant behind it — and the
       old `!picks.length` let that through, checking out with the stand alone
       while the receipt promised a set. */
    if (picks.length < 2) { toast('This combination is not available right now'); return; }
    if (picks.some(function (v) { return v.avail === false; })) {
      toast('Part of this set is sold out — try another color'); return;
    }
    btn.disabled = true;
    var label = btn.textContent;
    btn.textContent = 'Adding…';
    /* the primed count when we have one, a live one when we do not */
    var beforeP = (cartPrimed === null) ? cartCount() : Promise.resolve(cartPrimed);
    beforeP.then(function (before) {
      /* One batched write; only if it does not land do we go line by line
         (a single refused line fails the whole batch, and the rest of the set
         should still get in). */
      var acked = 0;
      return addBatch(picks.map(function (v) { return v.id; })).then(function (n) {
        if (n > 0) { acked = n; return null; }
        return picks.reduce(function (p, v) {
          return p.then(function () {
            return addOne(v.id).then(function (ok) { if (ok) acked++; });
          });
        }, Promise.resolve());
      }).then(function () {
        /* Every line came back with a cart payload: the adds are proven and the
           second /cart.js buys nothing but ~400ms in front of the redirect.
           Go, and let the cart badge catch up on the next page. */
        if (acked === picks.length) {
          state.cart = before + picks.length;
          $('cartCount').textContent = state.cart;
          /* The gift app saw none of those writes. Hand it a read it CAN see.
             No keepalive needed now that we stay on the page — and staying is
             the point: the app gets real time to run its rules, and both the
             shopper and we can SEE whether the charm landed. A set here is
             $70-80, the charm threshold is $99, so the cart drawer is also
             exactly where "one more piece" is worth showing. */
          try { window.fetch('/cart.js?ts=' + Date.now(), { cache: 'no-store' }).catch(function () {}); }
          catch (e) { /* a wrapper that throws on call is why writes left it */ }
          btn.textContent = 'Added to cart';
          openThemeCart();
          setTimeout(function () { btn.disabled = false; btn.textContent = label; }, 1600);
          return null;
        }
        /* Anything less than a clean answer and we are back to the only truth
           we trust with these app wrappers in place: count the cart. */
        return cartCount().then(function (after) {
          state.cart = after;
          $('cartCount').textContent = after;
          if (after >= before + picks.length) {
            btn.textContent = 'Added to cart';
            openThemeCart();
            setTimeout(function () { btn.disabled = false; btn.textContent = label; }, 1600);
            return;
          }
          btn.disabled = false; btn.textContent = label;
          /* something landed but not everything: open the cart and say so, so
             the shopper sees what actually went in rather than a silent half
             set. The drawer shows the truth; the toast explains it. */
          if (after > before) { toast('Part of the set could not be added'); openThemeCart(); return; }
          toast('Could not add to cart — please try again');
        });
      });
    }).catch(function () {
      btn.disabled = false; btn.textContent = label;
      toast('Could not add to cart — please try again');
    });
  });
  /* The ghost-link row under every commit row is gone (user, 2026-09-05 mobile
     pass): "底部类似 Different iPhone / Different Case 这类幽灵按钮全部删除,
     已有 Back Button, 不需要重复操作入口". Every one of them fired an action the
     Back button on the same board already fires, so nothing is lost — the paths
     below (enterCase / backToEntry / openModelSheet) are still reached through
     data-back. The one that was not navigation, "roll a colour", went with the
     row it lived in.

     ⚠ PARKED, NOT DEAD — same standing as "Surprise me" (see the section's own
     comment): ringRoll() and its easing island (ROLL_A/B/C/K/V + rollEase) have
     had no entry point since 2026-09-05 and a dead-code sweep will flag them
     every time. They are kept because the interaction is finished and only its
     doorway is missing. Delete all six together if it is decided against; there
     is nothing else pointing at them. (2026-09-10 清场，逐项过用户) */
  /* ---- the back button on the commit row (2026-09-04 review) ----
     One button, four paths; data-back says what this board's previous step is.
     Every path is an action that already existed — no new navigation semantics:
       model     case pick   -> back to the model picker (the "different iPhone" sheet)
       casePick  case colour -> back to the two cases
       casePaint stand pick  -> back to the case colour (the case stays chosen)
       standPick stand colour-> back to the four stands
     Delegated rather than four handlers: the buttons live in four different zones
     and a zone is re-rendered wholesale on shopify:section:load, so a delegate
     never has to be re-attached. */
  on(root, 'click', function (e) {
    var b = e.target.closest && e.target.closest('.mfs-back[data-back]');
    if (!b) return;
    e.preventDefault();
    switch (b.getAttribute('data-back')) {
      case 'model':     backToEntry(); openModelSheet(); break;
      case 'casePick':
        /* With a single case type enterCase pushes the board straight back to
           casePaint (see the skip at its top), so back would spin in place. That
           step does not exist, and the one before it is picking the phone. */
        if (caseTypeCount() < 2) { backToEntry(); openModelSheet(); }
        else enterCase();
        break;
      case 'casePaint':
        /* Back to the case colour: the case type is not re-picked, the board just
           steps back to paint. These lines are the same ones the first breadcrumb
           uses for its "already past Case, so land on paint" branch (see the
           fbPaint handler). */
        state.standPicked = false;
        clearFly();
        root.classList.remove('mfs-flying');
        setStep('casePaint');
        renderPhone(); buildFan(); buildCaseRing(); ringEnter();
        micro('');
        break;
      case 'standPick':
        state.standPicked = false;
        clearFly();
        root.classList.remove('mfs-flying');
        enterSnap();
        break;
    }
  });
  /* "play again" really replays the creation: clear this round's manual picks, bang again, roll a fresh set */
  $('replayBtn').addEventListener('click', function () {
    /* Keep the phone. The model and its body colour are the user's statement of
       fact about the device in their pocket, not a creative choice — "play again"
       re-rolls the outfit, it does not un-choose their phone. Everything below the
       phone (case, stand, both colours) starts over. */
    var keepModel = state.model, keepBody = state.body;
    state.standManual = false;
    makeDeck();
    setPhase('t1');
    state.standPicked = false;
    bigBang(function () {
      firstLook();
      state.model = keepModel; state.body = keepBody;
      renderPhone(); layoutRail(); renderStand();
      buildFan();
      enterCase();
    });
  });

  /* ================= confetti ================= */
  function confetti() {
    if (reduced) return;
    var cv = $('confettiCv'), ctx = cv.getContext('2d');
    var fr = frameRect();
    cv.style.left = fr.left + 'px'; cv.style.top = fr.top + 'px';
    cv.style.width = fr.width + 'px'; cv.style.height = fr.height + 'px';
    cv.width = fr.width; cv.height = fr.height;
    /* The run used to stop on a 2100ms timer, which cut the fall in half: the
       slowest piece starts above the top edge and moves 2.2px a frame, so in
       2.1s it had covered barely a third of the frame when the canvas was
       wiped (user, 2026-09-02: "放半屏就没了"). It now ends when the last
       piece has actually left the bottom, and a little gravity keeps that from
       taking forever — the slowest one clears a 905px frame in ~2.8s. The
       6s ceiling is a guard, not the normal exit.
       The canvas stays pointer-events:none (stylesheet), so the cart panel it
       falls past is clickable the whole way down. */
    /* Density, not just count (user, 2026-09-02: "感觉不太够满屏，但不要过度").
       Two things were thin. A fixed 110 spreads further apart the wider the
       screen, so the count now follows the width — one piece per 8px, with 110
       as the floor so a narrow frame is unchanged. And the start band was only
       0.35 of the frame: the pieces arrived as a single sheet that swept past
       and was gone. At 0.7 they keep arriving for the whole fall. The band is
       twice as tall, so the count has to roughly double just to hold the old
       instantaneous density — 210 pieces at 1680px wide put ~124 on screen at
       any moment against the old ~81. */
    var H = cv.height, N = Math.max(110, Math.round(cv.width / 8)), ps = [];
    for (var i = 0; i < N; i++) {
      ps.push({
        x: Math.random() * cv.width, y: -20 - Math.random() * H * .7,
        w: 5 + Math.random() * 6, h: 8 + Math.random() * 8,
        c: MOVAS[i % MOVAS.length].h, v: 2.5 + Math.random() * 2.5,
        r: Math.random() * Math.PI, vr: (Math.random() - .5) * .2
      });
    }
    var GRAV = 0.06, VMAX = 11;
    var t0 = performance.now();
    (function step(t) {
      ctx.clearRect(0, 0, cv.width, cv.height);
      var live = 0;
      ps.forEach(function (p) {
        if (p.v < VMAX) p.v += GRAV;
        p.y += p.v; p.r += p.vr;
        /* half the diagonal covers the piece at any rotation */
        if (p.y - (p.w + p.h) / 2 <= H) live++;
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r);
        ctx.fillStyle = p.c; ctx.fillRect(-p.w / 2, -p.h / 2, p.w, p.h);
        ctx.restore();
      });
      if (live && t - t0 < 6000) requestAnimationFrame(step);
      else ctx.clearRect(0, 0, cv.width, cv.height);
    })(t0);
  }

  /* ================= share poster ================= */
  /* The modal's open state rides two CSS transitions (scrim colour, sheet slide).
     In at least one rendering environment those transitions never advance past
     their first frame, which leaves the poster invisible even though .open is on
     the element and the rules match. So after the transition's own duration the
     final values are pinned inline: a browser that ran the transition lands on
     the same numbers and sees nothing; one that froze it snaps to the end state.
     Clearing the inline values on close hands control back to the stylesheet. */
  function settleModal(m, on) {
    setTimeout(function () {
      if (m.classList.contains('mfs-open') !== on) return;   /* state moved on */
      var sh = m.querySelector('.mfs-sheet');
      if (on) {
        /* transition:none is the load-bearing part. Writing only the end values
           re-targets the frozen transition and it freezes again at its START —
           the transition has to be removed for the inline values to paint. */
        m.style.transition = 'none';
        m.style.background = 'rgba(26,26,26,.42)';
        if (sh) { sh.style.transition = 'none'; sh.style.transform = 'none'; sh.style.opacity = '1'; }
      }
    }, 460);
    if (!on) {
      m.style.transition = ''; m.style.background = '';
      var sh2 = m.querySelector('.mfs-sheet');
      if (sh2) { sh2.style.transition = ''; sh2.style.transform = ''; sh2.style.opacity = ''; }
    }
  }
  /* ---- the poster leaves by the system share sheet on a phone ----
     "Save the image, open Instagram, upload" is four moves and a round trip
     through the camera roll; navigator.share with a file is one. The <img> laid
     over the canvas is the fallback for a browser without it — a canvas cannot
     be long-pressed to save, a real image can — and it is also what makes the
     poster save-able at all on iOS, where <a download> is inert.
     Both paths go through toDataURL/toBlob, so both are behind the same
     CORS-taint guard as the existing download (deviation #12). */
  /* ---- 分享用的 blob 预先烤好 ----
     走查 0905 D12「分享调不起 Instagram」：navigator.share 必须在用户手势的
     激活窗口内同步调用，而原来它在 cv.toBlob 的异步回调里——1080 方 PNG 编码
     要几十到几百毫秒，回调跑到时激活窗口常常已经过期，iOS 上就是「点了没反应」。
     改成海报画好就烤 blob，点击时同步拿现成的交给 navigator.share。
     key 跟着配置走，配置变了就作废重烤，不会分享出上一套。 */
  /* 只是把三张图放进 CORS 缓存，画不画由 drawPoster 决定 */
  var POSTER_WARM = {};
  function warmPosterLayers() {
    var c = find(MOVAS, state.color), sc = find(MOVAS, state.standColor);
    if (!c || !sc) return;
    [caseLayer(state.caseType, curModel(), c.k),
     bodyLayer(curModel(), curBody()),
     standLayer(state.fam, sc.k)].forEach(function (n) {
      if (!n || POSTER_WARM[n]) return;
      var u = posterSrc(layerURL(n)); if (!u) return;
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.decoding = 'async';
      im.src = u;
      POSTER_WARM[n] = im;          /* hold the reference so it is not collected mid-flight */
    });
  }
  /* ---- 海报取图用 720 档，不用 1080 原图 ----
     海报画布是 1080 方，但产品区只有约 607px 宽（caseH = 288 × 2.109），三层图
     都画在那里面。拉 1080 原图是白下一倍多的字节，而分享用的 blob 必须等三张
     都到齐才能烤——那段等待就是 iOS 上「点了没反应」的时间。720 档缩到 607 仍是
     downscale，清晰度不受影响。
     ⚠ 预热（warmPosterLayers）和实际绘制必须走同一个 URL，否则是两个缓存条目、
     预热白做——这正是走查 0905 D12 修过的那个坑，换尺寸时最容易再犯。 */
  var POSTER_PX = 720;
  function posterSrc(u) { return cdnSrc(u, POSTER_PX); }
  var posterBlob = null, posterBlobKey = '', posterUrl = '';
  function posterKey() {
    return [state.caseType, state.color, state.fam, state.standColor,
            (state.ver || {})[state.fam] || 0, curModel(), curBody()].join('|');
  }
  /* ---- 按钮的就绪态 ----
     分享要的 blob 是异步烤的，在它好之前 sharePoster() 只能回落，点了什么也不弹
     —— iOS 上这就是「按钮坏了」（用户 2026-09-10：「我点 share 是没反应的，然后
     很一个时间，才会弹」）。不能改成「点了之后等 blob 好再 share」：navigator.share
     必须留在点击的调用栈里，等一等激活窗口就过期，还是不弹。所以把等待摆到明面上，
     让按钮自己说它还没好，好了再让点。 */
  function posterReady(on) {
    var b = $('dlBtn');
    if (!b) return;
    b.classList.toggle('mfs-busy', !on);
    b.setAttribute('aria-busy', on ? 'false' : 'true');
    b.textContent = on ? 'Share poster' : 'Preparing\u2026';
  }
  /* ---- 一次编码，三处用 ----
     原来同一张 1080 画布要编码三次：dlBtn 的 href 一次 toDataURL、可长按的 <img>
     一次 toDataURL、分享的 blob 一次 toBlob。桌面上一次约 30ms 不痛不痒，iOS Safari
     的 PNG 编码慢一个量级，三次就是实打实的等待。改为只 toBlob 一次，用它的
     objectURL 同时喂 <img> 与下载链接——File 也直接拿这个 blob。 */
  function bakePoster() {
    var cv = $('shareCanvas');
    if (!cv || !cv.toBlob) { posterReady(true); return; }
    var want = posterKey();
    posterBlob = null; posterBlobKey = '';
    if (posterUrl) { URL.revokeObjectURL(posterUrl); posterUrl = ''; }
    try {
      cv.toBlob(function (b) {
        if (!b || posterKey() !== want) return;   /* 配置已经变了，这张作废 */
        posterBlob = b; posterBlobKey = want;
        posterUrl = URL.createObjectURL(b);
        var im = $('shareImg');
        /* 手机端盖一张真 <img> 供长按保存——canvas 长按存不了 */
        if (im) { if (isMob()) { im.src = posterUrl; im.hidden = false; } else { im.hidden = true; } }
        var dl = $('dlBtn'); if (dl) dl.href = posterUrl;
        posterReady(true);
      }, 'image/png');
    } catch (err) {
      /* 画布被污染：编码这条路断了，但长按与下载还在，别把按钮永远卡在 Preparing */
      posterReady(true);
    }
  }
  function sharePoster() {
    if (!(navigator.share && window.File)) return false;
    /* 还没烤好（比如刚打开就点）就交回长按那条路，别在这里等——一等就丢激活窗口 */
    if (!posterBlob || posterBlobKey !== posterKey()) return false;
    var f = new File([posterBlob], 'moft-original.png', { type: 'image/png' });
    if (navigator.canShare && !navigator.canShare({ files: [f] })) { toast('Long-press the poster to save it'); return true; }
    try {
      /* 同步调用：这一行必须留在点击的调用栈里 */
      navigator.share({ files: [f], title: 'My MOFT Original', text: comboWords() + ' \u00b7 #MOFTColorMatch' })
        .catch(function () { /* the user dismissed the share sheet */ });
      return true;
    } catch (err) { return false; }
  }
  $('shareBtn').addEventListener('click', function () {
    /* 进 done 板时已经画过一张；配置没变就直接用，别再来一轮拉图+编码 */
    if (!posterBlob || posterBlobKey !== posterKey()) drawPoster();
    var m = $('shareModal');
    m.classList.add('mfs-open');
    settleModal(m, true);
    mobSheets();
    /* drawPoster lays the layers in as they decode, so the still has to be taken
       after they land rather than on the same tick */
    /* bakePoster 由 drawPoster 的 settlePoster 在图层落定后调用，不再用定时器
       猜时机。按钮此刻是「Preparing…」，blob 出来才变回可点。 */
  });
  $('dlBtn').addEventListener('click', function (e) {
    if (!isMob()) return;                    /* desk keeps the plain download */
    if (sharePoster()) { e.preventDefault(); return; }
    if (!$('shareImg') || $('shareImg').hidden) return;   /* nothing to long-press: let the href try */
    e.preventDefault();
    toast('Long-press the poster to save it');
  });
  $('closeShare').addEventListener('click', function () { var m = $('shareModal'); m.classList.remove('mfs-open'); settleModal(m, false); mobSheets(); });
  $('shareModal').addEventListener('click', function (e) { if (e.target === this) { this.classList.remove('mfs-open'); settleModal(this, false); mobSheets(); } });
  function rr(ctx, x, y, w, h, r) {
    ctx.beginPath();
    if (ctx.roundRect) { ctx.roundRect(x, y, w, h, r); return; }
    ctx.rect(x, y, w, h);
  }
  /* Board 571:1584 · the standard share poster.
     Ivory ground, the real three-layer composite up top, then the type block:
     16px A MOFT ORIGINAL · 48px THE CASE COLOUR NAME (that is the headline —
     not the combo word) · two 14px lines naming each product's colourway, and
     #MOFTColorMatch running up the left edge. All text derives from the state.
     The composite images load async, so the poster paints its ground and type
     immediately and lays the product in as each layer arrives. */
  /* Board 1556:3543 · 512 wide × 580 tall, drawn in board units and exported at
     1080 wide. The earlier board (571:1584) was a 512 square; the taller frame
     gives the type block its own band instead of crowding the product. */
  var POSTER_W = 1080;
  var POSTER_S = POSTER_W / 512;
  var POSTER_H = Math.round(580 * POSTER_S);
  function pFont(w, px) { return w + ' ' + Math.round(px * POSTER_S) + 'px "Basis Grotesque Pro", "Helvetica Neue", sans-serif'; }
  /* The wordmark, straight off the board's own Logo group (571:1584 → 1515:3031),
     as the five paths Figma exports in its 69×39 box. Drawn rather than loaded:
     the canvas is exported and shared, and an <img> from another origin would
     taint it — the layer art already has to come through CORS for that reason.
     Coordinates are the export's own, so a redraw in Figma is a copy-paste. */
  var MOFT_MARK = {
    w: 69, h: 39, fill: '#231815',
    d: ['M31.2855 23.1931C28.2467 23.1931 25.7734 20.7101 25.7734 17.6546C25.7734 14.599 28.2446 12.116 31.2855 12.116C34.3264 12.116 36.7976 14.599 36.7976 17.6546C36.7976 20.7101 34.3264 23.1931 31.2855 23.1931ZM31.2855 14.8874C29.7661 14.8874 28.5295 16.13 28.5295 17.6567C28.5295 19.1834 29.7661 20.426 31.2855 20.426C32.8049 20.426 34.0415 19.1834 34.0415 17.6567C34.0415 16.13 32.8049 14.8874 31.2855 14.8874Z',
        'M21.4593 12.6711L17.0501 18.5784L12.6409 12.6711L10.1611 13.042V13.5017V13.9636V26.8841H12.9172V17.4507L15.9473 21.5125H18.1508L21.1831 17.4507V26.8841H23.9391V13.9636V13.5017V13.042L21.4593 12.6711Z',
        'M48.7383 12.58V15.1432H52.4115V26.884H55.1676V15.1432H58.8408V12.58H48.7383Z',
        'M35.4183 25.0386H27.1523V26.884H35.4183V25.0386Z',
        'M39.5532 12.58L38.6338 13.5016V13.9635V14.4254V26.884H41.3898V21.1416H47.3594V18.5783H41.3898V15.1432H47.3594V12.58H40.0107H39.5532Z']
  };
  /* x/y/w in board units (512). Path2D is everywhere we support; without it the
     poster simply ships without the mark rather than throwing mid-draw. */
  function drawMark(ctx, x, y, w) {
    if (typeof Path2D !== 'function') return;
    var s = (w * POSTER_S) / MOFT_MARK.w;
    ctx.save();
    ctx.translate(Math.round(x * POSTER_S), Math.round(y * POSTER_S));
    ctx.scale(s, s);
    ctx.fillStyle = MOFT_MARK.fill;
    for (var i = 0; i < MOFT_MARK.d.length; i++) ctx.fill(new Path2D(MOFT_MARK.d[i]));
    ctx.restore();
  }
  function drawPoster() {
    var cv = $('shareCanvas'), ctx = cv.getContext('2d');
    posterReady(false);        /* blob 烤好之前，按钮自己说它还没好 */
    var c = find(MOVAS, state.color), sc = find(MOVAS, state.standColor);
    var cs = find(CASES, state.caseType), fa = find(FAMS, state.fam);

    /* 铺满整块，不留圆角（走查 0905 D15：「发 IG 后四角黑边：导出图圆角外应为
       透明，现在填了黑底」）。原来是 clearRect + 圆角填充，导出的 PNG 四角是
       透明的；Instagram 把带 alpha 的图拍平时用黑色垫底，于是四角变黑。屏幕上
       的圆角本来就不是画出来的——.mfs-share-canvas-wrap 有 border-radius +
       overflow:hidden——所以画满方形对显示零影响，只是让导出图不再有透明像素。 */
    /* The canvas markup carries the same ratio for layout; set it here too so a
       board change lands even on a page served before the markup caught up. */
    if (cv.width !== POSTER_W || cv.height !== POSTER_H) { cv.width = POSTER_W; cv.height = POSTER_H; }
    ctx.fillStyle = '#F6F5F1';
    ctx.fillRect(0, 0, POSTER_W, POSTER_H);

    /* The wordmark, top right (board 430,12 · 69 wide). Drawn again after the
       product lands: the product box is 360 wide from x=76, so its right edge
       (436) overlaps the mark's left edge (430) — on the board the mark sits
       above the product, and repainting it last is what reproduces that order. */
    drawMark(ctx, 430, 12, 69);

    /* Type block — the board's own y-values × the 512→1080 scale, measured from
       the TOP of each text box (`textBaseline = 'top'`), which is what Figma
       reports. Reading them as baselines is what put the three lines ~20px high
       and bunched: 357 / 404 / 432 / 446 were hand-nudged around that mistake. */
    ctx.textAlign = 'center'; ctx.textBaseline = 'top'; ctx.fillStyle = '#141113';
    ctx.font = pFont('500', 16);
    ctx.fillText('A MOFT ORIGINAL', 540, Math.round(424 * POSTER_S));
    ctx.font = pFont('500', 48);
    /* the combo name, exactly as the cart header spells it — board 571:1584's
       placeholder literally reads "Case Color Name" but the cart board (571:1422)
       and the older poster board (571:1477, "FOG MINT") both use the combo name,
       and the two surfaces must agree (Kay, 2026-08-31) */
    ctx.fillText(comboWords().toUpperCase(), 540, Math.round(450 * POSTER_S));
    ctx.font = pFont('500', 14);
    ctx.fillText(c.n + ' ' + cs.n, 540, Math.round(516 * POSTER_S));
    ctx.fillText(sc.n + ' ' + fa.n, 540, Math.round(530 * POSTER_S));

    /* the tag runs up the left edge (box 20,24 · 14×106, so its foot is y130) */
    ctx.save();
    ctx.translate(Math.round(27 * POSTER_S), Math.round(130 * POSTER_S));
    ctx.rotate(-Math.PI / 2);
    ctx.textAlign = 'left'; ctx.textBaseline = 'alphabetic';
    ctx.font = pFont('500', 12);
    ctx.fillText('#MOFTColorMatch', 0, 0);
    ctx.restore();
    ctx.textBaseline = 'alphabetic';

    /* the composite: the same three layers the stage wears, centred in the top
       band (board: device 288 tall in a 512 poster → 608 at export size) */
    var caseL = caseLayer(state.caseType, curModel(), c.k),
        bodyL = bodyLayer(curModel(), curBody()),
        standL = standLayer(state.fam, sc.k);
    var caseH = Math.round(360 * POSTER_S);
    var caseW = caseL ? Math.round(caseH * layerAR(caseL)) : Math.round(caseH * 0.5);
    var cx = 540, top = Math.round(20 * POSTER_S);   /* board: Product 框 76,20 · 360 方 */
    var want = [bodyL, caseL, standL], have = 0;
    var wantN = want.filter(Boolean).length;
    var settled = false, settleT = 0;
    /* 图层落定 → 编码一次（bakePoster 里的 toBlob），下载链接、可长按的 <img>、
       分享用的 File 都从那一个 blob 出。兜底定时器在于图层可能永远不 onload
       （离线、CORS 失败），那时也要把已经画好的字块交出去。 */
    function settlePoster() {
      if (settled) return;
      settled = true; clearTimeout(settleT);
      bakePoster();
    }
    settleT = setTimeout(settlePoster, 1600);
    if (!wantN) settlePoster();
    function place(idx, img) {
      /* draw order is fixed (body under case under stand) so redraw all three
         each time — images arrive out of order and canvas has no z-index */
      want[idx].img = img;
      /* repaint the whole product area */
      ctx.save();
      rr(ctx, cx - caseW / 2 - 20, top - 10, caseW + 40, caseH + 30, 8);
      ctx.fillStyle = '#F6F5F1'; ctx.fill();
      ctx.clip();
      var b = want[0], k = want[1], st = want[2];
      if (b && b.img) {
        var bw = caseW * LBOX.body.w / 100;
        ctx.drawImage(b.img, cx - caseW / 2 + caseW * LBOX.body.x / 100, top, bw, bw / layerAR(bodyL));
      }
      if (k && k.img) ctx.drawImage(k.img, cx - caseW / 2, top, caseW, caseH);
      if (st && st.img) {
        var sh = caseH * LBOX.stand.h / 100, sw = sh * layerAR(standL);
        ctx.drawImage(st.img, cx - sw / 2, top + caseH * LBOX.stand.y / 100, sw, sh);
      } else {
        /* colourway not shot yet: the poster still shows the stand, as the tinted
           plate the stage falls back to */
        var fh = caseH * LBOX.stand.h / 100, fw = fh * find(FAMS, state.fam).ar;
        var fx = cx - fw / 2, fy = top + caseH * LBOX.stand.y / 100;
        rr(ctx, fx, fy, fw, fh, 14);
        /* 拼色的兜底也要是两色（走查 0905 C），135° 硬分割，与 swatchBg 同一读法 */
        if (sc.h2) {
          var lg = ctx.createLinearGradient(fx, fy, fx + fw, fy + fh);
          lg.addColorStop(0, sc.h); lg.addColorStop(0.5, sc.h);
          lg.addColorStop(0.5, sc.h2); lg.addColorStop(1, sc.h2);
          ctx.fillStyle = lg;
        } else {
          ctx.fillStyle = sc.h;
        }
        ctx.fill();
        ctx.strokeStyle = 'rgba(0,0,0,.12)'; ctx.lineWidth = 2; ctx.stroke();
      }
      ctx.restore();
      /* The product box overlaps the wordmark's left edge, and the repaint above
         clears that overlap along with the product area, so put the mark back on
         top — the order the board has it in. */
      drawMark(ctx, 430, 12, 69);
      /* 编码不在这里做：toDataURL 对 1080 PNG 是同步的、几十到上百毫秒一次，
         原来每落一层就编一次、加上 posterImg 的两次定时，一次分享要同步编码
         6 次画布，这是「Share Poster 点击后加载很慢」的大头。改为图层落定后
         只编一次（settlePoster → bakePoster 的 toBlob，2026-09-10 起连
         dlBtn.href 与长按用的 <img> 也复用它的 objectURL）。 */
      if (++have >= wantN) settlePoster();
    }
    /* generation guard: a stale onload from a previous colour must not paint
       over the poster the sheet is showing now */
    var gen = drawPoster.__gen = (drawPoster.__gen || 0) + 1;
    want.forEach(function (name, i) {
      if (!name) { want[i] = null; return; }
      want[i] = { name: name };
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { if (gen === drawPoster.__gen) place(i, im); };
      /* 没有这一条，一张图挂掉（CORS 失败、404、离线）就让 have 永远追不上
         wantN，settlePoster 只能等满 1600ms 兜底——而分享用的 blob 是在那之后
         才烤的，所以那 1.6 秒里点分享什么也不弹，iOS 上就是「按钮没反应」
         （用户 2026-09-10）。失败的那层留空，其余两层照常出图。 */
      im.onerror = function () {
        if (gen !== drawPoster.__gen) return;
        want[i] = null;
        if (++have >= wantN) settlePoster();
      };
      im.src = posterSrc(layerURL(name));   /* 与 warmPosterLayers 同一个 URL */
    });
  }

  /* ================= chrome ================= */
  /* the frame is a bezel, never a scroller. It is scrollable in principle (the
     closed sheets overhang it by their own height), so anything that scrolls an
     ancestor can shove the entire mockup off screen — pin it. */
  (function pinFrame() {
    var fr = $('phoneFrame');
    fr.addEventListener('scroll', function () { fr.scrollTop = 0; fr.scrollLeft = 0; }, { passive: true });
  })();

  var toastT = 0;
  function toast(msg) {
    var t = $('toast'); t.textContent = msg; t.classList.add('mfs-show');
    clearTimeout(toastT); toastT = setTimeout(function () { t.classList.remove('mfs-show'); }, 2600);
  }
  on(window, 'keydown', function (e) {
    if (e.key !== 'Escape') return;
    root.querySelectorAll('.mfs-modal.mfs-open').forEach(function (m) { m.classList.remove('mfs-open'); });
    mobSheets();
  });
  function relayout() {
    mobZ = 0;                 /* the viewport moved: the device is derived from it */
    sizeSpacers(fanEl); sizeSpacers(standFanEl);
    renderPhone(); layoutRail(); renderStand();
  }
  /* ================= mode: desk board or phone build =================
     One engine, one class. The section ships .mfs-desk and an inline script
     swaps it before first paint (the desk composition would otherwise flash a
     1500px board on a handset); this keeps it honest afterwards, so rotating a
     tablet or dragging a desktop window across the breakpoint re-composes
     rather than stranding half a layout. ?mob=1 / ?desk=1 pin it, which is how
     the regression tools drive the phone build. */
  var MOB_Q = '(max-width: 767px)';
  var forceMode = /[?&]mob=1/.test(location.search) ? 'mob'
                : /[?&]desk=1/.test(location.search) ? 'desk' : null;
  function wantMob() {
    if (forceMode) return forceMode === 'mob';
    return !!(window.matchMedia && window.matchMedia(MOB_Q).matches);
  }
  function applyMode(mob) {
    if (isMob() === mob) return false;
    root.classList.toggle('mfs-mob', mob);
    root.classList.toggle('mfs-desk', !mob);
    mobZ = 0;
    /* the two builds do not get the same allowance: a resized window has to
       take the other one's rail radius and bitmap ceiling, not keep the one it
       happened to boot with */
    resetBudget();
    return true;
  }
  /* Re-composing is not just a relayout: the ring's circle, the rail's role and
     the stand strip's geometry all change shape, and each is built once per
     step. So the current step is re-entered through its own front door. */
  function remode() {
    mobFit();
    mobSheets();
    relayout();               /* layoutRail refills the seat window on its own */
    var ph = root.getAttribute('data-ph'), cs = root.getAttribute('data-cs');
    if (ph === 't1' && cs === 'pick') { renderAltCase(); buildCasePick(); }
    else if (ph === 't1') { buildFan(); buildCaseRing(); }
    else if (ph === 't2') { famRailSync(); renderStandStep(); buildStandFan(); }
    else if (ph === 't3') { buildStandFan(); }
    else if (ph === 'done') { renderDone(false); }
  }
  applyMode(wantMob());
  if (!forceMode && window.matchMedia) {
    var mq = window.matchMedia(MOB_Q);
    var onMq = function () { if (applyMode(wantMob())) remode(); };
    if (mq.addEventListener) on(mq, 'change', onMq);
  }
  root.querySelectorAll('.mfs-qarrow').forEach(function (a) {
    a.addEventListener('click', function () {
      var t = a.getAttribute('data-t');
      var sc = t === 'fan' ? fanEl : standFanEl;
      var item = sc.querySelector('.mfs-item');
      /* the board pages a full window of ten; the phone column still nudges by one */
      var step = root.classList.contains('mfs-desk')
        ? sc.clientWidth
        : (item ? item.offsetWidth + 10 : 120);
      sc.scrollBy({ left: a.classList.contains('mfs-next') ? step : -step, behavior: reduced ? 'auto' : 'smooth' });
    });
  });
  on(window, 'resize', function () { if (!spinning) relayout(); });


  /* ================= entry: attract mode — the line walks past by itself ================= */
  var entryTimer = 0;
  function entryTick() {
    if (spinning) return;
    /* the picker bar is open: hold the row still. Choosing a model while the
       whole line walks past behind the bar reads as noise, and the thing being
       chosen is not the thing that is moving (user, 2026-09-01).
       Skipping the tick rather than killing the interval means an in-flight
       spin still lands properly — stopEntryLoop clears the settle timer, which
       would leave the deck read and the row out of step — and closing the bar
       resumes by itself with nothing to re-arm. */
    var bar = $('modelModal');
    if (bar && bar.classList.contains('mfs-open')) return;
    /* Move on only when the next look's pixels are in. The loop used to change
       the case every tick whether or not the new layer had arrived, and each
       change retires the pending one (swapLayer's out-of-order guard) — on a
       slow or cold CDN no case ever landed and the phone sat bare, or bare with
       just a stand (kk 2026-10-23 NO.264, iPhone Duo page, intermittent). Holding
       the current look for a tick costs nothing; a bare phone is a broken page. */
    if (!nextLookReady() && ++entryHold < 4) return;
    entryHold = 0;   /* four ticks is long enough to wait on a file that never comes */
    rollPaint(); renderPhone();
    spinRail(1, 900);
  }
  function nextLookReady() {
    var cn = stageLay(caseLayer('movas', curModel(), nextPaint)), ok = true;
    if (cn && !warmLoaded(cn)) { warm(cn); ok = false; }
    if (DECK) {
      var d = deck[(((railIdx + 1) % DECK) + DECK) % DECK];
      var sn = stageLay(standLayer(d.fam, d.color));
      if (sn && !warmLoaded(sn)) { warm(sn); ok = false; }
    }
    return ok;
  }
  var entryHold = 0;
  function startEntryLoop() {
    /* state.model stays null until the user actually picks — renderPhone falls
       back to 17 Pro, and the sheet knows nothing has been chosen yet */
    clearInterval(entryTimer);
    entryTimer = setInterval(entryTick, 2000);
  }
  function stopEntryLoop() {
    clearInterval(entryTimer); clearTimeout(spinT); spinning = false;
  }
  /* Both builds behave the same way here: entry plays once and the studio owns
     the section from there. The phone build briefly had a ✕ and a resume path,
     which a full-screen layer needed — a layer has to be dismissible. It is a
     plain section again, so scrolling away IS the way out. */
  /* ---- back to the first screen ----
     The model picker is opened FROM the attract screen, so opening it over the
     case board does not line up: the phone is wearing a case and the row is a
     four-product deck, while the picker is asking which phone this is (user,
     2026-09-04: "上一步如果回到选手机的话，那手机要回到初始横滚的状态，
     不然对应不上"). So this replays the section's own boot sequence — the idle
     roll, the bare phone — and only then opens the picker.
     firstLook() is deliberately NOT called: it rolls a fresh random look, and the
     attract loop is about to do that on its own tick. */
  function backToEntry() {
    state.standPicked = false;
    /* hasLook means "the entry bang has played, the flow has begun". Sending the
       user back to the FIRST SCREEN un-begins it, so this flag has to come back
       with them — all three of its readers want the same answer here:
         commitPick  otherwise takes the mid-flow branch and drops straight onto
                     the case-COLOUR board, skipping the bang and the case pick
                     (user, 2026-09-04: "从选手机这选，要从新放烟花动效再到选壳
                     类型才对" — with a screenshot of the crumb already reading
                     "1 Case MOVAS™")
         armCTA      the sheet's button reads "Start" again, not "Switch to this iPhone"
         flowSync    the first crumb stops showing a case value it no longer has
       standManual goes with it for the same reason the replay button clears it:
       a fresh round must be free to roll its own colours. */
    hasLook = false;
    state.standManual = false;
    clearFly();
    root.classList.remove('mfs-flying');
    closeInfo();
    /* the case captions are hung on the two cases by buildCasePick; the first
       screen has no "two cases" to caption */
    Array.prototype.forEach.call(root.querySelectorAll('.mfs-case-cap'), function (n) { n.remove(); });
    setStep('entry');
    makeDeck();
    layoutRail();
    renderPhone(); renderStand();
    railEnter();
    startEntryLoop();
    micro('');
  }
  $('startBtn').addEventListener('click', function () { surpriseMode = false; openModelSheet(); });
  var surpriseMode = false;
  $('surpriseBtn').addEventListener('click', function () { surpriseMode = true; openModelSheet(); });
  function dismissPicker() {
    root.classList.remove('mfs-picking');
    $('modelModal').classList.remove('mfs-open');
    $('stepModel').classList.remove('mfs-open');
    surpriseMode = false;
    mobSheets();
  }
  $('modelModal').addEventListener('click', function (e) { if (e.target === this) dismissPicker(); });
  /* short screens: fade + arrow at the frame's foot while there is more below */
  var pcolEl = root.querySelector('.mfs-phone-col');
  function updHint() {
    var h = $('scrollHint'); if (!h || !pcolEl) return;
    var more = pcolEl.scrollHeight - pcolEl.clientHeight - pcolEl.scrollTop > 24;
    h.classList.toggle('mfs-on', more);
  }
  pcolEl.addEventListener('scroll', updHint, { passive: true });
  on(window, 'resize', updHint);
  setTimeout(updHint, 300);

  /* ================= boot ================= */
  setPhase('entry');
  makeDeck();
  firstLook();
  /* The three layers of the opening frame are asked for BEFORE anything is
     laid out, and at high priority. The order matters as much as the timing:
     layoutRail is about to ask for a handful of rail seats and renderPhone for
     the stage, and if the stage went in second it queued behind them. The seat
     the reel opens on is also the one the section preloaded, so on a warm
     network that request is already in flight before this line runs. */
  warm(stageLay(bodyLayer(curModel(), curBody())), 'high');
  warm(stageLay(caseLayer(state.caseType, curModel(), state.color)), 'high');
  warm(stageLay(standLayer(state.fam, state.standColor)), 'high');
  layoutRail();
  renderPhone(); renderStand();
  railEnter();
  startEntryLoop();

  /* ================= the warm-up ladder, at boot =================
     Rung one runs NOW, not at idle. It used to sit behind requestIdleCallback
     with a 2500ms timeout while the attract loop ticked at 2000ms, so the first
     roll of the showreel landed on a colour nothing had asked for yet.

     It is also much smaller than it was. It no longer decodes all thirteen case
     colours "in case" — rollNext() pre-rolls the next one and decodes exactly
     that — so what boot pays for is what boot shows: the bodies of the model on
     stage, the case it is wearing, the case it is about to wear, and the
     handful of rail seats railFill decided are in reach.

     Everything beyond one interaction is bytes only (lookahead, below). The old
     second rung decoded every model's bodies and all four stand ranges: 169
     squares, ~3MB on the wire but ~750MB of bitmaps, which is why it had to be
     switched off on phones entirely. Prefetching the same ground without
     decoding costs the same bytes on every device and no memory at all. */
  bodiesFor(curModel()).forEach(function (b) { warm(stageLay(bodyLayer(curModel(), b))); });
  idleWarm(function () { lookahead(); });

  /* ?debug=1 — QA / automation hooks. Never mounted by default; zero effect on normal visits.
     Regression shots jump straight to any state without clicking through. */
  if (new URLSearchParams(location.search).get('debug') === '1') {
    window.__ss = {
      state: state, DATA: { MODELS: MODELS, FAMS: FAMS, CASES: CASES, MOVAS: MOVAS, CASE_POOL: CASE_POOL, FRAME_POOL: FRAME_POOL, STAND_POOL: STAND_POOL },
      setPhase: setPhase, openModelSheet: openModelSheet,
      enterCase: enterCase, chooseCase: chooseCase, swapCase: swapCase,
      enterSnap: enterSnap, chooseStand: chooseStand, enterPaint: enterPaint,
      renderDone: renderDone, renderPhone: renderPhone, renderStand: renderStand,
      pickModel: pickModel, pickBody: pickBody, commitPick: commitPick,
      railStep: railStep, closeInfo: closeInfo, drawPoster: drawPoster,
      stopEntryLoop: stopEntryLoop, startEntryLoop: startEntryLoop,
      /* loading introspection: what is decoded, what it costs, what the row is
         holding. `mb` is the number evictWarm spends against budget().mb. */
      load: function () {
        var seats = 0, kids = trackEl.children, i;
        for (i = 0; i < kids.length; i++)
          if (kids[i].firstElementChild && kids[i].firstElementChild.firstChild) seats++;
        return { budget: budget(), mb: Math.round(warmMB * 10) / 10, decoded: Object.keys(WARM).length,
                 fetched: Object.keys(FETCHED).length, queued: FQ.length, stagePx: stagePx() || 1080,
                 railSeats: seats, railR: railWinR(), railIdx: railIdx, dpr: window.devicePixelRatio || 1 };
      },
      railFill: railFill, lookahead: lookahead, evictWarm: evictWarm,
      /* freeze the attract randomness and pin a config so regression shots reproduce */
      pin: function (o) {
        stopEntryLoop();
        o = o || {};
        state.model = o.model || MODELS[0].k;
        state.body = o.body || bodiesFor(state.model)[0];
        state.caseType = o.caseType || 'movas';
        state.fam = o.fam || FAMS[0].k;
        state.color = o.color || CASE_POOL[0];
        state.standColor = o.standColor || standColors(state.fam)[1] || standColors(state.fam)[0];
        if (o.ver) state.ver = o.ver;
        swingSettle();          /* a flight in the air would repaint after us */
        renderPhone(); renderStand();
        if (root.getAttribute('data-sn') === 'pick') { famRailSync(); renderStandStep(); }
        return this.snap();
      },
      /* current state snapshot, for assertions */
      snap: function () {
        var b = root;
        return {
          ph: b.getAttribute('data-ph'), cs: b.getAttribute('data-cs'), sn: b.getAttribute('data-sn'),
          model: curModel(), body: curBody(), caseType: state.caseType,
          fam: state.fam, ver: JSON.parse(JSON.stringify(state.ver || {})),
          color: state.color, standColor: state.standColor,
          standPicked: !!state.standPicked, infoOn: b.classList.contains('mfs-info-on')
        };
      }
    };
  }


    /* ================= asset inspector (dev tool) =================
       The bench is a full-screen tool in its own file (stylesheet included),
       imported only on its own page — the storefront never downloads a byte.

       Pre-launch cleanup (user, 2026-09-10: "inspect 需要移到单独的页面…我们准备
       把 studio 要上线了"): the Studio page carries no entry point at all now.
       The bench opens from the `data-mfs-inspect-only` branch near the top of
       this file, which returns before any of the Studio is built; all this end
       still owns is the context object it hands over. The INSPECT fab and the
       ?inspect=1 switch are gone. */
    function insCtx() {
      return {
        colors: COLORS, models: MODELS, bodyName: BODY_NAME, bodyHex: BODY_HEX,
        cases: CASES, caseVar: CASE_VAR, casePool: CASE_POOL, framePool: FRAME_POOL,
        fams: FAMS, standPool: STAND_POOL, standVar: STAND_VAR,
        layers: LAYERS, layerUrls: LAYER_URLS,
        devCandidates: devCandidates, find: find, def: DEF
      };
    }


    /* ── teardown: everything registered on document/window, plus timers ── */
    return function () {
      stopEntryLoop();
      mobLock(false);            /* never leave <html> unscrollable behind us */
      clearInterval(angTimer);
      clearTimeout(spinT); clearTimeout(toastT); clearTimeout(infoLeaveT);
      cancelAnimationFrame(ringAnim);
      swingSettle();
      for (var i = 0; i < __subs.length; i++) __subs[i][0].removeEventListener(__subs[i][1], __subs[i][2], __subs[i][3]);
      __subs.length = 0;
    };
  }

  /* Fall back to the static shopping page — the same thing the section's inline
     watchdog does when the module never arrives.

     The watchdog alone was not enough: it asks `customElements.get(...)`, i.e.
     "was the element DEFINED", and defining it is unconditional (the call sits at
     the end of this file and does not depend on the data). So a module that loads
     fine but THROWS while mounting left the watchdog satisfied, the static layer
     hidden by `html.js`, and the section blank.
     That is not a hypothetical: emptying the stand blocks in the theme editor —
     an ordinary thing for staff to do — makes state's `fam: FAMS[0].k` read `k`
     off undefined. Measured 2026-09-04, all four combinations (no stands / no
     cases / neither / one case + no stands) throw. */
  function fallbackToStatic() {
    var sec = document.querySelector('[data-mfs-section]');
    if (sec) sec.classList.add('mfs-no-component');
  }
  class MfSnapStudio extends HTMLElement {
    connectedCallback() {
      if (this.__booted) return;
      this.__booted = true;
      var island = this.querySelector('script[data-mfs-data]');
      var data = null;
      try { data = JSON.parse(island ? island.textContent : 'null'); } catch (err) { data = null; }
      if (!data) {
        this.__booted = false;
        fallbackToStatic();
        throw new Error('mf-snap-studio: data island missing or invalid');
      }
      try {
        this.__cleanup = initStudio(this, data);
      } catch (err) {
        /* __booted back to false so a theme-editor re-render gets to try again
           (the section is re-created on every settings change, and the next
           attempt may well have the blocks back). */
        this.__booted = false;
        fallbackToStatic();
        throw err;          /* still surface it — this is a bug worth seeing */
      }
    }
    disconnectedCallback() {
      if (this.__cleanup) { this.__cleanup(); this.__cleanup = null; }
      this.__booted = false;
    }
  }
  if (!customElements.get('mf-snap-studio')) customElements.define('mf-snap-studio', MfSnapStudio);

  /* Theme editor: section re-render swaps the DOM, so connect/disconnect above
     already handle shopify:section:load. Blocks are data sources (products),
     not visual regions — nothing to highlight on shopify:block:select. */
})();
